import { getOperatingDateStr } from '../../lib/utils';
import React, { useState, useEffect, useRef } from 'react';
import { useAdminBookingModalState } from '../../hooks/useAdminBookingModalState';
import { AdminBookingDetailModal } from './AdminBookingDetailModal';
import { CantinaProductSection } from './CantinaProductSection';
import { AdminCantinaTabsCard } from './AdminCantinaTabsCard';
import { AdminCantinaTabModal } from './AdminCantinaTabModal';
import { AdminChargeConfirmModal } from './AdminChargeConfirmModal';
import { cantinaTabService } from '../../services/cantinaTabService';
import type { CantinaTab } from '../../types';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayerDetailModal } from '../player/PlayerDetailModal';
import { AdminWhatsApp } from './AdminWhatsApp';
import {
  Calendar,
  Plus,
  Trash2,
  Check,
  X,
  MessageCircle,
  MessageSquare,
  Clock,
  MapPin,
  DollarSign,
  User,
  Users,
  TrendingUp,
  Store,
  TrendingDown,
  Save,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sliders,
  Menu,
  Trophy,
  ShieldCheck,
  BarChart3,
  CreditCard,
  AlertCircle,
  AlertTriangle,
  Smartphone,
  CheckCircle2,
  CalendarDays,
  Repeat,
  Pin,
  Pencil,
  FileText,
  ShoppingCart,
  PlusCircle,
  Image as ImageIcon,
  Settings,
  Search,
  LogOut,
  Eye,
  EyeOff,
  Bell,
  Lightbulb,
  Zap,
  Handshake,
  Banknote,
  RotateCcw,
  Phone,
  Flame,
  Lock
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  runTransaction,
  arrayUnion,
  deleteField,
  increment
} from 'firebase/firestore';
import { ClubSchedule, Booking, BookingItem, BookingRequest, BookingStats, Player, Config, OffDay, DaySchedule, TimeDiscount, Product, PaymentRule, PaymentConfig } from '../../types';
import { getRequiredDepositPercentage } from '../../lib/paymentUtils';
import { logActivity } from '../../services/activityLogger';
import { cashService } from '../../services/cashService';
import { CashSession } from '../../types';
import { auth, app, db, getCollectionPath } from '../../lib/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { cancelAssociatedMatch } from '../../lib/matchmakingUtils';

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2).toString().padStart(2, '0');
  const mins = i % 2 === 0 ? '00' : '30';
  return `${hours}:${mins}`;
});
function formatRequestDate(dateStr: string): string {
  if (!dateStr) return '';

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tyyyy = tomorrow.getFullYear();
  const tmm = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const tdd = String(tomorrow.getDate()).padStart(2, '0');
  const tomorrowStr = `${tyyyy}-${tmm}-${tdd}`;

  const [year, month, day] = dateStr.split('-').map(Number);
  const targetDate = new Date(year, month - 1, day);

  let dayOfWeek = targetDate.toLocaleDateString('es-ES', { weekday: 'long' });
  dayOfWeek = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);

  const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;

  if (dateStr === todayStr) {
    return `Hoy (${dayOfWeek}, ${formattedDate})`;
  } else if (dateStr === tomorrowStr) {
    return `Mañana (${dayOfWeek}, ${formattedDate})`;
  } else {
    return `${dayOfWeek}, ${formattedDate}`;
  }
}
export const checkIfSlotBlockedByOffDay = (
  offDays: OffDay[] | undefined,
  date: string,
  court: string,
  time: string,
  dayOfWeek: number
) => {
  if (!offDays || offDays.length === 0) return { isBlocked: false, reason: '' };

  for (const od of offDays) {
    const isMatchingDate = od.date === date;
    const isMatchingDayOfWeek = od.dayOfWeek !== undefined && od.dayOfWeek === dayOfWeek;

    if (isMatchingDate || isMatchingDayOfWeek) {
      const isCourtAffected = !od.courts || od.courts.length === 0 || od.courts.includes(court);

      if (isCourtAffected) {
        if (od.allDay) {
          return { isBlocked: true, reason: od.reason || 'Día Off' };
        }

        if (od.startHour && od.endHour) {
          const [sh, sm] = od.startHour.split(':').map(Number);
          const [eh, em] = od.endHour.split(':').map(Number);
          const [th, tm] = time.split(':').map(Number);

          const startMin = sh * 60 + sm;
          let endMin = eh * 60 + em;
          if (endMin <= startMin) endMin += 1440;

          const timeMin = th * 60 + tm;
          if (timeMin >= startMin && timeMin < endMin) {
            return { isBlocked: true, reason: od.reason || 'Inhabilitación Parcial' };
          }
        }
      }
    }
  }
  return { isBlocked: false, reason: '' };
};

interface AdminBookingsProps {
  userId: string; // Sede logueada
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  highlightRequestId?: string | null;
  onHighlightHandled?: () => void;
  onOpenChatWithPlayer?: (playerId: string, playerName: string, playerPhoto: string) => void;
  onlySetup?: boolean;
  onNavigateToConfiguracion?: () => void;
  onNavigateToSection?: (section: string) => void;
  userRole?: string;
  sedesPublicas?: any[];
  subscriptionPlan?: string;
}

function AdminQuickNoteCard({ userId, db, getCollectionPath }: { userId: string; db: any; getCollectionPath: (c: string) => string }) {
  const [quickNote, setQuickNote] = useState(() => localStorage.getItem('admin_quick_note') || '');
  const [savedQuickNote, setSavedQuickNote] = useState(() => localStorage.getItem('admin_quick_note') || '');
  const [noteSavedAt, setNoteSavedAt] = useState(() => localStorage.getItem('admin_quick_note_saved_at') || '');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [dynamicHeight, setDynamicHeight] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileScreen(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Real-time Firestore sync
  useEffect(() => {
    if (!userId || !db) return;
    const noteRef = doc(db, getCollectionPath('quickNotes'), userId);
    const unsub = onSnapshot(noteRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const text = data.text || '';
        const savedAt = data.savedAt || '';
        setQuickNote(text);
        setSavedQuickNote(text);
        setNoteSavedAt(savedAt);
        localStorage.setItem('admin_quick_note', text);
        if (savedAt) localStorage.setItem('admin_quick_note_saved_at', savedAt);
      }
    });
    return () => unsub();
  }, [userId, db, getCollectionPath]);

  const isReadOnly = quickNote === savedQuickNote && savedQuickNote !== "" && !isEditingNote;


  const [depositDropdownOpen, setDepositDropdownOpen] = useState<string | null>(null);
  const handleSave = async () => {
    localStorage.setItem('admin_quick_note', quickNote);
    setSavedQuickNote(quickNote);

    let formattedDate = '';
    if (quickNote.trim() !== '') {
      const now = new Date();
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
      const dayName = dayNames[now.getDay()];
      const dateNum = now.getDate();
      const monthNum = now.getMonth() + 1;
      formattedDate = `${dayName} ${dateNum}/${monthNum}`;
      localStorage.setItem('admin_quick_note_saved_at', formattedDate);
      setNoteSavedAt(formattedDate);
    } else {
      localStorage.removeItem('admin_quick_note_saved_at');
      setNoteSavedAt('');
    }

    if (userId && db) {
      try {
        await setDoc(doc(db, getCollectionPath('quickNotes'), userId), {
          text: quickNote,
          savedAt: formattedDate,
          updatedAt: Date.now()
        }, { merge: true });
      } catch (e) {
        console.warn("No se pudo sincronizar nota rápida:", e);
      }
    }

    setIsEditingNote(false);
    setIsNoteExpanded(false);
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 1500);
  };

  const handleClear = async () => {
    setQuickNote('');
    setSavedQuickNote('');
    setNoteSavedAt('');
    localStorage.removeItem('admin_quick_note');
    localStorage.removeItem('admin_quick_note_saved_at');

    if (userId && db) {
      try {
        await setDoc(doc(db, getCollectionPath('quickNotes'), userId), {
          text: '',
          savedAt: '',
          updatedAt: Date.now()
        }, { merge: true });
      } catch (e) {
        console.warn("No se pudo limpiar nota rápida:", e);
      }
    }

    setIsEditingNote(false);
    setIsNoteExpanded(false);
  };

  // Calculate perfect height dynamically based on scrollHeight
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    if (!isNoteExpanded) {
      setDynamicHeight(null);
      return;
    }

    // Reset to auto to get correct scrollHeight
    el.style.height = 'auto';
    const scHeight = el.scrollHeight;
    el.style.height = ''; // restore

    const isMobile = window.innerWidth < 768;
    const baseCardHeight = isMobile ? 68 : 86;
    const baseTextareaHeight = isMobile ? 24 : 36;

    if (scHeight > baseTextareaHeight) {
      const extra = scHeight - baseTextareaHeight;
      // limit max card height to keep design clean and prevent excessive layout shifts
      const maxExtra = isMobile ? 80 : 110;
      setDynamicHeight(baseCardHeight + Math.min(extra, maxExtra));
    } else {
      setDynamicHeight(null);
    }
  }, [quickNote, isNoteExpanded]);

  return (
    <div
      onBlur={(e) => {
        const currentTarget = e.currentTarget;
        setTimeout(() => {
          if (!currentTarget.contains(document.activeElement)) {
            setIsNoteExpanded(false);
          }
        }, 150);
      }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        setIsNoteExpanded(prev => !prev);
      }}
      style={dynamicHeight ? { height: `${dynamicHeight}px` } : undefined}
      className={`col-span-6 bg-[#1a1c23] border border-[#2d313f] rounded-xl md:rounded-2xl pl-3 pr-11 md:pl-4 md:pr-13 flex flex-col justify-between py-1.5 md:py-2.5 relative overflow-hidden shadow-lg group hover:border-theme-primary/40 transition-all duration-300 cursor-pointer ${!dynamicHeight ? (isNoteExpanded ? 'h-[145px] md:h-[180px]' : 'h-[68px] md:h-[86px]') : ''
        }`}
    >
      <div className="absolute right-14 top-1/2 -translate-y-1/2 text-white/5 group-hover:text-theme-primary/10 transition-all duration-300 pointer-events-none">
        <Pencil size={44} className="opacity-[0.03]" />
      </div>

      {/* Título a la izquierda y línea divisoria hacia la derecha */}
      <div className="flex items-center gap-2 w-full select-none shrink-0 mt-0.5">
        <div className="flex items-center gap-1 shrink-0">
          <FileText className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 text-zinc-500 shrink-0" />
          <span className="text-[7.5px] min-[360px]:text-[8.5px] sm:text-[9.5px] md:text-[11px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">
            <span className="hidden sm:inline">Nota Rápida</span>
            <span className="inline sm:hidden">Notas</span>
            {noteSavedAt && (
              <span className="text-[5.5px] min-[360px]:text-[6.5px] sm:text-[7.5px] md:text-[8.5px] text-orange-200/60 font-semibold ml-1.5 normal-case font-mono">
                ({noteSavedAt})
              </span>
            )}
          </span>
        </div>
        <div className="h-[1px] bg-zinc-800/80 flex-grow" />
      </div>

      {/* Editor de Texto */}
      <div className="flex items-center gap-1.5 md:gap-2 flex-1 relative z-10 mt-0.5 md:mt-1 mb-0 md:mb-0.5 min-w-0">
        <textarea
          ref={textareaRef}
          id="quick-note-textarea"
          value={quickNote}
          onChange={(e) => {
            const val = e.target.value;
            setQuickNote(val);
            if (val.length > 35 || val.includes('\n')) {
              setIsNoteExpanded(true);
            } else {
              setIsNoteExpanded(false);
            }
          }}
          onFocus={() => {
            if (quickNote.length > 35 || quickNote.includes('\n')) {
              setIsNoteExpanded(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isReadOnly) {
              e.preventDefault();
              handleSave();
            }
          }}
          readOnly={isReadOnly}
          spellCheck={false}
          placeholder={isMobileScreen ? '"Ej: Avisar clientes: Cancha 3 en mantenimiento"' : '"Ej: Avisar clientes: mantenimiento de cancha 3 el Lunes"'}
          className={`bg-transparent font-note placeholder-zinc-700/25 font-bold resize-none outline-none border-none focus:ring-0 flex-1 h-full overflow-y-auto select-text scrollbar-none text-center placeholder:text-center leading-[1.1] py-0.5 pr-1 transition-all text-[10px] min-[360px]:text-[11px] sm:text-xs md:text-[11.5px] lg:text-[12px] xl:text-[13px] ${isReadOnly
            ? 'text-zinc-500/50 cursor-default select-none'
            : 'text-amber-200 cursor-text'
            }`}
          rows={isNoteExpanded ? 5 : 2}
        />

        {/* Animación "Nota Guardada" en verde */}
        {showSaveSuccess && (
          <div className="absolute inset-0 bg-[#2d313f]/95 backdrop-blur-xs flex items-center justify-center gap-1.5 md:gap-2 z-30 animate-fade-in text-emerald-400 font-extrabold text-[10px] min-[360px]:text-[11px] sm:text-xs md:text-sm rounded-lg border border-emerald-500/10">
            <CheckCircle2 size={16} className="text-emerald-400 animate-bounce" />
            <span>Nota guardada</span>
          </div>
        )}
      </div>

      {/* Barra de herramientas derecha integrada que ocupa toda la extensión vertical */}
      <div className="absolute right-0 top-0 bottom-0 w-9 md:w-11 border-l border-[#2d313f] bg-[#14161d] flex flex-col divide-y divide-[#2d313f] z-20">
        {/* Guardar / Editar */}
        {isReadOnly ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditingNote(true);
              setIsNoteExpanded(true);
              setTimeout(() => {
                const el = document.getElementById('quick-note-textarea');
                if (el) {
                  (el as HTMLTextAreaElement).focus();
                  const len = (el as HTMLTextAreaElement).value.length;
                  (el as HTMLTextAreaElement).setSelectionRange(len, len);
                }
              }, 50);
            }}
            className="flex-1 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
            title="Editar nota"
          >
            <Pencil className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            className={`flex-1 flex items-center justify-center transition-all cursor-pointer ${quickNote !== savedQuickNote
              ? 'bg-emerald-500 text-black animate-pulse font-bold hover:bg-emerald-400'
              : 'text-white/40 hover:text-white hover:bg-white/5'
              }`}
            title="Guardar recordatorio"
          >
            <Save className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
          </button>
        )}

        {/* Borrar */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClear();
          }}
          className="flex-1 flex items-center justify-center text-red-400 hover:text-white hover:bg-red-500 transition-all cursor-pointer"
          title="Borrar nota"
        >
          <Trash2 className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
        </button>
      </div>
    </div>
  );
}

const CountdownText = ({ expiresAt }: { expiresAt: number }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const mins = Math.max(0, Math.ceil((expiresAt - now) / 60000));
  return <span className="text-red-400 font-bold block text-[10px] animate-pulse mt-0.5">Expira en: {mins} min</span>;
};

function AnimatedNumber({ value }: { value: number }) {
  const [displayValue, setDisplayValue] = useState(value);
  const currentDisplayValue = useRef(value);

  useEffect(() => {
    let startTimestamp: number | null = null;
    const duration = 2500; // 2.5 seconds
    const startValue = currentDisplayValue.current;
    const diff = value - startValue;

    if (diff === 0) {
      setDisplayValue(value);
      currentDisplayValue.current = value;
      return;
    }

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // easeOutQuart
      const ease = 1 - Math.pow(1 - progress, 4);
      
      const currentVal = startValue + diff * ease;
      setDisplayValue(currentVal);
      currentDisplayValue.current = currentVal;

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
        currentDisplayValue.current = value;
      }
    };

    window.requestAnimationFrame(step);
  }, [value]);

  return <>{Math.round(displayValue).toLocaleString('es-AR')}</>;
}

export function AdminBookings({
  userId,
  players,
  db,
  getCollectionPath,
  config,
  highlightRequestId,
  onHighlightHandled,
  onOpenChatWithPlayer,
  onlySetup = false,
  onNavigateToConfiguracion,
  onNavigateToSection,
  userRole,
  sedesPublicas = [],
  subscriptionPlan = 'Basico'
}: AdminBookingsProps) {
  

  // --- ESTADOS DE CONFIGURACIÓN Y GRILLA ---
  const [schedule, setSchedule] = useState<ClubSchedule | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [requests, setRequests] = useState<BookingRequest[]>([]);
  const [selectedPlayerForDetail, setSelectedPlayerForDetail] = useState<Player | null>(null);
  const [activeLocks, setActiveLocks] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(false);
  const setupInitializedRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mobileRequestsOpen, setMobileRequestsOpen] = useState(false);
  const [localHighlightId, setLocalHighlightId] = useState<string | null>(null);
  const [requestNotes, setRequestNotes] = useState<Record<string, string>>({});
  const [depositDropdownOpen, setDepositDropdownOpen] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, cellInfo: any } | null>(null);
  const contextMenuActionRef = useRef<string | null>(null);
  const [fastBlockPrompt, setFastBlockPrompt] = useState<{court: string, time: string, isPromo: boolean} | null>(null);
  const [fastBlockReason, setFastBlockReason] = useState('');
  const [fastUnblockConfirm, setFastUnblockConfirm] = useState<Booking | null>(null);
  const [refundConfirmState, setRefundConfirmState] = useState<{ resolve: (val: boolean | 'cancel_action') => void, booking: Booking } | null>(null);

  const [isCashModalOpen, setIsCashModalOpen] = useState(false);
  const [activeCashSession, setActiveCashSession] = useState<CashSession | null>(null);
  const [cashSessionsHistory, setCashSessionsHistory] = useState<CashSession[]>([]);
  const [showOpenCashModal, setShowOpenCashModal] = useState(false);
  const [showCloseCashModal, setShowCloseCashModal] = useState(false);
  const [cashTab, setCashTab] = useState<'movimientos' | 'historial'>('movimientos');
  const [initialCashInput, setInitialCashInput] = useState('');
  const [declaredCashInput, setDeclaredCashInput] = useState('');
  const [showBalance, setShowBalance] = useState(true);
  const [cashSearch, setCashSearch] = useState('');

  // --- CAROUSEL ESTADOS ---
  const daysCarouselRef = useRef<HTMLDivElement>(null);
  const [isDraggingDaysCarousel, setIsDraggingDaysCarousel] = useState(false);
  const [daysCarouselStartX, setDaysCarouselStartX] = useState(0);
  const [daysCarouselScrollLeft, setDaysCarouselScrollLeft] = useState(0);
  const [canDaysScrollLeft, setCanDaysScrollLeft] = useState(false);
  const [canDaysScrollRight, setCanDaysScrollRight] = useState(true);
  const hasDraggedDaysCarousel = useRef(false);

  const checkDaysCarouselScroll = () => {
    if (daysCarouselRef.current) {
      setCanDaysScrollLeft(daysCarouselRef.current.scrollLeft > 0);
      setCanDaysScrollRight(
        Math.ceil(daysCarouselRef.current.scrollLeft) < 
        daysCarouselRef.current.scrollWidth - daysCarouselRef.current.clientWidth
      );
    }
  };

  useEffect(() => {
    checkDaysCarouselScroll();
    window.addEventListener('resize', checkDaysCarouselScroll);
    return () => window.removeEventListener('resize', checkDaysCarouselScroll);
  }, []);
  const [cashStatusFilter, setCashStatusFilter] = useState<'all' | 'paid' | 'confirmed' | 'cancelled'>('all');
  const [cashDateFilter, setCashDateFilter] = useState<'all' | 'today' | 'month' | 'custom'>('today');
  const [cashCustomDate, setCashCustomDate] = useState('');
  const [visibleCashItemsCount, setVisibleCashItemsCount] = useState(40);

  // --- ESTADOS: EGRESOS ---
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseTab, setExpenseTab] = useState<'general' | 'stock'>('general');
  const [expenseCategory, setExpenseCategory] = useState('Gastos / Pagos');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseMethod, setExpenseMethod] = useState<'cash' | 'transfer'>('cash');
  
  // --- ESTADOS: STOCK ---
  const [products, setProducts] = useState<Product[]>([]);
  const [stockProductId, setStockProductId] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');
  
  React.useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, getCollectionPath('products')), where('clubId', '==', userId));
    return onSnapshot(q, (snap) => {
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }) as Product));
    });
  }, [db, userId, getCollectionPath]);

  // --- ESTADOS: CANTINA ---
  const [showCantinaModal, setShowCantinaModal] = useState(false);
  const [cantinaSearch, setCantinaSearch] = useState('');
  const [cantinaCategory, setCantinaCategory] = useState<'all' | 'Bebidas' | 'Snacks' | 'Equipamiento' | 'Otros'>('all');
  const [cantinaPaymentCash, setCantinaPaymentCash] = useState<string>('');
  const [cantinaCart, setCantinaCart] = useState<BookingItem[]>([]);
  const [cantinaClientName, setCantinaClientName] = useState('Consumidor Final');
  const [cantinaTabs, setCantinaTabs] = useState<CantinaTab[]>([]);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [cantinaTabDefaultName, setCantinaTabDefaultName] = useState<string | undefined>(undefined);
  const [cantinaTabSlotNumber, setCantinaTabSlotNumber] = useState<number | undefined>(undefined);
  const [isTabModalOpen, setIsTabModalOpen] = useState(false);
  const [recentCantinaPayments, setRecentCantinaPayments] = useState<{tabId?: string, slotNumber?: number, amount: number, timestamp: number}[]>([]);

  React.useEffect(() => {
    if (recentCantinaPayments.length > 0) {
      const interval = setInterval(() => {
        const now = Date.now();
        setRecentCantinaPayments(prev => prev.filter(p => now - p.timestamp < 3000));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [recentCantinaPayments.length]);

  const fetchCantinaTabs = async () => {
    // Keep empty function so onSaved doesn't break, though it's not needed anymore
  };

  useEffect(() => {
    if (!userId) return;
    
    // Configurar listener en tiempo real para las mesas abiertas
    const tabsRef = collection(db, getCollectionPath('cantina_tabs'));
    const q = query(tabsRef, where('clubId', '==', userId), where('status', '==', 'open'));
    
    const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (snapshot) => {
      if (snapshot.metadata.fromCache && snapshot.empty) {
        const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_cantina_tabs_${userId}`) : null;
        if (cachedStr && JSON.parse(cachedStr).length > 0) {
          setCantinaTabs(JSON.parse(cachedStr));
          return;
        }
      }
      const tabs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];
      // Opcionalmente ordenar por createdAt descendente como lo hacía el servicio
      tabs.sort((a, b) => b.createdAt - a.createdAt);
      if (!snapshot.empty || !snapshot.metadata.fromCache) {
        if (typeof window !== 'undefined') window.localStorage.setItem(`cached_cantina_tabs_${userId}`, JSON.stringify(tabs));
      }
      setCantinaTabs(tabs);
    }, (error) => {
      console.error("Error en real-time cantina tabs:", error);
      const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_cantina_tabs_${userId}`) : null;
      if (cachedStr) setCantinaTabs(JSON.parse(cachedStr));
    });

    return () => unsubscribe();
  }, [userId, db, getCollectionPath]);

  const [showCantinaQuickItemModal, setShowCantinaQuickItemModal] = useState(false);
  const [cantinaQuickItemName, setCantinaQuickItemName] = useState('');
  const [cantinaQuickItemPrice, setCantinaQuickItemPrice] = useState<string>('');
  const [cantinaQuickItemQuantity, setCantinaQuickItemQuantity] = useState(1);

  // --- ESTADOS: TOAST DE ÉXITO ---
  const [successToast, setSuccessToast] = useState<{message: string, show: boolean}>({message: '', show: false});
  const [errorModalMessage, setErrorModalMessage] = useState<{title: string, message: string} | null>(null);

  const showSuccessToast = (message: string) => {
    setSuccessToast({ message, show: true });
    setTimeout(() => {
      setSuccessToast(prev => ({ ...prev, show: false }));
    }, 2500);
  };



  const currentAdminProfile = players.find(p => p.id === userId);
  const userAsSede = sedesPublicas.find(s => s.uid === userId || s.id === userId);
  const staffClubId = (currentAdminProfile as any)?.clubId || (userAsSede as any)?.clubId;
  const staffClub = staffClubId ? sedesPublicas.find(s => s.uid === staffClubId || s.id === staffClubId) : null;
  const clubName = staffClub?.name || userAsSede?.name || currentAdminProfile?.name || config?.pageName || 'el club';

  const currentUser = auth.currentUser;
  const currentUserProfile = players.find(p => p.id === currentUser?.uid);
  
  // Si hay un empleado logueado en el POS, usamos su nombre. Si no, el del admin.
  const posActiveStaffId = typeof window !== 'undefined' ? window.sessionStorage.getItem('posActiveStaffId') : null;
  const posActiveStaffName = typeof window !== 'undefined' ? window.sessionStorage.getItem('posActiveStaffName') : null;
  const staffProfile = posActiveStaffId ? (posActiveStaffId === 'admin' ? { name: 'Administrador' } : players.find(p => p.email === posActiveStaffId)) : null;
  
  const activeUserName = posActiveStaffName || staffProfile?.name || currentUserProfile?.name || currentUser?.displayName || posActiveStaffId?.split('@')[0] || currentUser?.email || 'Administrador';

  useEffect(() => {
    if (highlightRequestId) {
      setLocalHighlightId(highlightRequestId);
      setMobileRequestsOpen(true);

      setTimeout(() => {
        const el = document.getElementById(`request-card-${highlightRequestId}`) || document.getElementById(`request-card-mobile-${highlightRequestId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        onHighlightHandled?.();
      }, 1000);

      const timer = setTimeout(() => {
        setLocalHighlightId(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [highlightRequestId, onHighlightHandled]);

  // --- CONFIGURACIÓN DE FORMULARIO DE SEDE ---
  const [setupActiveTab, setSetupActiveTab] = useState<'general' | 'canchas' | 'dias_horarios' | 'descuentos' | 'pagos' | 'whatsapp'>('general');
  const [setupCourts, setSetupCourts] = useState<string[]>(['Cancha 1', 'Cancha 2', 'Cancha 3']);
  const [setupOpenTime, setSetupOpenTime] = useState('09:30');
  const [setupCloseTime, setSetupCloseTime] = useState('00:30');
  const [setupSlotDuration, setSetupSlotDuration] = useState(90); // 1:30 hs
  const [setupPrice, setSetupPrice] = useState(12000);
  const [setupCashRegisterCloseTime, setSetupCashRegisterCloseTime] = useState('03:30');
  const [setupOffDays, setSetupOffDays] = useState<OffDay[]>([]);
  const [setupSpecificDaySchedules, setSetupSpecificDaySchedules] = useState<DaySchedule[]>([]);
  const [setupTimeDiscounts, setSetupTimeDiscounts] = useState<TimeDiscount[]>([]);
  const [showAddDiscountForm, setShowAddDiscountForm] = useState(false);
  const [newDiscountStart, setNewDiscountStart] = useState('09:00');
  const [newDiscountEnd, setNewDiscountEnd] = useState('12:00');
  const [newDiscountDays, setNewDiscountDays] = useState<number[]>([]); // Array of 0-6 indices
  const [newDiscountCourts, setNewDiscountCourts] = useState<string[]>([]); // Array of court names
  const [newDiscountType, setNewDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [newDiscountValue, setNewDiscountValue] = useState<number>(0);
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(null);
  const [setupAutoConfirmationEnabled, setSetupAutoConfirmationEnabled] = useState(false);
  const [setupAutoConfirmationDays, setSetupAutoConfirmationDays] = useState(1);
  const [setupAutoConfirmationHour, setSetupAutoConfirmationHour] = useState('10:00');

  // Mercado Pago & Señas setup states
  const [setupPaymentPolicy, setSetupPaymentPolicy] = useState<'none' | 'deposit_required' | 'full_payment_required'>('none');
  const [setupDepositFraction, setSetupDepositFraction] = useState<number>(4);
  const [setupPaymentDefault, setSetupPaymentDefault] = useState<0 | 25 | 50 | 100>(0);
  const [setupPaymentRules, setSetupPaymentRules] = useState<PaymentRule[]>([]);
  const [editingPaymentRuleId, setEditingPaymentRuleId] = useState<string | null>(null);
  const [newPaymentRuleStart, setNewPaymentRuleStart] = useState<string>('17:00');
  const [newPaymentRuleEnd, setNewPaymentRuleEnd] = useState<string>('22:00');
  const [newPaymentRuleDays, setNewPaymentRuleDays] = useState<number[]>([1,2,3,4,5,6]);
  const [newPaymentRulePercentage, setNewPaymentRulePercentage] = useState<25 | 50 | 100>(50);
  const [privateConfig, setPrivateConfig] = useState<any>(null);

  // --- FILTRO DE FECHA ---
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return getOperatingDateStr(new Date(), '03:30');
  });
  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const desktopDateInputRef = React.useRef<HTMLInputElement>(null);
  const [showMonthCalendar, setShowMonthCalendar] = useState(false);
  const [calendarMonthDate, setCalendarMonthDate] = useState<Date>(new Date());

  const [currentDateObj, setCurrentDateObj] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateObj(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const todayStr = React.useMemo(() => {
    return getOperatingDateStr(currentDateObj, schedule?.cashRegisterCloseTime || '03:30');
  }, [currentDateObj, schedule?.cashRegisterCloseTime]);

  // --- LÍMITES GLOBALES DEL CALENDARIO (Fase A) ---
  const { minDateLimitStr, maxDateLimitStr } = React.useMemo(() => {
    const minD = new Date(currentDateObj); minD.setDate(currentDateObj.getDate() - 30);
    const maxD = new Date(currentDateObj); maxD.setDate(currentDateObj.getDate() + 60);
    return {
      minDateLimitStr: minD.toISOString().split('T')[0],
      maxDateLimitStr: maxD.toISOString().split('T')[0]
    };
  }, [currentDateObj]);

  // Generar barra de navegación de días (Ayer a prox. semana)
  const desktopDays = React.useMemo(() => {
    const days = [];
    const closeTime = schedule?.cashRegisterCloseTime || '03:30';
    const today = new Date(getOperatingDateStr(currentDateObj, closeTime) + 'T12:00:00');
    for (let i = -1; i <= 7; i++) {
      const current = new Date(today);
      current.setDate(today.getDate() + i);
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;

      let label = '';
      if (i === 0) {
        label = 'Hoy';
      } else {
        const weekdayStr = current.toLocaleDateString('es-ES', { weekday: 'short' });
        const dayNum = current.getDate();
        const cleanWeekday = weekdayStr.replace('.', '');
        const capitalized = cleanWeekday.charAt(0).toUpperCase() + cleanWeekday.slice(1);
        label = `${capitalized} ${dayNum}`;
      }

      days.push({
        dateStr,
        label
      });
    }
    return days;
  }, [currentDateObj, schedule?.cashRegisterCloseTime]);

  const isDateInDesktopDays = React.useMemo(() => {
    return desktopDays.some(day => day.dateStr === selectedDate);
  }, [selectedDate, desktopDays]);

  const formattedCustomDate = React.useMemo(() => {
    if (!selectedDate) return '';
    const parts = selectedDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`; // DD/MM
    }
    return selectedDate;
  }, [selectedDate]);

  // Helper para generar días del mes en el calendario modal
  const calendarDaysList = React.useMemo(() => {
    const year = calendarMonthDate.getFullYear();
    const month = calendarMonthDate.getMonth(); // 0-indexed

    const firstDay = new Date(year, month, 1);
    let startOffset = firstDay.getDay() - 1; // 1 = Lunes
    if (startOffset < 0) startOffset = 6; // Domingo

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < startOffset; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      days.push({ dayNumber: i, dateStr });
    }

    return days;
  }, [calendarMonthDate]);

  
  const handleStartEditPaymentRule = (rule: PaymentRule) => {
    setEditingPaymentRuleId(rule.id);
    setNewPaymentRuleStart(rule.startHour);
    setNewPaymentRuleEnd(rule.endHour);
    setNewPaymentRuleDays(rule.daysOfWeek);
    setNewPaymentRulePercentage(rule.depositPercentage);
  };

  const handleSavePaymentRule = () => {
    if (newPaymentRuleDays.length === 0) {
      alert('Debes seleccionar al menos un día');
      return;
    }

    const newRule: PaymentRule = {
      id: editingPaymentRuleId || Date.now().toString(),
      startHour: newPaymentRuleStart,
      endHour: newPaymentRuleEnd,
      daysOfWeek: newPaymentRuleDays,
      depositPercentage: newPaymentRulePercentage
    };

    if (editingPaymentRuleId) {
      setSetupPaymentRules(setupPaymentRules.map(r => r.id === editingPaymentRuleId ? newRule : r));
    } else {
      setSetupPaymentRules([...setupPaymentRules, newRule]);
    }

    setEditingPaymentRuleId(null);
    setNewPaymentRuleStart('17:00');
    setNewPaymentRuleEnd('22:00');
    setNewPaymentRuleDays([1,2,3,4,5,6]);
    setNewPaymentRulePercentage(50);
  };

  const handleRemovePaymentRule = (id: string) => {
    setSetupPaymentRules(setupPaymentRules.filter(r => r.id !== id));
    if (editingPaymentRuleId === id) setEditingPaymentRuleId(null);
  };

  const handlePrevMonth = () => {
    const next = new Date(calendarMonthDate);
    next.setMonth(next.getMonth() - 1);
    setCalendarMonthDate(next);
  };

  const handleNextMonth = () => {
    const next = new Date(calendarMonthDate);
    next.setMonth(next.getMonth() + 1);
    setCalendarMonthDate(next);
  };

  // --- MODAL / BOTTOM SHEET ---
  const [selectedSlot, setSelectedSlot] = useState<{
    court: string;
    time: string;
    booking?: Booking;
  } | null>(null);

          
  // --- PREVENT BODY SCROLL WHEN MODALS OPEN ---
  React.useEffect(() => {
    // Definir si alguno de los modales principales está abierto
    const anyModalOpen = !!selectedSlot || showCantinaModal || isTabModalOpen;
    
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedSlot, showCantinaModal, isTabModalOpen]);

  React.useEffect(() => {
    if (selectedSlot) {
      if (contextMenuActionRef.current === 'promote') {
        contextMenuActionRef.current = null;
        return; // Don't override if explicitly requested from context menu
      }
      
      if (selectedSlot.booking && selectedSlot.booking.status === 'promo') {
        setModalActiveTab('promote');
        setPromoDiscount(selectedSlot.booking.promoDiscountPercentage || 0);
        setPromoDrink(!!selectedSlot.booking.promoPerks?.drink);
        setPromoBalls(!!selectedSlot.booking.promoPerks?.balls);
        setPromoWinnerPrize(!!selectedSlot.booking.promoPerks?.winnerPrize);
      } else {
        setModalActiveTab('assign');
        setPromoDiscount(20);
        setPromoDrink(false);
        setPromoBalls(false);
        setPromoWinnerPrize(false);
      }
    }
  }, [selectedSlot]);

  // --- GESTIÓN DE BLOQUEOS TEMPORALES DE SLOT POR EL ADMINISTRADOR ---
  const [myActiveLockId, setMyActiveLockId] = useState<string | null>(null);

  useEffect(() => {
    // Si no hay slot seleccionado, o si ya tiene una reserva real confirmada/pagada (no promo),
    // nos aseguramos de limpiar cualquier bloqueo activo que hayamos creado
    if (!selectedSlot || (selectedSlot.booking && selectedSlot.booking.status !== 'promo')) {
      if (myActiveLockId) {
        const lockId = myActiveLockId;
        setMyActiveLockId(null);
        const lockRef = doc(db, getCollectionPath('slotLocks'), lockId);
        deleteDoc(lockRef).catch(e => console.error("Error clearing admin lock:", e));
      }
      return;
    }

    // Si hay un slot seleccionado (vacío o con promo), creamos el bloqueo
    const { court, time } = selectedSlot;
    const slotId = `${userId}_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`;
    setMyActiveLockId(slotId);

    const lockRef = doc(db, getCollectionPath('slotLocks'), slotId);
    setDoc(lockRef, {
      clubId: userId,
      courtName: court,
      date: selectedDate,
      timeSlot: time,
      lockedBy: auth.currentUser?.uid || userId,
      lockedByName: 'Administrador',
      expiresAt: Date.now() + 90000
    }).catch(e => console.error("Error setting admin lock:", e));

    // Al desmontar o cambiar de slot, limpiar el bloqueo
    return () => {
      const lockRef = doc(db, getCollectionPath('slotLocks'), slotId);
      deleteDoc(lockRef).catch(e => console.error("Error clearing admin lock on cleanup:", e));
    };
  }, [selectedSlot, userId, selectedDate, db, getCollectionPath]);

  // Limpieza al descargar la página
  const myActiveLockIdRef = useRef<string | null>(null);
  useEffect(() => {
    myActiveLockIdRef.current = myActiveLockId;
  }, [myActiveLockId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (myActiveLockIdRef.current) {
        const lockId = myActiveLockIdRef.current;
        const lockRef = doc(db, getCollectionPath('slotLocks'), lockId);
        deleteDoc(lockRef).catch(() => { });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [db, getCollectionPath]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const handleFastBlock = async (court: string, time: string, isPromo: boolean, reason?: string) => {
    if (!userId || !selectedDate) return;

    try {
      const baseBookingId = isPromo 
        ? `${userId}_promo_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`
        : `${userId}_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`;
        
      const bookingDocRef = doc(db, getCollectionPath('bookings'), baseBookingId);

      const blockData: Booking = {
        id: baseBookingId,
        clubId: userId,
        courtName: court,
        date: selectedDate,
        timeSlot: time,
        playerName: 'BLOQUEADO',
        notes: reason || '',
        playerPhone: '',
        status: 'confirmed',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        price: 0,
        manualPrice: 0,
        confirmationStatus: 'confirmed',
        paymentStatus: 'pending',
        isFixed: false,
        playerConfirmed: false
      };

      await setDoc(bookingDocRef, blockData);
    } catch (err) {
      console.error('Error al bloquear casillero', err);
    }
  };

  const activeBooking = React.useMemo(() => {
    if (!selectedSlot?.booking) return null;
    let targetId = selectedSlot.booking.id;
    if (selectedSlot.booking.id.includes('_projected_')) {
      const originalId = selectedSlot.booking.id.split('_projected_')[0];
      targetId = `${originalId}_materialized_${selectedSlot.booking.date}`;
    }
    const rawBooking = bookings.find(b => b.id === targetId || b.id === selectedSlot.booking.id) || selectedSlot.booking;
    
    // Override inherited status for materialized fixed bookings
    if (rawBooking && rawBooking.isFixed && rawBooking.id.includes('_materialized_')) {
      const b = { ...rawBooking };
      
      const baseId = b.id.split('_materialized_')[0];
      const baseBooking = bookings.find(root => root.id === baseId);
      
      // If the materialized booking has the exact same number of payment history entries as the root,
      // it means no new payments were made this week; it's just inherited data.
      let hasRealPayment = false;
      if (b.paymentHistory && b.paymentHistory.length > 0) {
        if (!baseBooking || !baseBooking.paymentHistory) {
          hasRealPayment = true;
        } else {
          const baseTimestamps = new Set(baseBooking.paymentHistory.map(h => h.timestamp));
          const hasNewPayment = b.paymentHistory.some(h => !baseTimestamps.has(h.timestamp));
          if (hasNewPayment) {
            hasRealPayment = true;
          }
        }
        
        if (!hasRealPayment) {
          // Inherited payment -> strip it so the modal doesn't show it
          b.paymentHistory = [];
          b.paymentBreakdown = undefined;
          b.paymentStatus = 'unpaid';
        }
      } else if (b.paymentStatus === 'full_paid' || b.paymentStatus === 'partial_paid') {
        if (!baseBooking || b.paymentStatus !== baseBooking.paymentStatus) {
          hasRealPayment = true;
        } else {
          b.paymentStatus = 'unpaid';
        }
      }

      const hasRealConfirmation = b.playerConfirmed || b.confirmationStatus === 'confirmed';
      
      if (hasRealPayment) {
        b.status = rawBooking.status; // Trust the database, a human interacted with it!
      } else if (hasRealConfirmation) {
        b.status = 'confirmed';
      } else {
        b.status = 'pending';
      }
      return b;
    }
    
    return rawBooking;
  }, [bookings, selectedSlot, userId]);

  const isNextImmediatePendingTurn = React.useMemo(() => {
    if (!activeBooking || !activeBooking.isFixed) return false;

    const todayStr = new Date().toLocaleDateString('en-CA');
    const baseIdWithProjected = activeBooking.id.split('_projected_')[0];
      const docWithProjected = bookings.find(b => b.id === baseIdWithProjected);
      const baseId = (docWithProjected && docWithProjected.isFixedRoot) ? baseIdWithProjected : baseIdWithProjected.split('_materialized_')[0];
    const baseBooking = bookings.find(b => b.id === baseId);

    if (!baseBooking) return true; // Fallback

    const startStr = baseBooking.date > todayStr ? baseBooking.date : todayStr;
      const baseDate = new Date(startStr + 'T12:00:00');
    const targetDate = new Date(activeBooking.date + 'T12:00:00');
    const dayOfWeek = new Date(baseBooking.date + 'T12:00:00').getDay();

    let checkDate = new Date(baseDate);
    while (checkDate < targetDate) {
      if (checkDate.getDay() === dayOfWeek) {
        const dStr = checkDate.toLocaleDateString('en-CA');
        const isExcluded = baseBooking.excludedDates?.includes(dStr);

        const existingForDate = bookings.find(b =>
          b.isFixed &&
          b.date === dStr &&
          b.timeSlot === baseBooking.timeSlot &&
          b.courtName === baseBooking.courtName &&
          b.status !== 'cancelled'
        );

        const isConfirmed = existingForDate?.playerConfirmed || existingForDate?.status === 'paid' || existingForDate?.status === 'confirmed';

        if (!isExcluded && !isConfirmed && (!existingForDate || existingForDate.status !== 'cancelled')) {
          return false; // Found an earlier pending turn
        }
      }
      checkDate.setDate(checkDate.getDate() + 1);
    }

    return true;
  }, [activeBooking, bookings]);

  const activeDiscountForModal = React.useMemo(() => {
    if (!selectedSlot) return null;
    const slotDate = activeBooking ? activeBooking.date : selectedDate;
    const slotCourt = activeBooking ? activeBooking.courtName : selectedSlot.court;
    const slotTime = activeBooking ? activeBooking.timeSlot : selectedSlot.time;

    const discounts = schedule?.timeDiscounts || setupTimeDiscounts || [];
    if (!discounts.length || !slotDate || !slotCourt || !slotTime) return null;

    const d = new Date(slotDate + 'T00:00:00');
    const dayOfWeek = d.getDay();

    return discounts.find(td => {
      if (!td.daysOfWeek?.includes(dayOfWeek)) return false;
      if (td.courts && td.courts.length > 0 && !td.courts.includes(slotCourt)) return false;
      return slotTime >= td.startHour && slotTime < td.endHour;
    }) || null;
  }, [selectedSlot, activeBooking, selectedDate, schedule, setupTimeDiscounts]);

  // --- FORMULARIO DE ASIGNACIÓN MANUAL ---
                  
  
  // --- PRODUCTOS Y COMPRAS ---
    
                          
  // --- POPUP EMERGENTE DE TURNO FIJO ---
            
  // --- ESTADO DE FILTRADO PARA BUSCADOR ---
  
  // --- SUBFORMS ESTADOS LOCALES ---
  const [showAddOffDayForm, setShowAddOffDayForm] = useState(false);
  const [newOffDayDate, setNewOffDayDate] = useState('');
  const [newOffDayAllDay, setNewOffDayAllDay] = useState(true);
  const [newOffDayStart, setNewOffDayStart] = useState('08:00');
  const [newOffDayEnd, setNewOffDayEnd] = useState('14:00');
  const [newOffDayReason, setNewOffDayReason] = useState('');
  const [newOffDayCourts, setNewOffDayCourts] = useState<string[]>([]);

  const [showAddDayScheduleForm, setShowAddDayScheduleForm] = useState(false);
  const [newDayScheduleDay, setNewDayScheduleDay] = useState<number>(0); // 0 = Domingo
  const [newDayScheduleClosed, setNewDayScheduleClosed] = useState(false);
  const [newDayScheduleOpen, setNewDayScheduleOpen] = useState('09:00');
  const [newDayScheduleClose, setNewDayScheduleClose] = useState('22:00');

  // Estados de edición
  const [editingOffDayId, setEditingOffDayId] = useState<string | null>(null);
  const [editingDayScheduleDay, setEditingDayScheduleDay] = useState<number | null>(null);
        
  // Estados de carrusel de productos
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const [carouselStartX, setCarouselStartX] = useState(0);
  const [carouselScrollLeft, setCarouselScrollLeft] = useState(0);
  const hasDragged = useRef(false);

  const ensureMaterialized = async (booking: Booking, overrides: Partial<Booking> = {}) => {
    if (booking.id.includes('_projected_')) {
      const originalId = booking.id.split('_projected_')[0];
      const realId = `${originalId}_materialized_${booking.date}`;
      
      const payloadToSave = {
        ...booking,
        id: realId,
        updatedAt: Date.now(),
        ...overrides
      };

      // Ensure inherited financial data from projection is explicitly cleared in DB
      // unless the overrides (like handleTogglePaymentStatus) are explicitly adding it.
      if (!overrides.paymentHistory) {
        payloadToSave.paymentHistory = [];
        payloadToSave.paymentBreakdown = deleteField() as any;
        payloadToSave.paymentStatus = 'unpaid';
        if (!overrides.status) {
          payloadToSave.status = 'pending';
        }
      }

      const sanitizedData = Object.fromEntries(
        Object.entries(payloadToSave).filter(([_, v]) => v !== undefined)
      );
      await setDoc(doc(db, getCollectionPath('bookings'), realId), sanitizedData, { merge: true });
      return realId;
    } else {
      const bookingRef = doc(db, getCollectionPath('bookings'), booking.id);
      await updateDoc(bookingRef, { ...overrides, updatedAt: Date.now() });
      return booking.id;
    }
  };
  
  const handleConfirmFixedBooking = async (booking: Booking) => {
    const realId = await ensureMaterialized(booking, { confirmationStatus: 'confirmed', status: 'confirmed' });
    if (selectedSlot && selectedSlot.booking && (selectedSlot.booking.id === booking.id || selectedSlot.booking.id === realId)) {
      setSelectedSlot(null);
    }

    const currentUserDisplayName = activeUserName;
    const currentUserRole = userRole || 'admin';
    await logActivity(
      db,
      getCollectionPath,
      userId,
      currentUserDisplayName,
      currentUserRole,
      `Confirmación manual de asistencia para turno fijo: Cancha ${booking.courtName}, Día ${booking.date} a las ${booking.timeSlot}h (${booking.playerName || 'Jugador'})`
    );

    // Enviar notificación al jugador si está registrado
    if (booking.playerId) {
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: booking.playerId,
          type: 'info',
          title: 'Reserva Confirmada',
          body: `Tu reserva en la cancha ${booking.courtName} para el día ${booking.date} a las ${booking.timeSlot}hs ha sido confirmada por la sede. ¡Te esperamos!`,
          read: false,
          createdAt: Date.now()
        });
      } catch (error) {
        console.error('Error enviando notificación al jugador:', error);
      }
    }

    setShowConfirmAttendancePopup(false);
  };

  const handleSaveDiscountPercent = async (percent: number) => {
    const basePrice = schedule?.pricePerSlot || 12000;
    const finalPrice = Math.max(0, basePrice * (1 - percent / 100));

    if (activeBooking) {
      try {
        await ensureMaterialized(activeBooking, { price: finalPrice });
      } catch (error) {
        console.error('Error al actualizar el descuento:', error);
        alert('Error al actualizar el descuento');
      }
    } else {
      setManualPrice(finalPrice);
    }
  };

  const DAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  useEffect(() => {
    const handleOpenPending = () => {
      setMobileRequestsOpen(true);
      setTimeout(() => {
        const desktopSection = document.getElementById('admin-pending-requests-section');
        const mobileToggle = document.getElementById('mobile-pending-requests-toggle');

        if (desktopSection && desktopSection.offsetParent !== null) {
          desktopSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (mobileToggle && mobileToggle.offsetParent !== null) {
          mobileToggle.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    };

    window.addEventListener('open-pending-requests', handleOpenPending);
    return () => window.removeEventListener('open-pending-requests', handleOpenPending);
  }, []);

  const handleStartEditOffDay = (od: OffDay) => {
    setEditingOffDayId(od.id);
    setNewOffDayDate(od.date);
    setNewOffDayAllDay(od.allDay);
    setNewOffDayStart(od.startHour || '08:00');
    setNewOffDayEnd(od.endHour || '14:00');
    setNewOffDayReason(od.reason || '');
    setNewOffDayCourts(od.courts || []);
    setShowAddOffDayForm(true);
  };

  const handleCancelOffDay = () => {
    setEditingOffDayId(null);
    setNewOffDayDate('');
    setNewOffDayAllDay(true);
    setNewOffDayStart('08:00');
    setNewOffDayEnd('14:00');
    setNewOffDayReason('');
    setNewOffDayCourts([]);
    setShowAddOffDayForm(false);
  };

  const handleStartEditDaySchedule = (ds: DaySchedule) => {
    setEditingDayScheduleDay(ds.dayOfWeek);
    setNewDayScheduleDay(ds.dayOfWeek);
    setNewDayScheduleClosed(ds.closed);
    setNewDayScheduleOpen(ds.openTime || '09:00');
    setNewDayScheduleClose(ds.closeTime || '22:00');
    setShowAddDayScheduleForm(true);
  };

  const handleCancelDaySchedule = () => {
    setEditingDayScheduleDay(null);
    setNewDayScheduleDay(0);
    setNewDayScheduleClosed(false);
    setNewDayScheduleOpen('09:00');
    setNewDayScheduleClose('22:00');
    setShowAddDayScheduleForm(false);
  };

  const handleAddOffDay = () => {
    if (!newOffDayDate) {
      alert('Por favor selecciona una fecha para el día off.');
      return;
    }
    const newDay: OffDay = {
      id: editingOffDayId || Date.now().toString(),
      date: newOffDayDate,
      allDay: newOffDayAllDay,
      courts: newOffDayCourts,
    };
    if (!newOffDayAllDay) {
      newDay.startHour = newOffDayStart;
      newDay.endHour = newOffDayEnd;
    }
    if (newOffDayReason.trim()) {
      newDay.reason = newOffDayReason.trim();
    }

    if (editingOffDayId) {
      setSetupOffDays(setupOffDays.map(d => d.id === editingOffDayId ? newDay : d));
    } else {
      setSetupOffDays([...setupOffDays, newDay]);
    }

    setEditingOffDayId(null);
    setNewOffDayDate('');
    setNewOffDayAllDay(true);
    setNewOffDayReason('');
    setNewOffDayCourts([]);
    setShowAddOffDayForm(false);
  };

  const handleRemoveOffDay = async (id: string) => {
    if (!await window.confirm('¿Eliminar este cierre programado? Esta acción no se puede deshacer.')) return;
    setSetupOffDays(setupOffDays.filter(d => d.id !== id));
    if (editingOffDayId === id) {
      setEditingOffDayId(null);
    }
  };

  const handleAddDaySchedule = () => {
    // Si estamos editando, o simplemente agregando, filtramos el día que estamos guardando para actualizarlo
    const updated = setupSpecificDaySchedules.filter(s => s.dayOfWeek !== newDayScheduleDay);
    const newSched: DaySchedule = {
      dayOfWeek: newDayScheduleDay,
      closed: newDayScheduleClosed,
      openTime: newDayScheduleClosed ? '00:00' : newDayScheduleOpen,
      closeTime: newDayScheduleClosed ? '00:00' : newDayScheduleClose
    };
    setSetupSpecificDaySchedules([...updated, newSched].sort((a, b) => a.dayOfWeek - b.dayOfWeek));
    setEditingDayScheduleDay(null);
    setShowAddDayScheduleForm(false);
  };

  const handleRemoveDaySchedule = async (dayOfWeek: number) => {
    if (!await window.confirm('¿Eliminar este horario específico? Esta acción no se puede deshacer.')) return;
    setSetupSpecificDaySchedules(setupSpecificDaySchedules.filter(s => s.dayOfWeek !== dayOfWeek));
    if (editingDayScheduleDay === dayOfWeek) {
      setEditingDayScheduleDay(null);
    }
  };

  const handleStartEditDiscount = (td: TimeDiscount) => {
    setEditingDiscountId(td.id);
    setNewDiscountStart(td.startHour || '09:00');
    setNewDiscountEnd(td.endHour || '12:00');
    setNewDiscountDays(td.daysOfWeek || []);
    setNewDiscountCourts(td.courts || []);
    setNewDiscountType(td.discountType || 'percentage');
    setNewDiscountValue(td.value || 0);
    setShowAddDiscountForm(true);
  };

  const handleCancelDiscount = () => {
    setEditingDiscountId(null);
    setNewDiscountStart('09:00');
    setNewDiscountEnd('12:00');
    setNewDiscountDays([]);
    setNewDiscountCourts([]);
    setNewDiscountType('percentage');
    setNewDiscountValue(0);
    setShowAddDiscountForm(false);
  };

  const handleAddDiscount = () => {
    if (newDiscountValue <= 0) {
      alert('Por favor, ingresa un valor de descuento mayor a cero.');
      return;
    }
    if (newDiscountDays.length === 0) {
      alert('Por favor, selecciona al menos un día de la semana.');
      return;
    }

    const newTd: TimeDiscount = {
      id: editingDiscountId || Date.now().toString(),
      startHour: newDiscountStart,
      endHour: newDiscountEnd,
      daysOfWeek: newDiscountDays,
      courts: newDiscountCourts,
      discountType: newDiscountType,
      value: Number(newDiscountValue)
    };

    if (editingDiscountId) {
      setSetupTimeDiscounts(setupTimeDiscounts.map(td => td.id === editingDiscountId ? newTd : td));
    } else {
      setSetupTimeDiscounts([...setupTimeDiscounts, newTd]);
    }

    handleCancelDiscount();
  };

  const handleRemoveDiscount = async (id: string) => {
    if (!await window.confirm('¿Eliminar este descuento? Esta acción no se puede deshacer.')) return;
    setSetupTimeDiscounts(setupTimeDiscounts.filter(td => td.id !== id));
    if (editingDiscountId === id) {
      setEditingDiscountId(null);
    }
  };

  // --- ESCUCHAR PRODUCTOS ---
  useEffect(() => {
    if (!userId) return;
    const productsRef = collection(db, getCollectionPath('products'));
    const q = query(productsRef, where('clubId', '==', userId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const prodList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as Product[];
      setAvailableProducts(prodList);
    });
    return () => unsubscribe();
  }, [db, getCollectionPath, userId]);

  // --- SINCRONIZAR PRODUCTOS DEL SLOT SELECCIONADO ---
  useEffect(() => {
    if (activeBooking?.items) {
      setBookingItems(activeBooking.items);
    } else {
      setBookingItems([]);
    }
  }, [activeBooking?.items]);

  useEffect(() => {
    setSearchPlayerQuery('');
    setSelectedPlayer(null);
    manualPlayerName.current = '';
    manualPlayerPhone.current = '';
    manualNotes.current = '';
    setFormResetKey(k => k + 1);
    setManualIsFixed(false);
    setSelectedProduct(null);
    setItemQuantity(1);
    setItemNote('');
    setShowItemDropdown(false);
  }, [selectedSlot]);

const modalState = useAdminBookingModalState(schedule, activeBooking, players);
  const {
    formResetKey, setFormResetKey, manualPlayerName, manualPlayerPhone, searchPlayerQuery, setSearchPlayerQuery,
    selectedPlayer, setSelectedPlayer, showPlayerDropdown, setShowPlayerDropdown, manualNotes, manualIsFixed, setManualIsFixed,
    manualPrice, setManualPrice, currentDiscountPercent, availableProducts, setAvailableProducts, bookingItems, setBookingItems,
    selectedProduct, setSelectedProduct, itemQuantity, setItemQuantity, itemNote, setItemNote, showItemDropdown, setShowItemDropdown,
    showQuickItemModal, setShowQuickItemModal, quickItemName, setQuickItemName, quickItemPrice, setQuickItemPrice,
    quickItemQuantity, setQuickItemQuantity, showChargeConfirmModal, setShowChargeConfirmModal, chargePaymentCash, setChargePaymentCash,
    chargePaymentTransfer, setChargePaymentTransfer, chargePaymentTotal, setChargePaymentTotal, confirmFixedPopup, setConfirmFixedPopup,
    showManualFixedConfirm, setShowManualFixedConfirm, isEditingNotes, setIsEditingNotes, bookingNotesEditRef,
    showCancelConfirmPopup, setShowCancelConfirmPopup, showCobradoToast, setShowCobradoToast, filteredPlayers,
    modalActiveTab, setModalActiveTab, isEditingPrice, setIsEditingPrice, tempPrice, setTempPrice,
    isEditingDiscount, setIsEditingDiscount, tempDiscount, setTempDiscount, tempCourtSelect, setTempCourtSelect,
    tempItemSelect, setTempItemSelect, showConfirmAttendancePopup, setShowConfirmAttendancePopup,
    promoDiscount, setPromoDiscount, promoDrink, setPromoDrink, promoBalls, setPromoBalls, promoWinnerPrize, setPromoWinnerPrize
  } = modalState;



  // --- ESCUCHAR DATOS (FIRESTORE REAL-TIME) ---
  useEffect(() => {
    if (!userId) return;

    setLoading(true);

    // 1. Escuchar Configuración del Club/Sede
    const scheduleRef = doc(db, getCollectionPath('clubSchedules'), userId);
    const unsubSchedule = onSnapshot(scheduleRef, (docSnap) => {
      setupInitializedRef.current = false;
      if (docSnap.exists()) {
        const data = docSnap.data() as ClubSchedule;
        if (typeof window !== 'undefined') window.localStorage.setItem(`cached_schedule_${userId}`, JSON.stringify(data));
        setSchedule(data);
        setManualPrice(data.pricePerSlot || 12000);
        setSetupCourts(data.courts || ['Cancha 1', 'Cancha 2', 'Cancha 3']);
        setSetupOpenTime(data.openTime || '09:30');
        setSetupCloseTime(data.closeTime || '00:30');
        setSetupSlotDuration(data.slotDuration || 90);
        setSetupPrice(data.pricePerSlot || 12000);
        setSetupCashRegisterCloseTime(data.cashRegisterCloseTime || '03:30');
        setSetupOffDays(data.offDays || []);
        setSetupSpecificDaySchedules(data.specificDaySchedules || []);
        setSetupTimeDiscounts(data.timeDiscounts || []);
        setSetupAutoConfirmationEnabled(data.autoConfirmation?.enabled || false);
        setSetupAutoConfirmationDays(data.autoConfirmation?.daysAdvance || 1);
        setSetupAutoConfirmationHour(data.autoConfirmation?.sendHour || '10:00');
        setSetupPaymentPolicy(data.paymentConfig?.paymentPolicy || 'none');
        setSetupDepositFraction(data.paymentConfig?.depositFraction || 4);
        setSetupPaymentDefault(data.paymentConfig?.defaultPercentage || 0);
        setSetupPaymentRules(data.paymentConfig?.rules || []);
      } else {
        const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_schedule_${userId}`) : null;
        if (cachedStr) {
          const data = JSON.parse(cachedStr) as ClubSchedule;
          setSchedule(data);
          setManualPrice(data.pricePerSlot || 12000);
          setSetupCourts(data.courts || ['Cancha 1', 'Cancha 2', 'Cancha 3']);
          setSetupOpenTime(data.openTime || '09:30');
          setSetupCloseTime(data.closeTime || '00:30');
          setSetupSlotDuration(data.slotDuration || 90);
          setSetupPrice(data.pricePerSlot || 12000);
          setSetupCashRegisterCloseTime(data.cashRegisterCloseTime || '03:30');
          setSetupOffDays(data.offDays || []);
          setSetupSpecificDaySchedules(data.specificDaySchedules || []);
          setSetupTimeDiscounts(data.timeDiscounts || []);
          setSetupAutoConfirmationEnabled(data.autoConfirmation?.enabled || false);
          setSetupAutoConfirmationDays(data.autoConfirmation?.daysAdvance || 1);
          setSetupAutoConfirmationHour(data.autoConfirmation?.sendHour || '10:00');
          setSetupPaymentPolicy(data.paymentConfig?.paymentPolicy || 'none');
          setSetupDepositFraction(data.paymentConfig?.depositFraction || 4);
          setSetupPaymentDefault(data.paymentConfig?.defaultPercentage || 0);
          setSetupPaymentRules(data.paymentConfig?.rules || []);
        } else if (userRole && ['admin', 'superadmin', 'co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(userRole)) {
          const defaultSched: ClubSchedule = {
            id: userId,
            openTime: '08:00',
            closeTime: '23:00',
            slotDuration: 60,
            courts: ['Cancha 1', 'Cancha 2', 'Cancha 3'],
            pricePerSlot: 12000,
            updatedAt: Date.now(),
            offDays: [],
            specificDaySchedules: [],
            timeDiscounts: []
          };
          setSchedule(defaultSched);
          setManualPrice(12000);
          setSetupCourts(['Cancha 1', 'Cancha 2', 'Cancha 3']);
          setSetupOpenTime('08:00');
          setSetupCloseTime('23:00');
          setSetupSlotDuration(60);
          setSetupPrice(12000);
          setSetupCashRegisterCloseTime('03:30');
          setSetupOffDays([]);
          setSetupSpecificDaySchedules([]);
          setSetupTimeDiscounts([]);
          setSetupPaymentPolicy('none');
          setSetupDepositFraction(4);
          setSetupPaymentDefault(0);
          setSetupPaymentRules([]);
        } else {
          setSchedule(null);
          setSetupOffDays([]);
          setSetupSpecificDaySchedules([]);
          setSetupTimeDiscounts([]);
          setSetupPaymentPolicy('none');
          setSetupDepositFraction(4);
          setSetupPaymentDefault(0);
          setSetupPaymentRules([]);
        }
      }
      setTimeout(() => { setupInitializedRef.current = true; }, 500);
      setLoading(false);
    });

    // 1.5. Escuchar Configuración Privada de Mercado Pago
    let unsubPrivate = () => {};
    if (userId === auth.currentUser?.uid || userRole === 'superadmin') {
      const privateConfigRef = doc(db, getCollectionPath('privateClubSettings'), userId);
      unsubPrivate = onSnapshot(privateConfigRef, (docSnap) => {
        if (docSnap.exists()) {
          setPrivateConfig(docSnap.data());
        } else {
          setPrivateConfig(null);
        }
      });
    }

    // 2. Escuchar Reservas del Club
    const bookingsCol = collection(db, getCollectionPath('bookings'));
    
    // Optimización Fase A: Limitamos la descarga de reservas históricas para ahorrar miles de lecturas.
    // Usamos los límites definidos a nivel componente (minDateLimitStr y maxDateLimitStr)
    const bookingsQuery = query(
      bookingsCol, 
      where('clubId', '==', userId),
      where('date', '>=', minDateLimitStr),
      where('date', '<=', maxDateLimitStr)
    );

    const fixedBookingsQuery = query(
      bookingsCol,
      where('clubId', '==', userId),
      where('isFixed', '==', true)
    );

    let normalBookingsList: Booking[] = [];
    let fixedBookingsList: Booking[] = [];

    const updateMergedBookings = () => {
      const mergedMap = new Map<string, Booking>();
      fixedBookingsList.forEach(b => mergedMap.set(b.id, b));
      normalBookingsList.forEach(b => mergedMap.set(b.id, b)); // Normal overwrites if duplicate
      const merged = Array.from(mergedMap.values());
      if (typeof window !== 'undefined') window.localStorage.setItem(`cached_bookings_${userId}`, JSON.stringify(merged));
      setBookings(merged);
    };

    const unsubBookings = onSnapshot(bookingsQuery, { includeMetadataChanges: true }, (snapshot) => {
      if (snapshot.metadata.fromCache && snapshot.empty) {
        const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_bookings_${userId}`) : null;
        if (cachedStr && JSON.parse(cachedStr).length > 0) {
          setBookings(JSON.parse(cachedStr));
          return;
        }
      }
      const list: Booking[] = [];
      snapshot.forEach((d) => {
        list.push({ id: d.id, ...d.data() } as Booking);
      });
      normalBookingsList = list;
      if (!snapshot.empty || !snapshot.metadata.fromCache) {
        updateMergedBookings();
      } else {
        updateMergedBookings();
      }
    }, (error) => {
      console.error("🔥 ERROR DE FIREBASE: FALTA UN ÍNDICE. HAZ CLIC EN EL LINK QUE APARECE AQUÍ ABAJO PARA CREARLO MÁGICAMENTE 🔥");
      console.error(error.message);
      const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_bookings_${userId}`) : null;
      if (cachedStr) setBookings(JSON.parse(cachedStr));
    });

    const unsubFixedBookings = onSnapshot(fixedBookingsQuery, { includeMetadataChanges: true }, (snapshot) => {
      const list: Booking[] = [];
      snapshot.forEach((d) => {
        list.push({ id: d.id, ...d.data() } as Booking);
      });
      fixedBookingsList = list;
      if (!snapshot.empty || !snapshot.metadata.fromCache) {
        updateMergedBookings();
      }
    }, (error) => {
      console.error("🔥 ERROR DE FIREBASE (FIJOS): HAZ CLIC EN EL LINK QUE APARECE AQUÍ ABAJO 🔥");
      console.error(error.message);
    });

    // 3. Escuchar Peticiones de Reservas
    const requestsCol = collection(db, getCollectionPath('bookingRequests'));
    const requestsQuery = query(requestsCol, where('clubId', '==', userId), where('status', 'in', ['pending', 'deposit_required']));
    const unsubRequests = onSnapshot(requestsQuery, { includeMetadataChanges: true }, (snapshot) => {
      if (snapshot.metadata.fromCache && snapshot.empty) {
        const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_requests_${userId}`) : null;
        if (cachedStr && JSON.parse(cachedStr).length > 0) {
          setRequests(JSON.parse(cachedStr));
          return;
        }
      }
      const list: BookingRequest[] = [];
      snapshot.forEach((d) => {
        list.push({ id: d.id, ...d.data() } as BookingRequest);
      });
      if (!snapshot.empty || !snapshot.metadata.fromCache) {
        if (typeof window !== 'undefined') window.localStorage.setItem(`cached_requests_${userId}`, JSON.stringify(list));
      }
      setRequests(list);
    }, () => {
      const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_requests_${userId}`) : null;
      if (cachedStr) setRequests(JSON.parse(cachedStr));
    });

    // 4. Escuchar Bloqueos Temporales (slotLocks)
    const locksCol = collection(db, getCollectionPath('slotLocks'));
    const locksQuery = query(locksCol, where('clubId', '==', userId));
    const unsubLocks = onSnapshot(locksQuery, { includeMetadataChanges: true }, (snapshot) => {
      if (snapshot.metadata.fromCache && snapshot.empty) {
        const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_locks_${userId}`) : null;
        if (cachedStr && JSON.parse(cachedStr).length > 0) {
          setActiveLocks(JSON.parse(cachedStr));
          return;
        }
      }
      const list: any[] = [];
      snapshot.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      if (!snapshot.empty || !snapshot.metadata.fromCache) {
        if (typeof window !== 'undefined') window.localStorage.setItem(`cached_locks_${userId}`, JSON.stringify(list));
      }
      setActiveLocks(list);
    }, () => {
      const cachedStr = typeof window !== 'undefined' ? window.localStorage.getItem(`cached_locks_${userId}`) : null;
      if (cachedStr) setActiveLocks(JSON.parse(cachedStr));
    });

    return () => {
      unsubSchedule();
      unsubPrivate();
      unsubBookings();
      unsubFixedBookings();
      unsubRequests();
      unsubLocks();
    };
  }, [userId, db, getCollectionPath]);

  // --- GENERAR HORARIOS (SLOTS) SEGÚN APERTURA/CIERRE ---
  const timeSlots = React.useMemo(() => {
    if (!schedule) return [];

    // 1. Obtener día de la semana y fecha seleccionada
    const d = new Date(selectedDate + 'T00:00:00');
    const dayOfWeek = d.getDay(); // 0 = Domingo, 1 = Lunes, etc.

    // 2. Verificar si hay un horario específico configurado para este día
    const specSchedule = schedule.specificDaySchedules?.find(s => s.dayOfWeek === dayOfWeek);
    if (specSchedule && specSchedule.closed) {
      return []; // El club está cerrado este día
    }

    // 3. Ya no filtramos el día entero ni bloques parciales aquí.
    // Queremos que los horarios se generen siempre para que la grilla los dibuje
    // y luego el renderizado de la celda (checkIfSlotBlockedByOffDay) muestre el cartel "INHABILITADA"

    // 4. Obtener horas de apertura y cierre base (ya sea custom o estándar)
    const openStr = specSchedule?.openTime || schedule.openTime;
    const closeStr = specSchedule?.closeTime || schedule.closeTime;

    const [startH, startM] = openStr.split(':').map(Number);
    const [endH, endM] = closeStr.split(':').map(Number);

    let currentMin = startH * 60 + startM;
    let endMin = endH * 60 + endM;

    // Si el horario de cierre es menor o igual al de apertura, indica que cierra al día siguiente
    if (endMin <= currentMin) {
      endMin += 24 * 60; // Añadir 1440 minutos para el día siguiente
    }

    const slots: string[] = [];
    const duration = schedule.slotDuration || 90;

    while (currentMin + duration <= endMin) {
      const hour24 = Math.floor(currentMin / 60) % 24;
      const h = hour24.toString().padStart(2, '0');
      const m = (currentMin % 60).toString().padStart(2, '0');
      slots.push(`${h}:${m}`);

      currentMin += duration;
    }
    return slots;
  }, [schedule, selectedDate]);

  // --- CÁLCULO DE ESTADÍSTICAS REALES + MERGE DE MOCK PARA EL WOW FACTOR ---
  const stats = React.useMemo<BookingStats & { turnosFijos: number }>(() => {
    const todayDayOfWeek = new Date(todayStr + 'T00:00:00').getDay();

    // Reservas directas de HOY real
    const directToday = bookings.filter(b =>
      b.date === todayStr &&
      (b.status === 'confirmed' || b.status === 'paid') &&
      (!b.excludedDates || !b.excludedDates.includes(todayStr)) &&
      !b.hiddenFromGrid
    );

    // Proyectadas fijas que aplican a HOY real
    const projectedFixed = bookings.filter(b =>
      b.isFixed &&
      (!b.id.includes('_materialized_') || b.isFixedRoot) &&
      !b.id.includes('_cancelled_') &&
      (b.status === 'confirmed' || b.status === 'paid') &&
      new Date(b.date + 'T00:00:00').getDay() === todayDayOfWeek &&
      todayStr >= b.date &&
      (!b.excludedDates || !b.excludedDates.includes(todayStr)) &&
      !b.hiddenFromGrid &&
      !directToday.some(d => d.courtName === b.courtName && d.timeSlot === b.timeSlot)
    );

    const totalTodayBookingsList = [...directToday, ...projectedFixed];
    const reservasHoy = totalTodayBookingsList.length;
    const turnosFijos = totalTodayBookingsList.filter(b => b.isFixed).length;

    const allTransactionsToday = bookings.filter(b => {
      const isCantinaOrExpense = b.courtId === 'pos_cantina' || b.courtId === 'expense';
      const isCancelled = b.status === 'cancelled' || (b.hiddenFromGrid && !isCantinaOrExpense);
      
      let actualStatus = b.status;
      if (b.isFixed && b.id.includes('_materialized_')) {
        const baseId = b.id.split('_materialized_')[0];
        const baseBooking = bookings.find(root => root.id === baseId);
        
        let hasRealPayment = false;
        if (b.paymentHistory && b.paymentHistory.length > 0) {
          if (!baseBooking || !baseBooking.paymentHistory) {
            hasRealPayment = true;
          } else {
            const baseTimestamps = new Set(baseBooking.paymentHistory.map(h => h.timestamp));
            const hasNewPayment = b.paymentHistory.some(h => !baseTimestamps.has(h.timestamp));
            if (hasNewPayment) {
              hasRealPayment = true;
            }
          }
        } else if (b.paymentStatus === 'full_paid' || b.paymentStatus === 'partial_paid') {
          if (!baseBooking || b.paymentStatus !== baseBooking.paymentStatus) {
            hasRealPayment = true;
          }
        }
        
        const hasRealConfirmation = b.playerConfirmed || b.confirmationStatus === 'confirmed';
        
        if (hasRealPayment) {
          actualStatus = 'paid';
        } else if (hasRealConfirmation) {
          actualStatus = 'confirmed';
        } else {
          actualStatus = 'pending';
        }
      }

      return b.date === todayStr &&
             !isCancelled &&
             (actualStatus === 'confirmed' || actualStatus === 'paid') &&
             (!b.excludedDates || !b.excludedDates.includes(todayStr));
    });
    const paidToday = allTransactionsToday.filter(b => {
      let actualStatus = b.status;
      if (b.isFixed && b.id.includes('_materialized_')) {
        const baseId = b.id.split('_materialized_')[0];
        const baseBooking = bookings.find(root => root.id === baseId);
        
        let hasRealPayment = false;
        if (b.paymentHistory && b.paymentHistory.length > 0) {
          if (!baseBooking || !baseBooking.paymentHistory) {
            hasRealPayment = true;
          } else {
            const baseTimestamps = new Set(baseBooking.paymentHistory.map(h => h.timestamp));
            const hasNewPayment = b.paymentHistory.some(h => !baseTimestamps.has(h.timestamp));
            if (hasNewPayment) {
              hasRealPayment = true;
            }
          }
        } else if (b.paymentStatus === 'full_paid' || b.paymentStatus === 'partial_paid') {
          if (!baseBooking || b.paymentStatus !== baseBooking.paymentStatus) {
            hasRealPayment = true;
          }
        }
        
        if (hasRealPayment) {
          actualStatus = 'paid';
        } else {
          actualStatus = 'pending'; // For revenue, we only care if it's 'paid'
        }
      }
      return actualStatus === 'paid';
    });
    const totalTodayRevenue = paidToday.reduce((sum, b) => {
      // Ignorar retiros de dinero para no afectar el balance de ingresos, ya que no son gastos operativos
      if (b.courtId === 'expense' && b.courtName === 'Retiro de Dinero') return sum;

      let amountToSum = (b.price ?? schedule?.pricePerSlot ?? 0) + (b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0);
      if (b.paymentBreakdown && (b.paymentBreakdown.cash !== undefined || b.paymentBreakdown.transfer !== undefined)) {
        amountToSum = (b.paymentBreakdown.cash || 0) + (b.paymentBreakdown.transfer || 0);
      }
      return sum + amountToSum;
    }, 0);

    // Suma mensual de todo el mes actual real
    const currentMonth = todayStr.substring(0, 7); // "YYYY-MM"
    const monthBookings = bookings.filter(b => {
      const isCantinaOrExpense = b.courtId === 'pos_cantina' || b.courtId === 'expense';
      const isCancelled = b.status === 'cancelled' || (b.hiddenFromGrid && !isCantinaOrExpense);
      
      let actualStatus = b.status;
      if (b.isFixed && b.id.includes('_materialized_')) {
        const baseId = b.id.split('_materialized_')[0];
        const baseBooking = bookings.find(root => root.id === baseId);
        
        let hasRealPayment = false;
        if (b.paymentHistory && b.paymentHistory.length > 0) {
          if (!baseBooking || !baseBooking.paymentHistory) {
            hasRealPayment = true;
          } else {
            const baseTimestamps = new Set(baseBooking.paymentHistory.map(h => h.timestamp));
            const hasNewPayment = b.paymentHistory.some(h => !baseTimestamps.has(h.timestamp));
            if (hasNewPayment) {
              hasRealPayment = true;
            }
          }
        } else if (b.paymentStatus === 'full_paid' || b.paymentStatus === 'partial_paid') {
          if (!baseBooking || b.paymentStatus !== baseBooking.paymentStatus) {
            hasRealPayment = true;
          }
        }
        
        const hasRealConfirmation = b.playerConfirmed || b.confirmationStatus === 'confirmed';
        
        if (hasRealPayment) {
          actualStatus = 'paid';
        } else if (hasRealConfirmation) {
          actualStatus = 'confirmed';
        } else {
          actualStatus = 'pending';
        }
      }

      return b.date.startsWith(currentMonth) && actualStatus === 'paid' && !isCancelled;
    });
    const monthlyRevenue = monthBookings.reduce((sum, b) => {
      // Ignorar retiros de dinero para no afectar el balance de ingresos, ya que no son gastos operativos
      if (b.courtId === 'expense' && b.courtName === 'Retiro de Dinero') return sum;

      let amountToSum = (b.price ?? schedule?.pricePerSlot ?? 0) + (b.items?.reduce((s, item) => s + (item.price * item.quantity), 0) || 0);
      if (b.paymentBreakdown && (b.paymentBreakdown.cash !== undefined || b.paymentBreakdown.transfer !== undefined)) {
        amountToSum = (b.paymentBreakdown.cash || 0) + (b.paymentBreakdown.transfer || 0);
      }
      return sum + amountToSum;
    }, 0);

    const hasAnyBookings = bookings.length > 0;

    return {
      reservasHoy,
      turnosFijos,
      nuevosUsuarios: hasAnyBookings ? Math.max(2, Math.floor(players.length * 0.05)) : 0,
      ingresosMes: hasAnyBookings ? (monthlyRevenue || 124000) : 0,
      totalIngresos: totalTodayRevenue
    };
  }, [bookings, todayStr, schedule, players]);

  // --- CAJA: SESIONES (APERTURA Y CIERRE) ---
  React.useEffect(() => {
    if (!userId) return;
    cashService.getActiveSession(userId).then(sess => setActiveCashSession(sess));
  }, [userId]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showPlayerDropdown) setShowPlayerDropdown(false);
        else if (showItemDropdown) setShowItemDropdown(false);
        else if (showQuickItemModal) setShowQuickItemModal(false);
        else if (showCancelConfirmPopup) setShowCancelConfirmPopup(false);
        else if (showChargeConfirmModal) setShowChargeConfirmModal(false);
        else if (showMonthCalendar) setShowMonthCalendar(false);
        else if (showExpenseModal) setShowExpenseModal(false);
        else if (selectedSlot) {
          setSelectedSlot(null);
          setSelectedPlayer(null);
          setSearchPlayerQuery('');
          manualPlayerName.current = '';
          setFormResetKey(k => k + 1);
          setIsEditingPrice(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPlayerDropdown, showItemDropdown, showQuickItemModal, showCancelConfirmPopup, showChargeConfirmModal, showMonthCalendar, showExpenseModal, selectedSlot]);

  React.useEffect(() => {
    if (isCashModalOpen && cashTab === 'historial' && userId) {
      cashService.getSessionsHistory(userId).then(hist => setCashSessionsHistory(hist));
    }
  }, [isCashModalOpen, cashTab, userId]);

  const handleOpenCashSession = async () => {
    if (!userId || !userId) return;
    const inputEl = document.getElementById('initialCashInput') as HTMLInputElement;
    const initialCash = inputEl ? parseFloat(inputEl.value) : (parseFloat(initialCashInput) || 0);
    const session: Omit<CashSession, 'id'> = {
      clubId: userId,
      openedAt: Date.now(),
      closedAt: null,
      openedBy: { uid: userId, name: activeUserName || 'Usuario' },
      closedBy: null,
      initialCash,
      declaredCash: null,
      expectedCash: null,
      difference: null,
      status: 'open'
    };
    const id = await cashService.openSession(session);
    setActiveCashSession({ id, ...session });
    setShowOpenCashModal(false);
    setInitialCashInput('');
    if (cashTab === 'historial' && userId) {
      cashService.getSessionsHistory(userId).then(hist => setCashSessionsHistory(hist));
    }
  };

  const handleCloseCashSession = async () => {
    if (!activeCashSession || !userId || !userId || !activeCashSession.id) return;
    const inputEl = document.getElementById('declaredCashInput') as HTMLInputElement;
    const declaredCash = inputEl ? parseFloat(inputEl.value) : (parseFloat(declaredCashInput) || 0);
    
    let shiftCash = 0;
    bookings.forEach(b => {
      const isCantinaOrExpense = b.courtId === 'pos_cantina' || b.courtId === 'expense';
      const effectiveStatus = (b.status === 'cancelled' || (b.hiddenFromGrid && !isCantinaOrExpense)) ? 'cancelled' : b.status;
      
      // Solo tomamos pagos realizados DESPUES de la apertura de la caja
      if (effectiveStatus === 'paid' && b.updatedAt >= activeCashSession.openedAt) {
        let pCash = b.price ?? schedule?.pricePerSlot ?? 12000;
        const productsSum = b.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
        pCash += productsSum;

        if (b.paymentBreakdown && (b.paymentBreakdown.cash !== undefined || b.paymentBreakdown.transfer !== undefined)) {
          pCash = b.paymentBreakdown.cash || 0;
        }
        
        shiftCash += pCash;
      }
    });

    const expectedCash = activeCashSession.initialCash + shiftCash;
    const difference = declaredCash - expectedCash;
    
    const updates = {
      closedAt: Date.now(),
      closedBy: { uid: userId, name: activeUserName || 'Usuario' },
      declaredCash,
      expectedCash,
      difference,
      status: 'closed' as const
    };
    await cashService.closeSession(activeCashSession.id, updates);
    setActiveCashSession(null);
    setShowCloseCashModal(false);
    setDeclaredCashInput('');
    setCashTab('historial');
    if (userId) {
      cashService.getSessionsHistory(userId).then(hist => setCashSessionsHistory(hist));
    }
  };

  // --- CÁLCULO DE CAJA REGISTRADORA Y TRANSACCIONES ---
  const cashFilteredBookings = React.useMemo(() => {
    // Convertir las mesas abiertas en turnos ficticios para que aparezcan en la grilla de transacciones
    const fakeBookingsFromTabs: any[] = cantinaTabs.map(tab => {
      const d = new Date(tab.createdAt);
      const tabDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const timeSlot = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
      return {
        id: tab.id,
        clubId: tab.clubId,
        courtName: 'CANTINA',
        courtId: 'pos_cantina',
        playerName: tab.name,
        date: tabDate,
        timeSlot: timeSlot,
        duration: 0,
        status: 'confirmed', // Esto hace que figuren como "Pendientes"
        isFixed: false,
        price: 0,
        items: tab.items,
        paymentBreakdown: undefined,
        paymentHistory: [],
        hiddenFromGrid: false
      };
    });

    const combinedBookings = [...bookings, ...fakeBookingsFromTabs];

    return combinedBookings.filter(b => {
      // Excluir turnos que aún están pendientes de confirmación
      if (b.status === 'pending') return false;

      // Búsqueda por nombre de cliente o cancha
      const matchSearch = !cashSearch.trim() ||
        b.playerName.toLowerCase().includes(cashSearch.toLowerCase()) ||
        b.courtName.toLowerCase().includes(cashSearch.toLowerCase());

      // Filtro de Estado de Pago
      const isCantinaOrExpense = b.courtId === 'pos_cantina' || b.courtId === 'expense';
      const effectiveStatus = (b.status === 'cancelled' || (b.hiddenFromGrid && !isCantinaOrExpense)) ? 'cancelled' : b.status;
      const matchStatus = cashStatusFilter === 'all' || effectiveStatus === cashStatusFilter;

      // Filtro de Fecha (Histórico vs Hoy vs Mes actual vs Día específico)
      let matchDate = true;
      if (cashDateFilter === 'today') {
        matchDate = b.date === todayStr && (!b.excludedDates || !b.excludedDates.includes(todayStr));
      } else if (cashDateFilter === 'month') {
        const currentMonth = todayStr.substring(0, 7);
        matchDate = b.date.startsWith(currentMonth) && (!b.excludedDates || !b.excludedDates.includes(b.date));
      } else if (cashDateFilter === 'custom' && cashCustomDate) {
        matchDate = b.date === cashCustomDate && (!b.excludedDates || !b.excludedDates.includes(cashCustomDate));
      }

      return matchSearch && matchStatus && matchDate;
    }).sort((a, b) => {
      // Ordenar por fecha y franja horaria en orden cronológico inverso (lo más reciente arriba)
      if (a.date !== b.date) {
        return b.date.localeCompare(a.date);
      }
      
      const closeTime = schedule?.cashRegisterCloseTime || '03:30';
      
      const getComparableTime = (timeStr) => {
        if (!timeStr) return 0;
        // Si la hora de la reserva es menor a la hora de cierre de caja, significa que corresponde a la madrugada del día siguiente
        if (timeStr < closeTime) {
          const [h, m] = timeStr.split(':').map(Number);
          return (h + 24) * 60 + m; // Sumar 24 horas para que tenga mayor peso cronológico
        }
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
      };

      const timeA = getComparableTime(a.timeSlot);
      const timeB = getComparableTime(b.timeSlot);
      return timeB - timeA;
    });
  }, [bookings, cantinaTabs, cashSearch, cashStatusFilter, cashDateFilter, cashCustomDate, todayStr]);

  const cashTotals = React.useMemo(() => {
    let totalPaid = 0;
    let totalPending = 0;
    let totalProducts = 0;
    let totalCash = 0;
    let totalTransfer = 0;

    cashFilteredBookings.forEach(b => {
      const isCantinaOrExpense = b.courtId === 'pos_cantina' || b.courtId === 'expense';
      const effectiveStatus = (b.status === 'cancelled' || (b.hiddenFromGrid && !isCantinaOrExpense)) ? 'cancelled' : b.status;

      const basePrice = b.price ?? schedule?.pricePerSlot ?? 12000;
      const productsSum = b.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
      
      if (effectiveStatus !== 'cancelled') {
        totalProducts += productsSum;
      }

      if (effectiveStatus === 'paid') {
        let amountToSum = basePrice + productsSum;
        let pCash = amountToSum;
        let pTransfer = 0;

        if (b.paymentBreakdown && (b.paymentBreakdown.cash !== undefined || b.paymentBreakdown.transfer !== undefined)) {
          amountToSum = (b.paymentBreakdown.cash || 0) + (b.paymentBreakdown.transfer || 0);
          pCash = b.paymentBreakdown.cash || 0;
          pTransfer = b.paymentBreakdown.transfer || 0;
        }
        
        totalPaid += amountToSum;
        totalCash += pCash;
        totalTransfer += pTransfer;
      } else if (effectiveStatus === 'confirmed') {
        totalPending += basePrice + productsSum;
      }
    });

    return {
      totalPaid,
      totalPending,
      totalProducts,
      totalCash,
      totalTransfer,
      totalTransactions: cashFilteredBookings.length
    };
  }, [cashFilteredBookings, schedule]);

  const handleTogglePaymentStatus = async (booking: Booking) => {
    // Si es paid pasa a confirmed, si es confirmed pasa a paid
    const newStatus = booking.status === 'paid' ? 'confirmed' : 'paid';
    const newPaymentStatus = newStatus === 'paid' ? 'full_paid' : 'unpaid';
    try {
      await ensureMaterialized(booking, { status: newStatus, paymentStatus: newPaymentStatus });
    } catch (error) {
      console.error('Error al cambiar el estado del pago:', error);
      alert('Error al actualizar el estado de pago del turno');
    }
  };

  // --- ACCIONES DE FIRESTORE ---

  // Guardar setup inicial de la sede
  const handleSaveSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (setupCourts.length === 0) return;

    try {
      const scheduleRef = doc(db, getCollectionPath('clubSchedules'), userId);

      // Sanitizar arreglos para eliminar posibles campos 'undefined' molestos para Firestore
      const sanitizedOffDays = setupOffDays.map(od => {
        const cleaned: any = {
          id: od.id,
          date: od.date,
          allDay: od.allDay
        };
        if (od.startHour !== undefined && od.startHour !== null) cleaned.startHour = od.startHour;
        if (od.endHour !== undefined && od.endHour !== null) cleaned.endHour = od.endHour;
        if (od.reason !== undefined && od.reason !== null) cleaned.reason = od.reason;
        if (od.courts !== undefined && od.courts !== null) cleaned.courts = od.courts;
        return cleaned;
      });

      const sanitizedSpecificSchedules = setupSpecificDaySchedules.map(ss => {
        const cleaned: any = {
          dayOfWeek: ss.dayOfWeek,
          closed: ss.closed,
          openTime: ss.openTime,
          closeTime: ss.closeTime
        };
        return cleaned;
      });

      const sanitizedTimeDiscounts = setupTimeDiscounts.map(td => {
        const cleaned: any = {
          id: td.id,
          startHour: td.startHour,
          endHour: td.endHour,
          daysOfWeek: td.daysOfWeek,
          courts: td.courts || [],
          discountType: td.discountType,
          value: Number(td.value)
        };
        return cleaned;
      });

      await setDoc(scheduleRef, {
        id: userId,
        courts: setupCourts,
        openTime: setupOpenTime,
        closeTime: setupCloseTime,
        slotDuration: setupSlotDuration,
        pricePerSlot: Number(setupPrice),
        cashRegisterCloseTime: setupCashRegisterCloseTime,
        updatedAt: Date.now(),
        offDays: sanitizedOffDays,
        specificDaySchedules: sanitizedSpecificSchedules,
        timeDiscounts: sanitizedTimeDiscounts,
        autoConfirmation: {
          enabled: setupAutoConfirmationEnabled,
          daysAdvance: Number(setupAutoConfirmationDays),
          sendHour: setupAutoConfirmationHour,
          mode: 'reminder'
        },
        paymentConfig: {
          paymentPolicy: setupPaymentPolicy,
          depositFraction: Number(setupDepositFraction),
          defaultPercentage: setupPaymentDefault,
          rules: setupPaymentRules
        }
      });

      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Modificación de ajustes de configuración de reservas y horarios (Canchas, Days Off, Descuentos)`
      );

      setSetupSuccess(true);
      setTimeout(() => setSetupSuccess(false), 4000);
      if (!onlySetup) {
        setShowSetup(false);
      }
    } catch (err) {
      console.error("Error al guardar la configuración de la sede", err);
    }
  };

  // Auto-guardado: función que persiste la configuración sin cerrar el panel
  const saveSetupToFirebase = async () => {
    if (setupCourts.length === 0) return;
    try {
      const scheduleRef = doc(db, getCollectionPath('clubSchedules'), userId);
      const sanitizedOffDays = setupOffDays.map(od => {
        const cleaned: any = { id: od.id, date: od.date, allDay: od.allDay };
        if (od.startHour !== undefined && od.startHour !== null) cleaned.startHour = od.startHour;
        if (od.endHour !== undefined && od.endHour !== null) cleaned.endHour = od.endHour;
        if (od.reason !== undefined && od.reason !== null) cleaned.reason = od.reason;
        if (od.courts !== undefined && od.courts !== null) cleaned.courts = od.courts;
        return cleaned;
      });
      const sanitizedSpecificSchedules = setupSpecificDaySchedules.map(ss => ({
        dayOfWeek: ss.dayOfWeek, closed: ss.closed, openTime: ss.openTime, closeTime: ss.closeTime
      }));
      const sanitizedTimeDiscounts = setupTimeDiscounts.map(td => ({
        id: td.id, startHour: td.startHour, endHour: td.endHour, daysOfWeek: td.daysOfWeek,
        courts: td.courts || [], discountType: td.discountType, value: Number(td.value)
      }));
      await setDoc(scheduleRef, {
        id: userId, courts: setupCourts, openTime: setupOpenTime, closeTime: setupCloseTime,
        slotDuration: setupSlotDuration, pricePerSlot: Number(setupPrice), cashRegisterCloseTime: setupCashRegisterCloseTime, updatedAt: Date.now(),
        offDays: sanitizedOffDays, specificDaySchedules: sanitizedSpecificSchedules,
        timeDiscounts: sanitizedTimeDiscounts,
        autoConfirmation: {
          enabled: setupAutoConfirmationEnabled, daysAdvance: Number(setupAutoConfirmationDays),
          sendHour: setupAutoConfirmationHour, mode: 'reminder'
        },
        paymentConfig: {
          paymentPolicy: setupPaymentPolicy,
          depositFraction: Number(setupDepositFraction),
          defaultPercentage: setupPaymentDefault,
          rules: setupPaymentRules
        }
      });
      setSetupSuccess(true);
      setTimeout(() => setSetupSuccess(false), 3000);
    } catch (err) {
      console.error('Error en auto-guardado de configuración', err);
    }
  };

  const handleMockLinkMP = async () => {
    try {
      const mpConfigRef = doc(db, getCollectionPath('settings'), 'mercadopago');
      const mpConfigSnap = await getDoc(mpConfigRef);
      
      let mpClientId = '5637598502898558'; // Hardcoded based on provided credentials
      // if (mpConfigSnap.exists()) {
      //   mpClientId = mpConfigSnap.data().clientId || '';
      // }

      if (mpClientId) {
        // Solo mandamos el userId para evitar que el WAF de Mercado Pago bloquee la URL de localhost en el state
        const state = userId;
        const redirectUri = encodeURIComponent('https://us-central1-roca-padel-tour.cloudfunctions.net/mercadopagoRedirect');
        const oauthUrl = `https://auth.mercadopago.com.ar/authorization?client_id=${mpClientId}&response_type=code&platform_id=mp&state=${state}&redirect_uri=${redirectUri}`;
        
        window.location.href = oauthUrl;
      } else {
        const privateRef = doc(db, getCollectionPath('privateClubSettings'), userId);
        await setDoc(privateRef, {
          mpAccessToken: 'mock_access_token_123456789',
          mpPublicKey: 'mock_public_key_123456789',
          linkedAt: Date.now(),
          linkedEmail: 'finanzas@' + (clubName.toLowerCase().replace(/\s+/g, '')) + '.com',
          mpUserId: 'mock_user_id_123456789'
        });
        alert('¡Cuenta de Mercado Pago vinculada con éxito (Modo Demo)!');
      }
    } catch (e) {
      console.error(e);
      alert('Error al vincular cuenta.');
    }
  };

  const handleUnlinkMP = async () => {
    if (window.confirm('¿Estás seguro de que deseas desvincular tu cuenta de Mercado Pago? Las reservas online ya no podrán cobrarse por este medio.')) {
      try {
        const privateRef = doc(db, getCollectionPath('privateClubSettings'), userId);
        await deleteDoc(privateRef);
        // Also disable policies if MP is unlinked
        setSetupPaymentPolicy('none');
        setSetupPaymentDefault(0);
        setSetupPaymentRules([]);
        alert('Cuenta desvinculada.');
      } catch (e) {
        console.error(e);
      }
    }
  };

  // useEffect de auto-guardado con debounce (900ms tras cada cambio)
  useEffect(() => {
    if (!setupInitializedRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      saveSetupToFirebase();
    }, 900);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupOpenTime, setupCloseTime, setupSlotDuration, setupPrice, setupCashRegisterCloseTime, setupOffDays, setupSpecificDaySchedules, setupTimeDiscounts, setupAutoConfirmationEnabled, setupAutoConfirmationDays, setupAutoConfirmationHour, setupCourts, setupPaymentPolicy, setupDepositFraction, setupPaymentDefault, setupPaymentRules]);

  // Función automática para rechazar solicitudes pendientes que coincidan con un turno ya reservado
  const autoReconcileOverlappingRequests = async (date: string, courtName: string, timeSlot: string, approvedRequestId?: string) => {
    try {
      // Filtrar solicitudes que coincidan con la misma fecha, cancha y horario
      const overlapping = requests.filter(r =>
        r.status === 'pending' &&
        r.id !== approvedRequestId &&
        r.date === date && (
          (r.option1.courtName === courtName && r.option1.timeSlot === timeSlot) ||
          (r.option2 && r.option2.courtName === courtName && r.option2.timeSlot === timeSlot)
        )
      );

      for (const req of overlapping) {
        const reqRef = doc(db, getCollectionPath('bookingRequests'), req.id);

        // Actualizar estado en Firebase
        await updateDoc(reqRef, {
          status: 'rejected',
          adminNote: 'El turno fue asignado a otra reserva confirmada previamente.'
        });

        // Enviar notificación al jugador afectado
        await addDoc(collection(db, getCollectionPath('notifications')), {
          userId: req.playerId,
          title: clubName,
          body: `Tu solicitud de turno para el ${new Date(req.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} ${req.date.split('-').reverse().join('/')} a las ${timeSlot} hs en ${clubName} (${courtName}) fue cancelada automáticamente porque el casillero fue reservado.`,
          type: 'booking_rejected',
          createdAt: Date.now(),
          url: '/?tab=reservas&subtab=mis-turnos'
        });
      }
    } catch (err) {
      console.error("Error al auto-conciliar solicitudes solapadas:", err);
    }
  };

  // Crear o Editar Reserva Manualmente
  const handleAssignBooking = async () => {
    if (!selectedSlot) return;
    const { court, time } = selectedSlot;

    const name = selectedPlayer ? selectedPlayer.name : manualPlayerName.current.trim();
    if (!name) return;

    if (!selectedPlayer && manualPlayerPhone.current.trim()) {
      if (manualPlayerPhone.current.trim().length < 10) {
        alert("Por favor, ingresa un número de teléfono válido (al menos 10 dígitos).");
        return;
      }
    }

    console.log("🔥 VALIDATION START:", { manualIsFixed, booking: selectedSlot.booking, isFixedProp: selectedSlot.booking?.isFixed });

    if (manualIsFixed && (!selectedSlot.booking || !selectedSlot.booking.isFixed)) {
      console.log("🔥 ENTRA A VALIDACION DE TURNO FIJO");
      try {
        const projectedDates: string[] = [selectedDate];
        const dateObj = new Date(selectedDate + 'T12:00:00');
        const selectedDayOfWeek = dateObj.getDay();

        // Revisamos selectedDate y las próximas 8 semanas (9 fechas en total)
        for (let i = 1; i <= 8; i++) {
          dateObj.setDate(dateObj.getDate() + 7);
          projectedDates.push(dateObj.toISOString().split('T')[0]);
        }
        
        console.log("🔥 FECHAS PROYECTADAS:", projectedDates);
        
        // 1. Conflictos con turnos fijos (ya están en memoria via fixedBookingsQuery)
        const fixedConflicts = bookings.filter(b => {
          if (b.id === selectedSlot?.booking?.id) return false; // Ignorar a sí misma
          if (b.courtName !== court || b.timeSlot !== time) return false;
          if (b.status === 'cancelled' || b.hiddenFromGrid) return false;
          if (!['confirmed', 'paid', 'pending'].includes(b.status)) return false;
          
          if (b.isFixed) {
             const bDateObj = new Date(b.date + 'T12:00:00');
             if (bDateObj.getDay() === selectedDayOfWeek) {
                 // Colisiona si la fecha proyectada es mayor o igual a la fecha de inicio (b.date) y NO está excluida
                 return projectedDates.some(d => {
                     if (d < b.date) return false;
                     return !b.excludedDates || !b.excludedDates.includes(d);
                 });
             }
          }
          return false;
        });

        console.log("🔥 FIXED CONFLICTS:", fixedConflicts);

        if (fixedConflicts.length > 0) {
          const conflict = fixedConflicts[0];
          const [y, m, d] = conflict.date.split('-');
          setErrorModalMessage({
            title: "COLISIÓN DE TURNOS",
            message: `No se puede crear un turno fijo aquí. Este horario choca con la serie de turnos fijos a nombre de ${conflict.playerName}, iniciada el ${d}/${m}/${y}.\n\nIntenta buscar otro horario o cancela la reserva futura primero.`
          });
          return;
        }

        // 2. Conflictos con turnos eventuales (consultamos la BD para cubrir fechas fuera de la vista actual)
        const futureDocsQuery = query(
          collection(db, getCollectionPath('bookings')),
          where('clubId', '==', userId),
          where('date', 'in', projectedDates)
        );
        const futureSnap = await getDocs(futureDocsQuery);
        
        let eventualConflict: Booking | null = null;
        futureSnap.forEach(docSnap => {
          if (eventualConflict) return; // Si ya encontramos uno, no seguimos buscando
          
          const b = docSnap.data() as Booking;
          if (b.id === selectedSlot?.booking?.id || docSnap.id === selectedSlot?.booking?.id) return; // Ignorar el turno actual
          if (b.courtName !== court || b.timeSlot !== time) return;
          if (b.status === 'cancelled' || b.hiddenFromGrid) return;
          if (!['confirmed', 'paid', 'pending'].includes(b.status)) return;
          if (b.isFixed) return; // Ya se validaron
          
          if (!b.excludedDates || !b.excludedDates.includes(b.date)) {
             eventualConflict = b;
          }
        });

        if (eventualConflict) {
          const conflict = eventualConflict as Booking;
          const [y, m, d] = conflict.date.split('-');
          setErrorModalMessage({
            title: "COLISIÓN DE TURNOS",
            message: `No se puede crear un turno fijo aquí. Este horario choca con una reserva existente el día ${d}/${m}/${y} a nombre de ${conflict.playerName}.\n\nIntenta buscar otro horario o cancela la reserva futura primero.`
          });
          return;
        }
      } catch (err: any) {
        console.error("Error al validar disponibilidad futura", err);
        alert("Ocurrió un error al validar la disponibilidad en las próximas semanas. Por favor, revisa la consola o intenta de nuevo.");
        return;
      }
    }

    const bookingData: Partial<Booking> = {
      clubId: userId,
      courtName: court,
      date: selectedDate,
      timeSlot: time,
      duration: schedule?.slotDuration || 90,
      status: 'confirmed',
      confirmationStatus: 'confirmed',
      isFixed: manualIsFixed,
      notes: manualNotes.current || '',
      price: Number(manualPrice),
      updatedAt: Date.now(),
      playerName: name,
      assignedByClub: true
    };

    if (selectedPlayer) {
      bookingData.playerId = selectedPlayer.id;
      bookingData.playerCategory = selectedPlayer.category || '';
      bookingData.playerPhoto = selectedPlayer.photoUrl || '';
      bookingData.contactInfo = selectedPlayer.phone || '';
    } else {
      // Explícitamente borramos el playerId si quedó de una reserva anterior (evita bug de merge)
      bookingData.playerId = deleteField() as any;
      if (manualPlayerPhone.current.trim()) {
        bookingData.contactInfo = manualPlayerPhone.current.trim();
      }
    }

    try {
      const baseBookingId = `${userId}_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`;
      let bookingId = selectedSlot.booking?.id || baseBookingId;
      let bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);

      const rawData = {
        id: bookingId,
        createdAt: selectedSlot.booking?.createdAt || Date.now(),
        operatorEmail: window.sessionStorage.getItem('posActiveStaffId') || undefined,
        ...bookingData
      };

      // Filtrar campos que contengan valor 'undefined' para evitar que falle Firestore
      const sanitizedData = Object.fromEntries(
        Object.entries(rawData).filter(([_, v]) => v !== undefined)
      );

      // Usar Transacción para evitar colisiones en reservas nuevas
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(bookingDocRef);
        // Si ya existe la reserva y NO estamos editando, arrojamos error de colisión, salvo que sea una reserva con esta fecha excluida
        if (docSnap.exists() && !selectedSlot.booking) {
          const existingData = docSnap.data() as Booking;
          if ((existingData.excludedDates && existingData.excludedDates.includes(selectedDate)) || existingData.status === 'cancelled' || existingData.hiddenFromGrid) {
            bookingId = `${baseBookingId}_manual_${Date.now()}`;
            bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);
            sanitizedData.id = bookingId;
          } else {
            throw new Error("COLLISION");
          }
        }

        // Eliminar promo si existe para liberar el casillero
        const promoBookingId = `${userId}_promo_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`;
        const promoBookingDocRef = doc(db, getCollectionPath('bookings'), promoBookingId);
        transaction.delete(promoBookingDocRef);

        transaction.set(bookingDocRef, sanitizedData, { merge: true });
      });

      // Notificación al jugador si se le asignó un turno desde el panel o se fijó su turno
      if (selectedPlayer) {
        const wasJustAssigned = !selectedSlot.booking || selectedSlot.booking.playerId !== selectedPlayer.id;
        const wasMadeFixed = manualIsFixed && selectedSlot.booking && !selectedSlot.booking.isFixed;

        if (wasJustAssigned || wasMadeFixed) {
          const diaSemana = new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' });
          const notifBody = manualIsFixed
            ? `Hola ${name}, el club ${clubName} te ha asignado un turno fijo todos los ${diaSemana} a las ${time} hs en ${court}.`
            : `Hola ${name}, el club ${clubName} te ha asignado un turno para el ${diaSemana} ${selectedDate.split('-').reverse().join('/')} a las ${time} hs en ${court}.`;

          await addDoc(collection(db, getCollectionPath('notifications')), {
            userId: selectedPlayer.id,
            title: clubName,
            body: notifBody,
            type: manualIsFixed ? 'fixed_booking_assigned' : 'booking_accepted',
            createdAt: Date.now(),
            url: '/?tab=reservas&subtab=mis-turnos'
          });
        }
      }

      // Registro de Actividad
      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Creación de reserva ${manualIsFixed ? 'fija' : 'única'} - Cancha: ${court}, Fecha: ${selectedDate}, Horario: ${time}hs, Titular: ${name}`
      );

      // Auto-conciliar solicitudes solapadas para este casillero
      await autoReconcileOverlappingRequests(selectedDate, court, time);

      // Reset
      setSelectedSlot(null);
      manualPlayerName.current = '';
      manualPlayerPhone.current = '';
      manualNotes.current = '';
      setFormResetKey(k => k + 1);
      setSearchPlayerQuery('');
      setSelectedPlayer(null);
      setShowPlayerDropdown(false);
      setManualIsFixed(false);
    } catch (err: any) {
      if (err.message === "COLLISION") {
        setErrorModalMessage({
          title: "COLISIÓN DE RESERVA",
          message: "⚠️ ¡Colisión de reserva detectada! Este casillero de horario acaba de ser ocupado por otra reserva simultánea. La operación fue cancelada para evitar sobreescrituras."
        });
      } else {
        console.error("Error al asignar reserva", err);
        setErrorModalMessage({
          title: "ERROR AL GUARDAR",
          message: "Ocurrió un error al guardar la reserva: " + err.message
        });
      }
    }
  };

  // Guardar o Actualizar una Promoción de Turno
  const handleSavePromo = async () => {
    if (!selectedSlot) return;
    const { court, time } = selectedSlot;

    const baseBookingId = `${userId}_promo_${court.replace(/\s+/g, '')}_${selectedDate}_${time.replace(':', '')}`;
    const bookingId = selectedSlot.booking?.status === 'promo' ? selectedSlot.booking.id : baseBookingId;
    const bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);

    const promoData: Booking = {
      id: bookingId,
      clubId: userId,
      courtName: court,
      date: selectedDate,
      timeSlot: time,
      duration: schedule?.slotDuration || 90,
      status: 'promo',
      isFixed: false,
      playerName: 'Turno Promocionado',
      createdAt: selectedSlot.booking?.createdAt || Date.now(),
      updatedAt: Date.now(),
      isPromo: true,
      promoDiscountPercentage: Number(promoDiscount) || 0,
      promoPerks: {
        drink: promoDrink,
        balls: promoBalls,
        winnerPrize: promoWinnerPrize
      }
    };

    try {
      await setDoc(bookingDocRef, promoData, { merge: true });

      // Registro de Actividad
      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Publicación de promoción - Cancha: ${court}, Fecha: ${selectedDate}, Horario: ${time}hs, Descuento: ${promoDiscount}%, Perks: [Bebida: ${promoDrink ? 'SÍ' : 'NO'}, Pelotas: ${promoBalls ? 'SÍ' : 'NO'}, Premio: ${promoWinnerPrize ? 'SÍ' : 'NO'}]`
      );

      // Reset
      setSelectedSlot(null);
    } catch (err: any) {
      console.error("Error al guardar promoción:", err);
      alert("Ocurrió un error al guardar la promoción: " + err.message);
    }
  };

  // Eliminar Promoción
  const handleDeletePromo = async () => {
    if (!selectedSlot || !selectedSlot.booking || selectedSlot.booking.status !== 'promo') return;
    if (!await window.confirm("¿Estás seguro de que quieres eliminar esta promoción? El slot volverá a estar disponible con el precio normal de la grilla.")) return;

    try {
      const bookingDocRef = doc(db, getCollectionPath('bookings'), selectedSlot.booking.id);
      await deleteDoc(bookingDocRef);

      // Registro de Actividad
      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Eliminación de promoción - Cancha: ${selectedSlot.court}, Fecha: ${selectedDate}, Horario: ${selectedSlot.time}hs`
      );

      setSelectedSlot(null);
    } catch (err: any) {
      console.error("Error al eliminar promoción:", err);
      alert("Ocurrió un error al eliminar la promoción: " + err.message);
    }
  };

  // --- AGREGAR Y QUITAR PRODUCTOS AL TURNO ---
  const handleDirectAddToCart = async (prod: any) => {
    if (!activeBooking) return;

    let updatedItems = [...(activeBooking.items || [])];
    const existingIndex = updatedItems.findIndex(i => i.id.startsWith(prod.id + '_'));

    if (existingIndex >= 0) {
      const existingItem = updatedItems[existingIndex];
      const newQuantity = existingItem.quantity + 1;
      // Si el ítem no tenía splitParts o sus splitParts eran iguales a la cantidad previa, se actualizan al nuevo total
      let newSplitParts = existingItem.splitParts;
      if (existingItem.paidCount && existingItem.paidCount > 0) {
        if (!existingItem.splitParts || existingItem.splitParts === existingItem.quantity) {
          newSplitParts = newQuantity;
        }
      }
      const updated = {
        ...existingItem,
        quantity: newQuantity,
        splitParts: (existingItem.paidCount && existingItem.paidCount > 0) ? newSplitParts : undefined
      };
      updatedItems[existingIndex] = Object.fromEntries(Object.entries(updated).filter(([_, v]) => v !== undefined)) as any;
    } else {
      const newItem = {
        id: `${prod.id}_${Date.now()}`,
        name: prod.name,
        price: prod.price,
        quantity: 1,
        note: '',
        imageUrl: prod.imageUrl || ''
      };
      updatedItems.push(Object.fromEntries(Object.entries(newItem).filter(([_, v]) => v !== undefined)) as any);
    }

    // ACTUALIZACIÓN OPTIMISTA UI
    setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, items: updatedItems } : b));
    if (selectedSlot && selectedSlot.booking && selectedSlot.booking.id === activeBooking.id) {
      setSelectedSlot({
        ...selectedSlot,
        booking: { ...selectedSlot.booking, items: updatedItems }
      });
    }

    try {
      await ensureMaterialized(activeBooking, { items: updatedItems });
    } catch (error) {
      console.error('Error al agregar producto:', error);
      alert('Error al agregar producto');
    }
  };

  const handleRemoveFromTicket = async (prod: any) => {
    if (!activeBooking) return;
    let updatedItems = [...(activeBooking.items || [])];
    const existingIndex = updatedItems.findIndex(i => i.id.startsWith(prod.id + '_'));
    if (existingIndex >= 0) {
      const existingItem = updatedItems[existingIndex];
      if (existingItem.quantity > 1) {
        const newQuantity = existingItem.quantity - 1;
        let newSplitParts = existingItem.splitParts;
        if (existingItem.paidCount && existingItem.paidCount > 0) {
          if (!existingItem.splitParts || existingItem.splitParts === existingItem.quantity) {
            newSplitParts = newQuantity;
          }
        }
        const maxAllowed = newSplitParts || newQuantity;
        const newPaid = (existingItem.paidCount || 0) > maxAllowed ? maxAllowed : existingItem.paidCount;
        const updated = {
          ...existingItem,
          quantity: newQuantity,
          paidCount: newPaid && newPaid > 0 ? newPaid : undefined,
          splitParts: newPaid && newPaid > 0 ? newSplitParts : undefined
        };
        updatedItems[existingIndex] = Object.fromEntries(Object.entries(updated).filter(([_, v]) => v !== undefined)) as any;
      } else {
        updatedItems.splice(existingIndex, 1);
      }

      // ACTUALIZACIÓN OPTIMISTA UI
      setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, items: updatedItems } : b));
      if (selectedSlot && selectedSlot.booking && selectedSlot.booking.id === activeBooking.id) {
        setSelectedSlot({
          ...selectedSlot,
          booking: { ...selectedSlot.booking, items: updatedItems }
        });
      }

      try {
        await ensureMaterialized(activeBooking, { items: updatedItems });
      } catch (error) {
        console.error('Error al quitar producto:', error);
      }
    }
  };

  const handleAddQuickItem = async () => {
    if (!quickItemName.trim() || quickItemPrice === '' || quickItemQuantity < 1 || !activeBooking) return;

    const newItem: BookingItem = {
      id: `quick_${Date.now()}`,
      name: quickItemName.trim(),
      price: Number(quickItemPrice),
      quantity: quickItemQuantity,
      note: '',
      imageUrl: ''
    };

    const cleanedItem = Object.fromEntries(Object.entries(newItem).filter(([_, v]) => v !== undefined)) as any;
    const updatedItems = [...(activeBooking.items || []), cleanedItem];

    try {
      await ensureMaterialized(activeBooking, { items: updatedItems });

      setQuickItemName('');
      setQuickItemPrice('');
      setQuickItemQuantity(1);
      setShowQuickItemModal(false);
    } catch (error) {
      console.error('Error al agregar item rápido:', error);
      alert('Error al agregar item rápido');
    }
  };

  const handleRemoveProductFromSlot = async (itemId: string) => {
    if (!activeBooking) return;

    const updatedItems = (activeBooking.items || [])
      .filter(item => item.id !== itemId)
      .map(item => Object.fromEntries(Object.entries(item).filter(([_, v]) => v !== undefined)) as any);

    // ACTUALIZACIÓN OPTIMISTA UI
    setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, items: updatedItems } : b));
    if (selectedSlot && selectedSlot.booking && selectedSlot.booking.id === activeBooking.id) {
      setSelectedSlot({
        ...selectedSlot,
        booking: { ...selectedSlot.booking, items: updatedItems }
      });
    }

    try {
      await ensureMaterialized(activeBooking, { items: updatedItems });
    } catch (error) {
      console.error('Error al quitar producto:', error);
      alert('Error al quitar producto');
    }
  };

  const handleUpdateItemQuantity = async (itemId: string, newQuantity: number) => {
    if (!activeBooking) return;

    let updatedItems: any[];
    if (newQuantity <= 0) {
      updatedItems = (activeBooking.items || []).filter(item => item.id !== itemId);
    } else {
      updatedItems = (activeBooking.items || []).map(item => {
        if (item.id === itemId) {
          const currentPaid = item.paidCount ?? 0;
          let newSplitParts = item.splitParts;
          if (currentPaid > 0 && (!item.splitParts || item.splitParts === item.quantity)) {
            newSplitParts = newQuantity;
          }
          const maxAllowed = newSplitParts || newQuantity;
          const newPaid = currentPaid > maxAllowed ? maxAllowed : currentPaid;
          const updated = {
            ...item,
            quantity: newQuantity,
            paidCount: newPaid > 0 ? newPaid : undefined,
            splitParts: newPaid > 0 ? newSplitParts : undefined
          };
          return Object.fromEntries(Object.entries(updated).filter(([_, v]) => v !== undefined));
        }
        return Object.fromEntries(Object.entries(item).filter(([_, v]) => v !== undefined));
      });
    }

    // ACTUALIZACIÓN OPTIMISTA UI
    setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, items: updatedItems } : b));
    if (selectedSlot && selectedSlot.booking && selectedSlot.booking.id === activeBooking.id) {
      setSelectedSlot({
        ...selectedSlot,
        booking: { ...selectedSlot.booking, items: updatedItems }
      });
    }

    try {
      await ensureMaterialized(activeBooking, { items: updatedItems });
    } catch (error) {
      console.error('Error al actualizar cantidad del producto:', error);
      alert('Error al actualizar cantidad');
    }
  };

  const getShortDayOfWeek = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T12:00:00');
      const days = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
      return days[date.getDay()];
    } catch (e) {
      return '';
    }
  };

  const handleUpdateBookingPrice = async (newPrice: number) => {
    if (!activeBooking) return;
    try {
      await ensureMaterialized(activeBooking, { price: newPrice });
    } catch (error) {
      console.error('Error al actualizar el precio de la cancha:', error);
      alert('Error al actualizar el precio de la cancha');
    }
  };

  const handleUpdateBookingNotes = async (newNotes: string) => {
    if (!activeBooking) return;
    try {
      await ensureMaterialized(activeBooking, { notes: newNotes });
    } catch (error) {
      console.error('Error al actualizar notas:', error);
      alert('Error al actualizar las notas');
    }
  };

  const currentSlotBookingTotal = React.useMemo(() => {
    if (!activeBooking) return 0;
    const basePrice = activeBooking.courtId === 'pos_cantina' ? 0 : (activeBooking.price ?? schedule?.pricePerSlot ?? 12000);
    const itemsTotal = activeBooking.courtId === 'expense' ? 0 : (activeBooking.items || []).reduce((acc, item) => acc + (item.price * item.quantity), 0);
    return basePrice + itemsTotal;
  }, [activeBooking, schedule?.pricePerSlot]);

  const currentSlotPaidTotal = React.useMemo(() => {
    if (!activeBooking) return 0;
    const basePrice = activeBooking.price ?? schedule?.pricePerSlot ?? 12000;
    const courtPaidCount = activeBooking.courtPaidPlayersCount || 0;
    const courtPaidAmount = (basePrice / 4) * courtPaidCount;

    const itemsPaidAmount = (activeBooking.items || []).reduce((acc, item) => {
      const pCount = item.paidCount || 0;
      if (pCount <= 0) return acc;
      if (item.splitParts && item.splitParts > 0) {
        return acc + ((item.price * item.quantity) / item.splitParts) * pCount;
      }
      return acc + (item.price * pCount);
    }, 0);

    return courtPaidAmount + itemsPaidAmount;
  }, [activeBooking, schedule?.pricePerSlot]);

  const currentSlotRemainingTotal = React.useMemo(() => {
    return Math.max(0, currentSlotBookingTotal - currentSlotPaidTotal);
  }, [currentSlotBookingTotal, currentSlotPaidTotal]);

    
  const handleUpdateItemPaidCount = async (itemId: string, paidCount: number, splitParts?: number) => {
    if (!activeBooking) return;
    const updatedItems = (activeBooking.items || []).map(item => {
      if (item.id === itemId) {
        const updated = { 
          ...item, 
          paidCount: paidCount > 0 ? paidCount : undefined,
          splitParts: paidCount <= 0 ? undefined : (splitParts !== undefined ? splitParts : item.splitParts)
        };
        return Object.fromEntries(Object.entries(updated).filter(([_, v]) => v !== undefined));
      }
      return Object.fromEntries(Object.entries(item).filter(([_, v]) => v !== undefined));
    });

    // Actualización optimista inmediata en UI
    setBookings(prev => prev.map(b => b.id === activeBooking.id ? { ...b, items: updatedItems } : b));
    if (selectedSlot && selectedSlot.booking && selectedSlot.booking.id === activeBooking.id) {
      setSelectedSlot({
        ...selectedSlot,
        booking: { ...selectedSlot.booking, items: updatedItems }
      });
    }

    try {
      await ensureMaterialized(activeBooking, { items: updatedItems });
    } catch (error) {
      console.error('Error al actualizar cobro parcial del ítem:', error);
    }
  };

  const handleUpdateCourtPaidPlayers = async (playersCount: number) => {
    if (!activeBooking) return;
    try {
      await ensureMaterialized(activeBooking, { courtPaidPlayersCount: playersCount });
    } catch (error) {
      console.error('Error al actualizar cobro parcial de cancha:', error);
    }
  };

  const handleModifyPaymentClick = (booking: Booking) => {
    const totalAmt = booking.courtId === 'pos_cantina' ? (booking.items?.reduce((acc, i) => acc + (i.price * i.quantity), 0) || 0) : booking.courtId === 'expense' ? (booking.price || 0) : ((booking.price || 0) + (booking.items?.reduce((acc, i) => acc + (i.price * i.quantity), 0) || 0));
    setChargePaymentTotal(totalAmt.toString());
    setChargePaymentCash(booking.paymentBreakdown?.cash?.toString() || '');
    setChargePaymentTransfer(booking.paymentBreakdown?.transfer?.toString() || '');
    setShowChargeConfirmModal(true);
  };

  // Actualizar Estado de Pago (Cobrar o Modificar)
  const handleMarkAsPaid = async (booking: Booking, paymentBreakdown?: { cash: number, transfer: number }, newTotal?: number) => {
    setShowCobradoToast(true);
    try {
      const isModification = booking.status === 'paid';
      const currentUserDisplayName = activeUserName || 'Admin';
      const itemsTotal = booking.courtId === 'expense' ? 0 : (booking.items?.reduce((acc, i) => acc + (i.price * i.quantity), 0) || 0);
      const totalAmt = newTotal !== undefined ? newTotal : (booking.courtId === 'pos_cantina' ? itemsTotal : ((booking.price || 0) + itemsTotal));
      const historyEntry = {
        action: isModification ? 'modification' : 'initial_charge',
        timestamp: Date.now(),
        by: currentUserDisplayName,
        previousTotal: isModification ? ((booking.price || 0) + itemsTotal) : null,
        newTotal: totalAmt,
        previousCash: booking.paymentBreakdown?.cash || 0,
        previousTransfer: booking.paymentBreakdown?.transfer || 0,
        newCash: paymentBreakdown?.cash || 0,
        newTransfer: paymentBreakdown?.transfer || 0,
      };

      await ensureMaterialized(booking, { 
        status: 'paid', 
        ...(paymentBreakdown ? { paymentBreakdown } : {}),
        paymentHistory: arrayUnion(historyEntry) as any
      });

      // --- STOCK DECREMENT ---
      const alerts: string[] = [];
      if (!isModification && booking.items && booking.items.length > 0) {
        for (const item of booking.items) {
          if (!item.id.startsWith('fast_item_') && !item.id.startsWith('quick_cantina_')) {
            const rawId = item.id.split('_')[0]; 
            const productRef = doc(db, getCollectionPath('products'), rawId);
            const prod = products.find(p => p.id === rawId);
            const updates: any = { salesCount: increment(item.quantity) };
            
            if (prod && prod.trackStock !== false && prod.stock !== undefined && prod.stock !== null) {
              updates.stock = increment(-item.quantity);
              updates.updatedAt = Date.now();
              const predictedStock = Math.max(0, prod.stock - item.quantity);
              if (predictedStock <= (prod.minStockAlert || 0) && prod.stock > (prod.minStockAlert || 0)) {
                alerts.push(`¡Alerta de stock! Quedan aprox. ${predictedStock} unidades de ${prod.name}.`);
              } else if (predictedStock <= (prod.minStockAlert || 0)) {
                alerts.push(`Stock bajo: Quedan aprox. ${predictedStock} unidades de ${prod.name}.`);
              }
            }
            updateDoc(productRef, updates).catch(e => console.log(e));
          }
        }
      }
      
      if (alerts.length > 0) {
        setTimeout(() => alert(alerts.join('\n')), 500);
      }
      // -----------------------

      // Registro de Actividad
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Cobro de reserva - Cancha: ${booking.courtName}, Fecha: ${booking.date}, Horario: ${booking.timeSlot}hs, Titular: ${booking.playerName}`
      );

      // Enviar notificación push al jugador (mediante trigger onCreate de la colección 'notifications')
      if (booking.playerId) {
        await addDoc(collection(db, getCollectionPath('notifications')), {
          userId: booking.playerId,
          title: clubName,
          body: `¡Se ha registrado el pago de tu turno para el ${new Date(booking.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} ${booking.date.split('-').reverse().join('/')} a las ${booking.timeSlot} hs en ${clubName} con éxito!`,
          type: 'booking_paid',
          createdAt: Date.now(),
          url: '/?tab=reservas&subtab=mis-turnos'
        });
      }

      setTimeout(() => {
        setShowCobradoToast(false);
        setSelectedSlot(null);
      }, 1000);
    } catch (err) {
      console.error("Error al cobrar turno", err);
      setShowCobradoToast(false);
    }
  };

  // Anular cobro
  const handleUnmarkPaid = async (booking: Booking) => {
    try {
      const currentUserDisplayName = activeUserName || 'Admin';
      const totalAmt = booking.courtId === 'pos_cantina' ? (booking.items?.reduce((acc, i) => acc + (i.price * i.quantity), 0) || 0) : booking.courtId === 'expense' ? (booking.price || 0) : ((booking.price || 0) + (booking.items?.reduce((acc, i) => acc + (i.price * i.quantity), 0) || 0));

      const historyEntry = {
        action: 'annulment',
        timestamp: Date.now(),
        by: currentUserDisplayName,
        previousTotal: totalAmt,
        newTotal: 0,
        previousCash: booking.paymentBreakdown?.cash || 0,
        previousTransfer: booking.paymentBreakdown?.transfer || 0,
        newCash: 0,
        newTransfer: 0,
      };

      await ensureMaterialized(booking, { 
        status: 'confirmed', 
        paymentBreakdown: deleteField() as any,
        paymentHistory: arrayUnion(historyEntry) as any
      });

      // --- RESTAURAR STOCK ---
      // --- RESTAURAR STOCK ---
      if (booking.items && booking.items.length > 0) {
        for (const item of booking.items) {
          if (!item.id.startsWith('fast_item_') && !item.id.startsWith('quick_cantina_')) {
            const rawId = item.id.split('_')[0]; 
            const productRef = doc(db, getCollectionPath('products'), rawId);
            const prod = products.find(p => p.id === rawId);
            const updates: any = { salesCount: increment(-item.quantity) };
            
            if (prod && prod.trackStock !== false && prod.stock !== undefined && prod.stock !== null) {
              updates.stock = increment(item.quantity);
              updates.updatedAt = Date.now();
            }
            updateDoc(productRef, updates).catch(e => console.log(e));
          }
        }
      }
      // -----------------------

      // Registro de Actividad
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Anulación de cobro - Cancha: ${booking.courtName}, Fecha: ${booking.date}, Horario: ${booking.timeSlot}hs, Titular: ${booking.playerName}`
      );
    } catch (err) {
      console.error("Error al anular cobro del turno", err);
      alert("Error al anular cobro");
    }
  };

  // NUEVO: Enviar solicitud de confirmación manual para turno fijo
  const handleSendConfirmationRequest = async (booking: Booking) => {
    try {
      const realBookingId = await ensureMaterialized(booking, {
        confirmationRequested: true,
        confirmationRequestedAt: Date.now(),
        playerConfirmed: false,
        updatedAt: Date.now()
      });

      // Notificación push (simulada aquí por log)
      await logActivity(
        db,
        getCollectionPath,
        userId,
        activeUserName,
        userRole || 'admin',
        `Solicitud de confirmación enviada - Cancha: ${booking.courtName}, Horario: ${booking.timeSlot}hs, Titular: ${booking.playerName}`
      );

      if (selectedSlot && selectedSlot.booking && (selectedSlot.booking.id === booking.id || selectedSlot.booking.id === realBookingId)) {
        setSelectedSlot({
          ...selectedSlot,
          booking: {
            ...selectedSlot.booking,
            id: realBookingId,
            confirmationRequested: true,
            playerConfirmed: false
          }
        });
      }

      alert('Solicitud de confirmación enviada al jugador.');
    } catch (err) {
      console.error("Error sending confirmation request:", err);
      alert('Error al enviar la solicitud.');
    }
  };

  // Eliminar / Cancelar Reserva sin window.confirm
  const executeDeleteBooking = async (booking: Booking, mode: 'single' | 'all') => {
    console.log('🔴 executeDeleteBooking CALLED', { id: booking.id, isFixed: booking.isFixed, mode, date: booking.date, court: booking.courtName, time: booking.timeSlot, status: booking.status });
    try {
      let refunded = false;
      if (booking.mpPaymentId && (booking.paymentStatus === 'partial_paid' || booking.paymentStatus === 'full_paid' || booking.status === 'confirmed')) {
        const wantsRefund = await new Promise<boolean | 'cancel_action'>((resolve) => {
          setRefundConfirmState({ resolve, booking });
        });
        
        if (wantsRefund === 'cancel_action') return;

        if (wantsRefund) {
          try {
            const functionsInstance = getFunctions(app);
            const refundPayment = httpsCallable(functionsInstance, 'refundMercadoPagoPayment');
            await refundPayment({ bookingId: booking.id, clubId: booking.clubId || userId });
            alert('¡Reembolso procesado exitosamente en Mercado Pago!');
            refunded = true;
          } catch (e: any) {
            console.error('Error devolviendo pago:', e);
            alert('Hubo un error al intentar devolver el pago: ' + e.message);
            const proceed = window.confirm('¿Deseas continuar con la cancelación de la reserva a pesar de que el reembolso falló?');
            if (!proceed) return;
          }
        }
      }
      if (booking.isFixed) {
        if (mode === 'single') {
          // === CANCELAR SOLO ESTA FECHA DE UN TURNO FIJO ===
          // La serie sigue activa para las semanas siguientes.

          // 1. Encontrar la raíz REAL de la serie fija
          const targetDayOfWeek = new Date(booking.date + 'T00:00:00').getDay();
          const seriesCandidates = bookings.filter(b =>
            b.isFixed &&
            (!b.id.includes('_materialized_') || b.isFixedRoot) &&
            !b.id.includes('_cancelled_') &&
            b.courtName === booking.courtName &&
            b.timeSlot === booking.timeSlot &&
            new Date(b.date + 'T00:00:00').getDay() === targetDayOfWeek &&
            b.date <= booking.date &&
            b.status !== 'cancelled'
          );
          seriesCandidates.sort((a, b) => a.date.localeCompare(b.date));
          const seriesRoot = seriesCandidates[0] || null;
          console.log('🔴 seriesCandidates:', seriesCandidates.map(c => ({ id: c.id, date: c.date, status: c.status })));
          console.log('🔴 seriesRoot:', seriesRoot ? { id: seriesRoot.id, date: seriesRoot.date, excludedDates: seriesRoot.excludedDates } : 'NULL');

          if (seriesRoot) {
            // 2. Agregar fecha a excludedDates de la raíz (para que no se proyecte más en esta fecha)
            const rootRef = doc(db, getCollectionPath('bookings'), seriesRoot.id);
            console.log('🔴 Updating excludedDates on root:', seriesRoot.id, 'adding date:', booking.date);
            await updateDoc(rootRef, { excludedDates: arrayUnion(booking.date) });
            console.log('🔴 excludedDates updated successfully');

            // 3. Crear registro materializado cancelado para historial (si era proyección o la raíz en su propia fecha)
            const isProjected = booking.id.includes('_projected_');
            const isRootItself = booking.id === seriesRoot.id;

            if (isProjected || (isRootItself && booking.date === seriesRoot.date)) {
              const historyId = `${seriesRoot.id}_cancelled_${booking.date}`;
              const historyRef = doc(db, getCollectionPath('bookings'), historyId);
              const { id: _, ...bookingData } = booking;
              
              // Limpiar valores undefined que Firebase no soporta
              const cleanData = Object.fromEntries(
                Object.entries(bookingData).filter(([_, v]) => v !== undefined)
              );

              await setDoc(historyRef, {
                ...cleanData,
                id: historyId,
                status: 'cancelled',
                isFixed: false,
                updatedAt: Date.now()
              });
            }

            // 4. Si el booking es un doc materializado real (no proyección, no raíz), cancelarlo
            if (!isRootItself && !isProjected) {
              const bookingDocRef = doc(db, getCollectionPath('bookings'), booking.id);
              if (booking.status === 'paid') {
                await updateDoc(bookingDocRef, { hiddenFromGrid: true });
              } else {
                await updateDoc(bookingDocRef, { status: 'cancelled' });
              }
            }

            // 5. Cancelar cualquier otro doc para esta misma fecha/cancha/horario
            //    (ej: docs creados por ensureMaterialized al cambiar precio, confirmar, etc.)
            const staleDocsForDate = bookings.filter(b =>
              b.id !== booking.id &&
              b.id !== seriesRoot.id &&
              !b.id.includes('_projected_') &&
              b.courtName === booking.courtName &&
              b.timeSlot === booking.timeSlot &&
              b.date === booking.date &&
              b.status !== 'cancelled' &&
              !b.hiddenFromGrid
            );
            for (const staleDoc of staleDocsForDate) {
              const staleRef = doc(db, getCollectionPath('bookings'), staleDoc.id);
              if (staleDoc.status === 'paid') {
                await updateDoc(staleRef, { hiddenFromGrid: true });
              } else {
                await updateDoc(staleRef, { status: 'cancelled' });
              }
            }
          } else {
            // Fallback: no se encontró raíz de serie. Cancelar el booking individual directamente.
            if (!booking.id.includes('_projected_')) {
              const bookingDocRef = doc(db, getCollectionPath('bookings'), booking.id);
              if (booking.status === 'paid') {
                await updateDoc(bookingDocRef, { hiddenFromGrid: true });
              } else {
                await updateDoc(bookingDocRef, { status: 'cancelled' });
              }
            }
          }
        } else {
          // === CANCELAR PERMANENTEMENTE (TODA LA SERIE) ===
          const targetDayOfWeek = new Date(booking.date + 'T00:00:00').getDay();
          const matchingSeries = bookings.filter(b =>
            b.isFixed &&
            b.courtName === booking.courtName &&
            b.timeSlot === booking.timeSlot &&
            new Date(b.date + 'T00:00:00').getDay() === targetDayOfWeek
          );

          for (const b of matchingSeries) {
            if (!b.id.includes('_projected_')) {
              const bRef = doc(db, getCollectionPath('bookings'), b.id);
              if (b.date >= booking.date) {
                if (b.status === 'paid') {
                  await updateDoc(bRef, { hiddenFromGrid: true });
                } else {
                  await updateDoc(bRef, { status: 'cancelled' });
                }
              } else {
                await updateDoc(bRef, { isFixed: false });
              }
            }
          }

          if (!booking.id.includes('_projected_')) {
            try {
              const bookingDocRef = doc(db, getCollectionPath('bookings'), booking.id);
              if (booking.status === 'paid') {
                await updateDoc(bookingDocRef, { hiddenFromGrid: true });
              } else {
                await updateDoc(bookingDocRef, { status: 'cancelled' });
              }
            } catch (e) { }
          }
        }
      } else {
        // === CANCELAR BOOKING NORMAL (NO FIJO) ===
        const bookingDocRef = doc(db, getCollectionPath('bookings'), booking.id);
        if (booking.courtId === 'pos_cantina' || booking.courtId === 'expense') {
          await updateDoc(bookingDocRef, { status: 'cancelled' });
        } else if (booking.status === 'paid') {
          await updateDoc(bookingDocRef, { hiddenFromGrid: true });
        } else {
          await updateDoc(bookingDocRef, { status: 'cancelled' });
        }
      }

      // Registro de Actividad
      await logActivity(
        db,
        getCollectionPath,
        userId,
        activeUserName,
        userRole || 'admin',
        `Cancelación de reserva ${booking.isFixed ? (mode === 'all' ? 'fija permanente' : 'fija (sólo esta fecha)') : 'única'} - Cancha: ${booking.courtName}, Fecha: ${booking.date}, Horario: ${booking.timeSlot}hs, Titular: ${booking.playerName}`
      );

      // Notificación al jugador (solo si no estaba pagado)
      if (booking.playerId && booking.status !== 'paid') {
        const isSingleFixed = booking.isFixed && mode === 'single';
        const isAllFixed = booking.isFixed && mode === 'all';

        const diaSemana = new Date(booking.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' });
        const dateFormatted = booking.date.split('-').reverse().join('/');

        let notifTitle = 'Turno cancelado';
        let notifBody = `Hola ${booking.playerName}, tu turno para el ${diaSemana} ${dateFormatted} a las ${booking.timeSlot} hs en ${clubName} ha sido cancelado por el administrador.`;
        let notifType = 'booking_cancelled';

        if (isSingleFixed) {
          notifTitle = 'Turno fijo: fecha cancelada';
          notifBody = `Hola ${booking.playerName}, tu turno fijo del ${diaSemana} ${dateFormatted} a las ${booking.timeSlot} hs en ${clubName} fue cancelado para esta fecha. Tu turno fijo sigue activo para las próximas semanas.`;
        } else if (isAllFixed) {
          notifTitle = 'Baja de Turno Fijo';
          notifBody = `Hola ${booking.playerName}, el club ${clubName} ha dado de baja tu turno fijo de los ${diaSemana} a las ${booking.timeSlot} hs. Todos los turnos futuros de esta serie fueron cancelados.`;
          notifType = 'fixed_booking_removed';
        }

        await addDoc(collection(db, getCollectionPath('notifications')), {
          userId: booking.playerId,
          title: notifTitle,
          body: notifBody,
          type: notifType,
          createdAt: Date.now(),
          url: '/?tab=reservas&subtab=mis-turnos'
        });
      }

      await cancelAssociatedMatch(booking.id);

      setSelectedSlot(null);
    } catch (err) {
      console.error("Error al eliminar reserva", err);
      alert("Error al eliminar la reserva");
    }
  };

  // Resolver Petición del Jugador (Aceptar o Rechazar)
  // Requerir seña para una solicitud
  const handleRequireDeposit = async (request: BookingRequest, option: 'option1' | 'option2', percentage: 25 | 50 | 100) => {
    try {
      const reqRef = doc(db, getCollectionPath('bookingRequests'), request.id);

      await runTransaction(db, async (transaction) => {
        const reqSnap = await transaction.get(reqRef);
        if (!reqSnap.exists()) {
          throw new Error("REQUEST_NOT_FOUND");
        }
        if (reqSnap.data()?.status !== 'pending') {
          throw new Error("ALREADY_RESOLVED");
        }

        // Marcar la solicitud como deposit_required pero guardando la opción elegida
        transaction.update(reqRef, {
          status: 'deposit_required',
          requiredDepositPercentage: percentage,
          selectedOptionForDeposit: option,
          depositExpiresAt: Date.now() + 10 * 60 * 1000 // 10 minutes timeout
        });
      });

      // Registro de Actividad
      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Requirió seña del ${percentage}% a solicitud - Titular: ${request.playerName}`
      );

      // Notificar al jugador
      const chosenOption = option === 'option1' ? request.option1 : request.option2;
      if (chosenOption) {
        await addDoc(collection(db, getCollectionPath('notifications')), {
          userId: request.playerId,
          title: 'Seña requerida para confirmar tu turno',
          body: `El club requiere una seña del ${percentage}% para confirmar tu turno el ${request.date.split('-').reverse().join('/')} a las ${chosenOption.timeSlot}hs.`,
          type: 'deposit_required',
          createdAt: Date.now(),
          url: '/?tab=reservas&subtab=mis-turnos'
        });
      }

    } catch (error: any) {
      console.error('Error requiring deposit:', error);
      if (error.message === 'ALREADY_RESOLVED') {
        alert('Esta solicitud ya fue resuelta o ya se le pidió seña.');
      } else {
        alert('Ocurrió un error al intentar requerir la seña.');
      }
    }
  };

  const handleCancelDeposit = async (request: BookingRequest) => {
    try {
      const reqRef = doc(db, getCollectionPath('bookingRequests'), request.id);

      await runTransaction(db, async (transaction) => {
        const reqSnap = await transaction.get(reqRef);
        if (!reqSnap.exists()) {
          throw new Error("REQUEST_NOT_FOUND");
        }
        if (reqSnap.data()?.status !== 'deposit_required') {
          throw new Error("NOT_DEPOSIT_REQUIRED");
        }

        // Revertir a pendiente
        transaction.update(reqRef, {
          status: 'pending',
          requiredDepositPercentage: deleteField(),
          selectedOptionForDeposit: deleteField(),
          depositExpiresAt: deleteField()
        });
      });

      // Registro de Actividad
      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Canceló requerimiento de seña para turno - Titular: ${request.playerName}`
      );

      // (Opcional: podríamos eliminar la notificación de "Seña requerida" aquí, 
      // pero para simplificar dejamos que el usuario la ignore, o podemos buscarla y borrarla si es estrictamente necesario)

    } catch (error: any) {
      console.error('Error canceling deposit requirement:', error);
      if (error.message === 'NOT_DEPOSIT_REQUIRED') {
        alert('Esta solicitud no estaba esperando una seña.');
      } else {
        alert('Ocurrió un error al intentar cancelar el requerimiento de seña.');
      }
    }
  };

  const handleResolveRequest = async (request: BookingRequest, option: 'option1' | 'option2' | 'reject', adminNote?: string) => {
    try {
      const reqRef = doc(db, getCollectionPath('bookingRequests'), request.id);

      if (option === 'reject') {
        // Ejecutar transacción para rechazo
        await runTransaction(db, async (transaction) => {
          const reqSnap = await transaction.get(reqRef);
          if (!reqSnap.exists()) {
            throw new Error("REQUEST_NOT_FOUND");
          }
          if (reqSnap.data()?.status !== 'pending') {
            throw new Error("ALREADY_RESOLVED");
          }

          transaction.update(reqRef, {
            status: 'rejected',
            adminNote: adminNote || ''
          });
        });

        // Registro de Actividad
        const currentUserDisplayName = activeUserName;
        const currentUserRole = userRole || 'admin';
        await logActivity(
          db,
          getCollectionPath,
          userId,
          currentUserDisplayName,
          currentUserRole,
          `Rechazo solicitud de turno - Fecha: ${request.date}, Horario: ${request.option1?.timeSlot || ''}hs, Titular: ${request.playerName}`
        );

        // Enviar notificación push de rejection al jugador
        await addDoc(collection(db, getCollectionPath('notifications')), {
          userId: request.playerId,
          title: clubName,
          body: adminNote
            ? `Tu solicitud de turno para el ${new Date(request.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} ${request.date.split('-').reverse().join('/')} en ${request.clubName || clubName} ha sido rechazada. Motivo: "${adminNote}"`
            : `Tu solicitud de turno para el ${new Date(request.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} ${request.date.split('-').reverse().join('/')} en ${request.clubName || clubName} ha sido rechazada.`,
          type: 'booking_rejected',
          createdAt: Date.now(),
          url: '/?tab=reservas&subtab=mis-turnos'
        });
        return;
      }

      const chosenOption = option === 'option1' ? request.option1 : request.option2;
      if (!chosenOption) return;

      // 1. Crear Reserva Confirmada
      let bookingId = `${request.clubId}_${chosenOption.courtName.replace(/\s+/g, '')}_${request.date}_${chosenOption.timeSlot.replace(':', '')}`;
      let bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);

      const bookingData = {
        id: bookingId,
        clubId: request.clubId,
        courtName: chosenOption.courtName,
        date: request.date,
        timeSlot: chosenOption.timeSlot,
        duration: schedule?.slotDuration || 90,
        status: 'confirmed' as const,
        isFixed: false,
        notes: request.notes || 'Petición aprobada desde la app',
        adminNote: adminNote || '', // Guardamos la nota del admin para que el jugador la vea
        price: request.price !== undefined ? request.price : (schedule?.pricePerSlot || 12000),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        playerId: request.playerId,
        playerName: request.playerName,
        playerCategory: request.playerCategory || '',
        playerPhoto: request.playerPhoto || '',
        assignedByClub: true, // Also added this to mark it properly
        ...(request.isPromo ? {
          isPromo: true,
          promoDiscountPercentage: request.promoDetails?.discount || 0,
          promoPerks: {
            drink: request.promoDetails?.drink || false,
            balls: request.promoDetails?.balls || false,
            winnerPrize: request.promoDetails?.winnerPrize || false
          }
        } : {})
      };

      // Filtrar campos que contengan valor 'undefined' para evitar que falle Firestore
      const sanitizedBookingData = Object.fromEntries(
        Object.entries(bookingData).filter(([_, v]) => v !== undefined)
      );

      // Usar transacción para asegurar que tanto la petición esté libre como el casillero de destino
      await runTransaction(db, async (transaction) => {
        // 1. Verificar estado de la solicitud
        const reqSnap = await transaction.get(reqRef);
        if (!reqSnap.exists()) {
          throw new Error("REQUEST_NOT_FOUND");
        }
        if (reqSnap.data()?.status !== 'pending') {
          throw new Error("ALREADY_RESOLVED");
        }

        // 2. Verificar estado del casillero (reserva)
        const bookingSnap = await transaction.get(bookingDocRef);
        if (bookingSnap.exists()) {
          const existingData = bookingSnap.data();
          if ((existingData.excludedDates && existingData.excludedDates.includes(request.date)) || existingData.status === 'cancelled' || existingData.hiddenFromGrid) {
            bookingId = `${bookingId}_request_${Date.now()}`;
            bookingDocRef = doc(db, getCollectionPath('bookings'), bookingId);
            sanitizedBookingData.id = bookingId;
          } else {
            throw new Error("COLLISION");
          }
        }

        // 3. Eliminar la promo si existe para esta sede/cancha/fecha/horario
        const promoBookingId = `${request.clubId}_promo_${chosenOption.courtName.replace(/\s+/g, '')}_${request.date}_${chosenOption.timeSlot.replace(':', '')}`;
        const promoBookingDocRef = doc(db, getCollectionPath('bookings'), promoBookingId);
        transaction.delete(promoBookingDocRef);

        // Crear la reserva confirmada
        transaction.set(bookingDocRef, sanitizedBookingData, { merge: true });

        // Marcar la petición actual como aprobada
        transaction.update(reqRef, {
          status: 'accepted',
          adminNote: adminNote || ''
        });
      });

      // Registro de Actividad
      const currentUserDisplayName = activeUserName;
      const currentUserRole = userRole || 'admin';
      await logActivity(
        db,
        getCollectionPath,
        userId,
        currentUserDisplayName,
        currentUserRole,
        `Aprobación solicitud de turno - Cancha: ${chosenOption.courtName}, Fecha: ${request.date}, Horario: ${chosenOption.timeSlot}hs, Titular: ${request.playerName}`
      );

      // Enviar notificación push de aceptación al jugador
      await addDoc(collection(db, getCollectionPath('notifications')), {
        userId: request.playerId,
        title: clubName,
        body: adminNote
          ? `¡Tu solicitud de turno para el ${new Date(request.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} ${request.date.split('-').reverse().join('/')} a las ${chosenOption.timeSlot} hs en ${request.clubName || clubName} (${chosenOption.courtName}) fue aprobada! Nota: "${adminNote}"`
          : `¡Tu solicitud de turno para el ${new Date(request.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} ${request.date.split('-').reverse().join('/')} a las ${chosenOption.timeSlot} hs en ${request.clubName || clubName} (${chosenOption.courtName}) ha sido aprobada con éxito!`,
        type: 'booking_accepted',
        createdAt: Date.now(),
        url: '/?tab=reservas&subtab=mis-turnos'
      });

      // Auto-rechazar en cascada otras solicitudes solapadas en esta celda
      await autoReconcileOverlappingRequests(request.date, chosenOption.courtName, chosenOption.timeSlot, request.id);

    } catch (err: any) {
      if (err.message === "COLLISION") {
        setErrorModalMessage({
          title: "COLISIÓN DE SOLICITUD",
          message: "⚠️ No se pudo aprobar la solicitud. Este casillero de horario acaba de ser ocupado por otra reserva o solicitud aprobada previamente. La operación se canceló de forma segura."
        });
      } else if (err.message === "ALREADY_RESOLVED") {
        setErrorModalMessage({
          title: "SOLICITUD YA RESUELTA",
          message: "⚠️ Esta solicitud ya ha sido resuelta (aceptada o rechazada) por otro administrador en tiempo real. La operación fue cancelada."
        });
      } else if (err.message === "REQUEST_NOT_FOUND") {
        setErrorModalMessage({
          title: "SOLICITUD NO ENCONTRADA",
          message: "⚠️ La solicitud seleccionada ya no existe en el sistema."
        });
      } else {
        console.error("Error al procesar la petición", err);
        setErrorModalMessage({
          title: "ERROR AL APROBAR",
          message: "Ocurrió un error al procesar la aprobación: " + err.message
        });
      }
    }
  };

  // Modificar días
  const changeDate = (days: number) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const newDateStr = `${yyyy}-${mm}-${dd}`;
    
    // Evitar que naveguen por fuera de la ventana optimizada (Fase A)
    if (newDateStr < minDateLimitStr || newDateStr > maxDateLimitStr) {
      alert(`Para mantener la app rápida, solo puedes navegar entre el ${minDateLimitStr.split('-').reverse().join('/')} y el ${maxDateLimitStr.split('-').reverse().join('/')}`);
      return;
    }
    
    setSelectedDate(newDateStr);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px]">
        <div className="w-12 h-12 rounded-full border-t-2 border-r-2 theme-primary-border animate-spin mb-4" />
        <p className="text-white/60 font-medium">Cargando grilla de reservas...</p>
      </div>
    );
  }

  // --- CARPETA DE SETUP SI NO TIENE CONFIGURACIÓN ---
  const isStaffRole = userRole && ['co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(userRole);
  if (onlySetup || (!schedule && !isStaffRole) || showSetup) {
    return (
      <div className="w-full max-w-5xl mx-auto py-4 px-4 animate-in fade-in zoom-in-95 duration-500">
        <div
          className="bg-[#242731] border border-white/15 rounded-3xl p-5 md:p-8 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] flex flex-col md:flex-row gap-6 md:gap-8"
        >
          {/* MENU LATERAL / TABS */}
          <div className="w-full md:w-64 shrink-0 flex flex-col gap-4">
            <div className="hidden md:block space-y-1">
              <h3 className="text-xl font-black uppercase tracking-wider text-white flex items-center gap-2">
                <Sliders className="text-theme-primary" size={20} />
                Ajustes
              </h3>
            </div>

            {/* Píldoras en Grid para MÓVIL (Para que siempre estén a la vista) */}
            <div className="md:hidden grid grid-cols-4 gap-1.5 pb-2">
              {[
                { id: 'general', label: 'General', icon: Sliders },
                { id: 'dias_horarios', label: 'Horarios', icon: Calendar },
                { id: 'descuentos', label: 'Promos', icon: DollarSign },
                { id: 'pagos', label: 'Pagos', icon: CreditCard },
                { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSetupActiveTab(tab.id as any)}
                  className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-wider transition-all text-center border ${setupActiveTab === tab.id
                      ? 'theme-primary-bg text-black border-theme-primary shadow-md'
                      : 'bg-black/40 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <tab.icon size={14} className={setupActiveTab === tab.id ? "text-black" : "text-white/50"} />
                  <span className="leading-none mt-0.5">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tarjetas verticales para ESCRITORIO */}
            <div className="hidden md:flex flex-col gap-2">
              {[
                { id: 'general', label: 'Reglas Generales', desc: 'Gestión de turnos y canchas', icon: Sliders },
                { id: 'dias_horarios', label: 'Horarios y Fechas', desc: 'Apertura, días especiales y cierres', icon: Calendar },
                { id: 'descuentos', label: 'Promos y Descuentos', desc: 'Happy hours y descuentos fijos', icon: DollarSign },
                { id: 'pagos', label: 'Cobros y Señas', desc: 'Señas, pagos completos y Mercado Pago', icon: CreditCard },
                { id: 'whatsapp', label: 'WhatsApp', desc: 'Notificaciones por Whatsapp', icon: MessageCircle }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSetupActiveTab(tab.id as any)}
                  className={`p-4 text-left rounded-2xl transition-all flex items-start gap-3 border ${setupActiveTab === tab.id
                      ? 'bg-theme-primary/10 border-theme-primary/30'
                      : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                    }`}
                >
                  <div className={`mt-0.5 p-1.5 rounded-lg ${setupActiveTab === tab.id ? 'theme-primary-bg text-black' : 'bg-black/40 text-white/50'}`}>
                    <tab.icon size={14} />
                  </div>
                  <div>
                    <span className={`text-xs font-black uppercase tracking-wider block ${setupActiveTab === tab.id ? 'text-theme-primary' : 'text-white/80'}`}>
                      {tab.label}
                    </span>
                    <p className={`text-[10px] mt-1 normal-case font-normal leading-tight ${setupActiveTab === tab.id ? 'text-white/80' : 'text-white/40'}`}>
                      {tab.desc}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Indicador Auto-Guardado (Desktop) */}
            <div className="hidden md:block mt-auto pt-8 space-y-3">
              {setupSuccess && (
                <div className="text-emerald-400 text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 px-3 py-2.5 rounded-xl border border-emerald-500/20 text-center flex items-center justify-center gap-1.5">
                  <Check size={11} /> Guardado automáticamente
                </div>
              )}
              {schedule && !onlySetup && (
                <button
                  type="button"
                  onClick={() => setShowSetup(false)}
                  className="w-full bg-white/5 border border-white/10 text-white/70 font-bold uppercase py-3 rounded-xl hover:bg-white/10 hover:text-white transition-all text-xs tracking-widest flex items-center justify-center gap-2"
                >
                  <X size={14} /> Volver a la grilla
                </button>
              )}
            </div>
          </div>

          {/* CONTENIDO ACTIVO */}
          <div className="flex-1 bg-black/20 border border-white/5 md:border-white/10 rounded-2xl p-4 md:p-6 lg:p-8 min-w-0">


            {setupActiveTab === 'dias_horarios' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                {/* SECCIÓN: HORARIOS DE ATENCIÓN */}
                <div className="bg-black/10 border border-white/5 rounded-2xl p-5 space-y-4">
                  <div className="text-center border-b border-white/5 pb-2.5">
                    <span className="text-[11px] font-black uppercase text-theme-primary tracking-widest">
                      Horario de Atención General
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 flex flex-col items-center">
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/50 text-center">
                        Hora Apertura
                      </label>
                      <div className="relative w-full max-w-[145px]">
                        <select
                          value={setupOpenTime}
                          onChange={(e) => setSetupOpenTime(e.target.value)}
                          className="w-full bg-[#1a1c23] border border-white/10 rounded-xl pl-4 pr-10 py-3 text-white text-center font-bold focus:outline-none focus:border-theme-primary/40 text-sm appearance-none transition-all cursor-pointer shadow-inner"
                        >
                          {TIME_OPTIONS.map(time => (
                            <option key={time} value={time} className="bg-[#242731] text-white font-bold">{time}</option>
                          ))}
                        </select>
                        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                          <ChevronDown size={14} />
                        </div>
                      </div>
                      <p className="text-[10px] text-white/40 text-center leading-normal max-w-[125px]">
                        Inicio del primer turno
                      </p>
                    </div>
                    <div className="space-y-2 flex flex-col items-center">
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/50 text-center">
                        Hora Cierre
                      </label>
                      <div className="relative w-full max-w-[145px]">
                        <select
                          value={setupCloseTime}
                          onChange={(e) => setSetupCloseTime(e.target.value)}
                          className="w-full bg-[#1a1c23] border border-white/10 rounded-xl pl-4 pr-10 py-3 text-white text-center font-bold focus:outline-none focus:border-theme-primary/40 text-sm appearance-none transition-all cursor-pointer shadow-inner"
                        >
                          {TIME_OPTIONS.map(time => (
                            <option key={time} value={time} className="bg-[#242731] text-white font-bold">{time}</option>
                          ))}
                        </select>
                        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                          <ChevronDown size={14} />
                        </div>
                      </div>
                      <p className="text-[10px] text-white/40 text-center leading-normal max-w-[125px]">
                        finalización del ultimo turno
                      </p>
                    </div>
                  </div>
                </div>

                {/* 0. SECCIÓN CIERRE DE CAJA */}
                <div className="bg-black/10 border border-white/5 rounded-2xl p-5 text-left">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex-1 space-y-1">
                      <label className="text-sm font-bold text-white uppercase tracking-wider block">Hora de Cierre Real de Caja</label>
                      <p className="text-[11px] text-white/50 leading-relaxed max-w-sm">
                        Si tu club opera de madrugada (ej. hasta las 02:00 AM), establece esa hora aquí. Los cobros realizados antes de esta hora se contabilizarán en el balance del "día anterior".
                      </p>
                    </div>
                    <div className="relative w-full md:w-32">
                      <select
                        value={setupCashRegisterCloseTime}
                        onChange={(e) => setSetupCashRegisterCloseTime(e.target.value)}
                        className="w-full bg-[#1a1c23] border border-white/10 rounded-xl pl-4 pr-10 py-3 text-white text-center font-bold focus:outline-none focus:border-theme-primary/40 text-sm appearance-none transition-all cursor-pointer shadow-inner"
                      >
                        <option value="00:00" className="bg-[#242731]">00:00</option>
                        <option value="01:00" className="bg-[#242731]">01:00</option>
                        <option value="02:00" className="bg-[#242731]">02:00</option>
                        <option value="03:00" className="bg-[#242731]">03:00</option>
                        <option value="03:30" className="bg-[#242731]">03:30</option>
                        <option value="04:00" className="bg-[#242731]">04:00</option>
                        <option value="05:00" className="bg-[#242731]">05:00</option>
                        <option value="06:00" className="bg-[#242731]">06:00</option>
                      </select>
                      <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {setupActiveTab === 'general' && (
              <div className="space-y-4 animate-in fade-in duration-300">
                {/* SECCIÓN: PARÁMETROS DE TURNOS */}
                <div className="bg-black/10 border border-white/5 rounded-2xl p-4 space-y-3">
                  <div className="text-center border-b border-white/5 pb-2">
                    <span className="text-sm font-black uppercase text-theme-primary tracking-widest">
                      Configuración del Turno
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 flex flex-col items-center">
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/50 text-center">
                        Precio por Turno
                      </label>
                      <div className="relative w-full max-w-[145px]">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs font-bold">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={setupPrice ? new Intl.NumberFormat('es-AR').format(setupPrice) : ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setSetupPrice(val ? Number(val) : 0);
                          }}
                          className="w-full bg-[#1a1c23] border border-white/10 rounded-xl pl-8 pr-4 py-3 text-white text-center font-bold focus:outline-none focus:border-theme-primary/40 text-sm transition-all shadow-inner"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 flex flex-col items-center">
                      <label className="block text-xs font-bold uppercase tracking-wider text-white/50 text-center">
                        Duración Turno
                      </label>
                      <div className="relative w-full max-w-[145px]">
                        <select
                          value={setupSlotDuration}
                          onChange={(e) => setSetupSlotDuration(Number(e.target.value))}
                          className="w-full bg-[#1a1c23] border border-white/10 rounded-xl pl-4 pr-10 py-3 text-white text-center font-bold focus:outline-none focus:border-theme-primary/40 text-sm appearance-none transition-all cursor-pointer shadow-inner"
                        >
                          <option value={60} className="bg-[#242731] text-white font-bold">60 mins</option>
                          <option value={90} className="bg-[#242731] text-white font-bold">90 mins</option>
                          <option value={120} className="bg-[#242731] text-white font-bold">120 mins</option>
                        </select>
                        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                          <ChevronDown size={14} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {setupActiveTab === 'dias_horarios' && (
              <div className="space-y-6 animate-in fade-in duration-300 mt-6 md:mt-0">
                {/* SECCIÓN NUEVA: HORARIOS POR DÍA */}
                <div className="space-y-4 border-t border-white/5 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-black uppercase tracking-wider text-theme-primary text-left">
                        Configurar Horarios específicos<br />de la semana
                      </h4>
                      <p className="text-[11px] text-white/40 text-left">
                        Configura rangos de horas o días cerrados en la semana (Lunes a Domingo).
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (showAddDayScheduleForm) {
                          handleCancelDaySchedule();
                        } else {
                          setShowAddDayScheduleForm(true);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 text-[11px] font-bold uppercase transition-all flex items-center gap-1"
                    >
                      {showAddDayScheduleForm ? 'Cancelar' : '+ Agregar horario por día'}
                    </button>
                  </div>

                  {showAddDayScheduleForm && (
                    <div className="bg-black/20 border border-white/10 rounded-2xl p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between border-b border-white/5 pb-2">
                        <span className="text-xs font-black uppercase text-theme-primary tracking-wider">
                          {editingDayScheduleDay !== null ? 'Editar Horario por Día' : 'Agregar Horario por Día'}
                        </span>
                        {editingDayScheduleDay !== null && (
                          <button
                            type="button"
                            onClick={handleCancelDaySchedule}
                            className="text-[10px] font-bold uppercase text-red-400 hover:text-red-300 transition-colors"
                          >
                            Cancelar Edición
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">Día de la semana</label>
                          <select
                            value={newDayScheduleDay}
                            onChange={(e) => setNewDayScheduleDay(Number(e.target.value))}
                            disabled={editingDayScheduleDay !== null}
                            className="w-full bg-[#1b1e26] border border-white/10 rounded-xl px-3 py-2 text-white text-xs focus:outline-none focus:border-theme-primary/40 transition-all font-bold disabled:opacity-50"
                          >
                            {DAYS_ES.map((day, idx) => (
                              <option key={day} value={idx} className="bg-[#1b1e26] text-white">{day}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1 flex flex-col justify-end">
                          <div className="flex items-center justify-between bg-white/5 px-3 py-2 rounded-xl border border-white/5 h-[38px]">
                            <span className="text-[10px] font-bold text-white/70 uppercase">¿Cerrado este día?</span>
                            <button
                              type="button"
                              onClick={() => setNewDayScheduleClosed(!newDayScheduleClosed)}
                              className={`w-8 h-5 rounded-full p-0.5 transition-all duration-300 ${newDayScheduleClosed ? 'bg-red-500' : 'bg-white/10'}`}
                            >
                              <div className={`w-4 h-4 rounded-full bg-black transition-transform duration-300 ${newDayScheduleClosed ? 'translate-x-3' : 'translate-x-0'}`} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {!newDayScheduleClosed && (
                        <div className="grid grid-cols-2 gap-4 bg-black/10 p-3 rounded-xl border border-white/5 animate-in fade-in duration-200">
                          <div className="space-y-1 flex flex-col items-center">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Hora Apertura</label>
                            <div className="relative w-full">
                              <select
                                value={newDayScheduleOpen}
                                onChange={(e) => setNewDayScheduleOpen(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-7 py-1 text-white text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                              >
                                <option value="">--:--</option>
                                {TIME_OPTIONS.map(time => (
                                  <option key={time} value={time} className="bg-[#242731]">{time}</option>
                                ))}
                              </select>
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                                <ChevronDown size={12} />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1 flex flex-col items-center">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Hora Cierre</label>
                            <div className="relative w-full">
                              <select
                                value={newDayScheduleClose}
                                onChange={(e) => setNewDayScheduleClose(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-7 py-1 text-white text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                              >
                                <option value="">--:--</option>
                                {TIME_OPTIONS.map(time => (
                                  <option key={time} value={time} className="bg-[#242731]">{time}</option>
                                ))}
                              </select>
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                                <ChevronDown size={12} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleAddDaySchedule}
                        className="w-full theme-primary-bg text-black font-black uppercase py-2.5 rounded-xl text-[10px] tracking-wider hover:scale-[1.01] active:scale-95 transition-all"
                      >
                        {editingDayScheduleDay !== null ? 'Guardar Cambios' : 'Guardar Horario Especial'}
                      </button>
                    </div>
                  )}

                  {setupSpecificDaySchedules.length === 0 ? (
                    <p className="text-[11px] text-white/30 italic text-center py-2.5 bg-white/5 rounded-xl border border-white/5">
                      Aun no hay horarios específicos configurados.<br />Se aplica el horario general de apertura y cierre todos los días de la semana.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                      {setupSpecificDaySchedules.map((ss) => (
                        <div
                          key={ss.dayOfWeek}
                          className={`flex items-center justify-between bg-black/20 border px-3 py-2.5 rounded-xl transition-all ${editingDayScheduleDay === ss.dayOfWeek ? 'border-theme-primary bg-theme-primary/5' : 'border-white/10 hover:border-white/15'
                            }`}
                        >
                          <div className="space-y-0.5 text-left">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-black text-white">
                                {DAYS_ES[ss.dayOfWeek]}
                              </span>
                              {ss.closed ? (
                                <span className="text-[9px] font-black uppercase bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded text-red-400">
                                  Cerrado todo el día
                                </span>
                              ) : (
                                <span className="text-[9px] font-black uppercase bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-400">
                                  Especial
                                </span>
                              )}
                            </div>
                            {!ss.closed && (
                              <p className="text-[10px] font-semibold text-white/50">
                                Abierto de {ss.openTime} a {ss.closeTime} hs
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleStartEditDaySchedule(ss)}
                              className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-theme-primary hover:text-black transition-all scale-90"
                              title="Editar Horario"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveDaySchedule(ss.dayOfWeek)}
                              className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all scale-90"
                              title="Eliminar Horario"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* SECCIÓN NUEVA: DÍAS OFF (BLOQUEOS) */}
                <div className="space-y-4 border-t border-white/5 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-black uppercase tracking-wider text-theme-primary text-left">
                        Cierre programado / Mantenimiento
                      </h4>
                      <p className="text-[11px] text-white/40 text-left">
                        Bloquea fechas completas o rangos horarios especiales de forma única.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (showAddOffDayForm) {
                          handleCancelOffDay();
                        } else {
                          setShowAddOffDayForm(true);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 text-[11px] font-bold uppercase transition-all flex items-center gap-1"
                    >
                      {showAddOffDayForm ? 'Cancelar' : '+ Agregar cierre'}
                    </button>
                  </div>

                  {showAddOffDayForm && (
                    <div className="bg-black/20 border border-white/10 rounded-2xl p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between border-b border-white/5 pb-2">
                        <span className="text-xs font-black uppercase text-theme-primary tracking-wider">
                          {editingOffDayId ? 'Editar Cierre' : 'Agregar cierre'}
                        </span>
                        {editingOffDayId && (
                          <button
                            type="button"
                            onClick={handleCancelOffDay}
                            className="text-[10px] font-bold uppercase text-red-400 hover:text-red-300 transition-colors"
                          >
                            Cancelar Edición
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">Fecha</label>
                          <input
                            type="date"
                            min={todayStr}
                            value={newOffDayDate}
                            onChange={(e) => setNewOffDayDate(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-xs focus:outline-none focus:border-theme-primary/40 transition-all font-bold [color-scheme:dark]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">Razón / Motivo (Opcional)</label>
                          <input
                            type="text"
                            placeholder="Mantenimiento, Feriado..."
                            value={newOffDayReason}
                            onChange={(e) => setNewOffDayReason(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/20 text-xs focus:outline-none focus:border-theme-primary/40 transition-all font-bold"
                          />
                        </div>
                      </div>

                      <div className="space-y-2 bg-white/5 p-3 rounded-xl border border-white/5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-white/80">Canchas afectadas</span>
                          <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                            <button
                              type="button"
                              onClick={() => setNewOffDayCourts([])}
                              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${newOffDayCourts.length === 0
                                  ? 'bg-white text-black shadow-md'
                                  : 'text-white/60 hover:text-white'
                                }`}
                            >
                              Todas las canchas
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (setupCourts.length > 0) {
                                  setNewOffDayCourts([setupCourts[0]]);
                                }
                              }}
                              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${newOffDayCourts.length > 0
                                  ? 'bg-white text-black shadow-md'
                                  : 'text-white/60 hover:text-white'
                                }`}
                            >
                              Canchas Específicas
                            </button>
                          </div>
                        </div>

                        {newOffDayCourts.length > 0 && (
                          <div className="pt-2 border-t border-white/5 flex flex-wrap gap-2 animate-in fade-in duration-200">
                            {setupCourts.map(court => {
                              const isSelected = newOffDayCourts.includes(court);
                              return (
                                <button
                                  key={court}
                                  type="button"
                                  onClick={() => {
                                    const updated = newOffDayCourts.filter(c => c !== court);
                                    if (isSelected) {
                                      if (updated.length === 0) {
                                        setNewOffDayCourts([]);
                                      } else {
                                        setNewOffDayCourts(updated);
                                      }
                                    } else {
                                      setNewOffDayCourts([...newOffDayCourts, court]);
                                    }
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all flex items-center gap-1.5 border ${isSelected
                                      ? 'bg-theme-primary/10 border-theme-primary/40 text-theme-primary'
                                      : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:text-white'
                                    }`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'theme-primary-bg animate-pulse' : 'bg-white/20'}`} />
                                  {court}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/40 block">Tipo de cierre</span>
                        <div className="flex bg-black/30 p-1 rounded-xl border border-white/10 gap-1">
                          <button
                            type="button"
                            onClick={() => setNewOffDayAllDay(true)}
                            className={`flex-1 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${newOffDayAllDay
                                ? 'bg-white text-black shadow-md'
                                : 'text-white/60 hover:text-white hover:bg-white/5'
                              }`}
                          >
                            Cierre de Jornada Completa
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewOffDayAllDay(false)}
                            className={`flex-1 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${!newOffDayAllDay
                                ? 'bg-white text-black shadow-md'
                                : 'text-white/60 hover:text-white hover:bg-white/5'
                              }`}
                          >
                            Cierre parcial (rango horario)
                          </button>
                        </div>
                      </div>

                      {!newOffDayAllDay && (
                        <div className="grid grid-cols-2 gap-4 bg-black/10 p-3 rounded-xl border border-white/5 animate-in fade-in duration-200">
                          <div className="space-y-1 flex flex-col items-center">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Hora Inicio</label>
                            <div className="relative w-full">
                              <select
                                value={newOffDayStart}
                                onChange={(e) => setNewOffDayStart(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-7 py-1 text-white text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                              >
                                <option value="">--:--</option>
                                {TIME_OPTIONS.map(time => (
                                  <option key={time} value={time} className="bg-[#242731]">{time}</option>
                                ))}
                              </select>
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                                <ChevronDown size={12} />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1 flex flex-col items-center">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Hora Fin</label>
                            <div className="relative w-full">
                              <select
                                value={newOffDayEnd}
                                onChange={(e) => setNewOffDayEnd(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-7 py-1 text-white text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                              >
                                <option value="">--:--</option>
                                {TIME_OPTIONS.map(time => (
                                  <option key={time} value={time} className="bg-[#242731]">{time}</option>
                                ))}
                              </select>
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                                <ChevronDown size={12} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleAddOffDay}
                        className="w-full theme-primary-bg text-black font-black uppercase py-2.5 rounded-xl text-[10px] tracking-wider hover:scale-[1.01] active:scale-95 transition-all"
                      >
                        {editingOffDayId ? 'Guardar Cambios' : 'Confirmar y Bloquear Día Off'}
                      </button>
                    </div>
                  )}

                  {setupOffDays.length === 0 ? (
                    <p className="text-[11px] text-white/30 italic text-center py-2.5 bg-white/5 rounded-xl border border-white/5">
                      No hay días off registrados. Tu agenda funcionará normalmente todos los días.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                      {setupOffDays.map((od) => (
                        <div
                          key={od.id}
                          className={`flex items-center justify-between bg-black/20 border px-3 py-2.5 rounded-xl transition-all ${editingOffDayId === od.id ? 'border-theme-primary bg-theme-primary/5' : 'border-white/10 hover:border-white/15'
                            }`}
                        >
                          <div className="space-y-0.5 text-left">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-black text-white">
                                {od.date ? od.date.split('-').reverse().join('/') : ''}
                              </span>
                              {od.reason && (
                                <span className="text-[9px] font-black uppercase bg-theme-primary/10 border border-theme-primary/20 px-1.5 py-0.5 rounded text-theme-primary">
                                  {od.reason}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] font-semibold text-white/50">
                              {od.allDay ? 'Cerrado todo el día' : `Bloqueado de ${od.startHour} a ${od.endHour} hs`}
                              {od.courts && od.courts.length > 0 ? (
                                <span className="block text-[9px] font-bold text-amber-400 mt-0.5 uppercase tracking-wider">
                                  {od.courts.join(', ')}
                                </span>
                              ) : (
                                <span className="block text-[9px] font-bold text-emerald-400 mt-0.5 uppercase tracking-wider">
                                  Todas las canchas
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleStartEditOffDay(od)}
                              className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-theme-primary hover:text-black transition-all scale-90"
                              title="Editar Día Off"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveOffDay(od.id)}
                              className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all scale-90"
                              title="Eliminar Día Off"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {setupActiveTab === 'descuentos' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="bg-yellow-400/5 border border-yellow-400/20 rounded-lg px-3 py-2 flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-md bg-yellow-400/15 flex items-center justify-center shrink-0">
                    <Lightbulb size={13} className="text-yellow-400" />
                  </div>
                  <p className="flex-1 text-[10px] font-medium text-white/70 leading-snug text-center text-balance">
                    <span className="font-bold text-yellow-400">Tip:</span> Puedes configurar promos por turno haciendo click directamente en un casillero de la grilla.
                  </p>
                  <div className="w-6 shrink-0" />
                </div>

                {/* SECCIÓN NUEVA: HORARIOS CON DESCUENTO */}
                <div className="space-y-4 border-t border-white/5 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-black uppercase tracking-wider text-theme-primary text-left">
                        Horarios con Descuento
                      </h4>
                      <p className="text-[11px] text-white/40 text-left">
                        Configura descuentos automáticos aplicables a ciertos días, horas o canchas específicas.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (showAddDiscountForm) {
                          handleCancelDiscount();
                        } else {
                          setShowAddDiscountForm(true);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 text-[11px] font-bold uppercase transition-all flex items-center gap-1"
                    >
                      {showAddDiscountForm ? 'Cancelar' : '+ Agregar descuento'}
                    </button>
                  </div>

                  {showAddDiscountForm && (
                    <div className="bg-black/20 border border-white/10 rounded-2xl p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between border-b border-white/5 pb-2">
                        <span className="text-xs font-black uppercase text-theme-primary tracking-wider">
                          {editingDiscountId ? 'Editar Horario con Descuento' : 'Agregar Horario con Descuento'}
                        </span>
                        {editingDiscountId && (
                          <button
                            type="button"
                            onClick={handleCancelDiscount}
                            className="text-[10px] font-bold uppercase text-red-400 hover:text-red-300 transition-colors"
                          >
                            Cancelar Edición
                          </button>
                        )}
                      </div>

                      {/* Días de la semana selectores */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">Días Aplicables</label>
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              if (newDiscountDays.length === 7) {
                                setNewDiscountDays([]);
                              } else {
                                setNewDiscountDays([0, 1, 2, 3, 4, 5, 6]);
                              }
                            }}
                            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all ${newDiscountDays.length === 7
                              ? 'theme-primary-bg text-black scale-105 shadow-[0_2px_8px_rgba(var(--glow-rgb),0.2)]'
                              : 'bg-white/5 border border-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                              }`}
                          >
                            Todos
                          </button>
                          {DAYS_ES.map((day, idx) => {
                            const isSelected = newDiscountDays.includes(idx);
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setNewDiscountDays(newDiscountDays.filter(d => d !== idx));
                                  } else {
                                    setNewDiscountDays([...newDiscountDays, idx]);
                                  }
                                }}
                                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all ${isSelected
                                  ? 'theme-primary-bg text-black scale-105 shadow-[0_2px_8px_rgba(var(--glow-rgb),0.2)]'
                                  : 'bg-white/5 border border-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                                  }`}
                              >
                                {day.substring(0, 3)}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Canchas aplicables selector */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">Canchas Aplicables</label>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setNewDiscountCourts([])}
                            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all ${newDiscountCourts.length === 0
                              ? 'theme-primary-bg text-black'
                              : 'bg-white/5 border border-white/5 text-white/70 hover:bg-white/10'
                              }`}
                          >
                            Todas las canchas
                          </button>
                          {setupCourts.map((courtName) => {
                            const isSelected = newDiscountCourts.includes(courtName);
                            return (
                              <button
                                key={courtName}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setNewDiscountCourts(newDiscountCourts.filter(c => c !== courtName));
                                  } else {
                                    setNewDiscountCourts([...newDiscountCourts, courtName]);
                                  }
                                }}
                                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all ${isSelected
                                  ? 'theme-primary-bg text-black'
                                  : 'bg-white/5 border border-white/5 text-white/70 hover:bg-white/10'
                                  }`}
                              >
                                {courtName}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Rango Horario */}
                      <div className="grid grid-cols-2 gap-4 bg-black/10 p-3 rounded-xl border border-white/5">
                        <div className="space-y-1 flex flex-col items-center">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Hora Inicio</label>
                          <div className="relative w-full">
                            <select
                              value={newDiscountStart}
                              onChange={(e) => setNewDiscountStart(e.target.value)}
                              className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-7 py-1 text-white text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                            >
                              <option value="">--:--</option>
                              {TIME_OPTIONS.map(time => (
                                <option key={time} value={time} className="bg-[#242731]">{time}</option>
                              ))}
                            </select>
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                              <ChevronDown size={12} />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1 flex flex-col items-center">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Hora Fin</label>
                          <div className="relative w-full">
                            <select
                              value={newDiscountEnd}
                              onChange={(e) => setNewDiscountEnd(e.target.value)}
                              className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-7 py-1 text-white text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                            >
                              <option value="">--:--</option>
                              {TIME_OPTIONS.map(time => (
                                <option key={time} value={time} className="bg-[#242731]">{time}</option>
                              ))}
                            </select>
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                              <ChevronDown size={12} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Tipo de Descuento y Valor */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">Tipo Descuento</label>
                          <div className="flex bg-white/5 rounded-xl p-0.5 border border-white/10">
                            <button
                              type="button"
                              onClick={() => setNewDiscountType('percentage')}
                              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all ${newDiscountType === 'percentage' ? 'bg-white text-black' : 'text-white/70 hover:text-white'
                                }`}
                            >
                              Porcentaje (%)
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewDiscountType('fixed')}
                              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all ${newDiscountType === 'fixed' ? 'bg-white text-black' : 'text-white/70 hover:text-white'
                                }`}
                            >
                              Monto Fijo ($)
                            </button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 block text-left">
                            {newDiscountType === 'percentage' ? 'Valor Descuento (%)' : 'Monto Descuento ($)'}
                          </label>
                          <div className="relative">
                            {newDiscountType === 'fixed' && (
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-xs font-bold">$</span>
                            )}
                            <input
                              type="number"
                              placeholder="Ej: 15"
                              value={newDiscountValue || ''}
                              onChange={(e) => setNewDiscountValue(Number(e.target.value))}
                              className={`w-full bg-[#1b1e26] border border-white/10 rounded-xl py-2 text-white text-xs font-bold focus:outline-none focus:border-theme-primary/40 transition-all ${newDiscountType === 'fixed' ? 'pl-7 pr-3' : 'px-3'
                                }`}
                            />
                            {newDiscountType === 'percentage' && (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs font-bold">%</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleAddDiscount}
                        className="w-full theme-primary-bg text-black font-black uppercase py-2.5 rounded-xl text-[10px] tracking-wider hover:scale-[1.01] active:scale-95 transition-all"
                      >
                        {editingDiscountId ? 'Guardar Cambios' : 'Confirmar y Agregar Descuento'}
                      </button>
                    </div>
                  )}

                  {setupTimeDiscounts.length === 0 ? (
                    <p className="text-[11px] text-white/30 italic text-center py-2.5 bg-white/5 rounded-xl border border-white/5">
                      No hay horarios con descuento configurados.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                      {setupTimeDiscounts.map((td) => (
                        <div
                          key={td.id}
                          className={`flex items-center justify-between bg-black/20 border px-3 py-2.5 rounded-xl transition-all ${editingDiscountId === td.id ? 'border-theme-primary bg-theme-primary/5' : 'border-white/10 hover:border-white/15'
                            }`}
                        >
                          <div className="space-y-0.5 text-left">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-black text-theme-primary">
                                {td.discountType === 'percentage' ? `${td.value}% OFF` : `$${td.value} OFF`}
                              </span>
                              <span className="text-[10px] font-bold text-white">
                                {td.startHour} a {td.endHour} hs
                              </span>
                            </div>
                            <p className="text-[10px] font-semibold text-white/50 leading-normal">
                              Días: {td.daysOfWeek.map(d => DAYS_ES[d].substring(0, 3)).join(', ')}
                              <br />
                              Canchas: {td.courts && td.courts.length > 0 ? td.courts.join(', ') : 'Todas'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleStartEditDiscount(td)}
                              className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-theme-primary hover:text-black transition-all scale-90"
                              title="Editar Descuento"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveDiscount(td.id)}
                              className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all scale-90"
                              title="Eliminar Descuento"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {setupActiveTab === 'general' && (
              <div className="space-y-4 animate-in fade-in duration-300 mt-4">
                {/* Canchas Disponibles */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 border-t border-white/5 pt-4">
                  <div className="space-y-0.5 text-center sm:text-left">
                    <h4 className="text-sm font-black uppercase tracking-wider text-theme-primary">
                      Canchas Disponibles
                    </h4>
                    <p className="text-[11px] text-white/40">
                      Seleccioná cuántas canchas tiene tu sede.
                    </p>
                  </div>

                  <div className="bg-black/20 border border-white/10 rounded-2xl p-3 w-[160px] shrink-0">
                    <div className="relative w-full">
                      <select
                        value={setupCourts.length}
                        onChange={(e) => setSetupCourts(Array.from({ length: Number(e.target.value) }, (_, i) => `Cancha ${i + 1}`))}
                        className="w-full bg-[#1b1e26] border border-white/10 rounded-xl px-4 py-2.5 text-white text-center font-bold focus:outline-none focus:border-theme-primary/40 text-sm appearance-none transition-all cursor-pointer shadow-inner"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                          <option key={n} value={n} className="bg-[#242731]">
                            {n} {n === 1 ? 'Cancha' : 'Canchas'}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                        <ChevronDown size={16} />
                      </div>
                    </div>

                    {setupCourts.length === 0 && (
                      <p className="text-xs text-red-400 font-bold uppercase tracking-wider mt-3">Debes seleccionar al menos 1 cancha</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {setupActiveTab === 'pagos' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                
                
                
                {/* 1. SECCIÓN MERCADO PAGO */}
                <div className="bg-black/10 border border-white/5 rounded-2xl p-5 space-y-4 text-left">
                  <div className="border-b border-white/5 pb-3">
                    <span className="text-sm font-black uppercase text-theme-primary tracking-widest flex items-center justify-center gap-2">
                      <CreditCard size={18} />
                      Vincular cuenta de Mercado Pago
                    </span>
                  </div>

                  <p className="text-xs text-white/60 leading-relaxed">
                    Para que los jugadores puedan abonar señas o el total de los turnos desde la aplicación, debes vincular la cuenta de Mercado Pago del club.
                  </p>

                  {privateConfig?.mpAccessToken ? (
                    <div className="bg-[#009EE3]/10 border border-[#009EE3]/20 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(0,158,227,0.15)] overflow-hidden border border-[#009EE3]/10">
                          <img src="/mp-logo.png" alt="Mercado Pago" className="w-[26px] h-[26px] object-contain" />
                        </div>
                        <div>
                          <span className="text-xs font-black uppercase tracking-wider text-[#009EE3] block">Mercado Pago Vinculado</span>
                          <p className="text-[11px] text-zinc-400 mt-0.5">
                            Cuenta activa: <b className="text-white">{privateConfig.linkedEmail || 'finanzas@club.com'}</b>
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleUnlinkMP}
                        className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer hover:scale-[1.02] active:scale-95 shrink-0"
                      >
                        Desvincular
                      </button>
                    </div>
                  ) : (
                    <div className="bg-zinc-800/40 border border-zinc-700/50 rounded-xl p-4 flex flex-col items-center text-center space-y-4 animate-in fade-in duration-200">
                      <div className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700/50 flex items-center justify-center text-zinc-400">
                        <CreditCard size={22} />
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-black uppercase tracking-wider text-white">Mercado Pago no vinculado</span>
                        <p className="text-[11px] text-white/40 max-w-sm">
                          Vincula tu cuenta para activar las reservas online con cobro de señas automáticas.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleMockLinkMP}
                        className="px-6 py-3 bg-[#009EE3] hover:bg-[#0087c2] text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all cursor-pointer hover:scale-[1.03] active:scale-95 flex items-center gap-2 shadow-lg shadow-sky-500/10 border border-sky-400/20"
                      >
                        <div className="w-[22px] h-[22px] bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm overflow-hidden">
                          <img src="/mp-logo.png" alt="Mercado Pago" className="w-[16px] h-[16px] object-contain" />
                        </div>
                        Vincular Mercado Pago
                      </button>
                    </div>
                  )}
                </div>

                {/* 2. SECCIÓN POLÍTICAS DE COBRO */}
                <div className="bg-black/10 border border-white/5 rounded-2xl p-5 space-y-5 text-left">
                  <div className="border-b border-white/5 pb-3">
                    <span className="text-sm font-black uppercase text-theme-primary tracking-widest flex items-center justify-center gap-2">
                      <Sliders size={18} />
                      Reglas de Cobro de Seña
                    </span>
                  </div>

                  <p className="text-xs text-white/60 leading-relaxed">
                    Personaliza en qué días y horarios se exige una seña para confirmar un turno. Si un turno no entra en ninguna regla, se aplicará el porcentaje "Por defecto".
                  </p>

                  {/* Configuración Por Defecto */}
                  <div className="bg-black/20 border border-white/5 rounded-xl p-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-white/60 block mb-2">Por defecto <span className="text-[10px] normal-case tracking-normal opacity-70">(Si no aplica ninguna regla)</span></label>
                    <div className="flex items-center gap-3">
                      {[0, 25, 50, 100].map(perc => (
                        <label key={'default_'+perc} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="defaultPercentage"
                            value={perc}
                            checked={setupPaymentDefault === perc}
                            onChange={() => setSetupPaymentDefault(perc as 0 | 25 | 50 | 100)}
                            className="accent-[#D4ED1A] w-4 h-4"
                          />
                          <span className="text-sm font-black text-white/90">{perc}%</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Lista de Reglas */}
                  <div className="space-y-3">
                    <span className="text-xs font-bold uppercase tracking-widest text-white/60 block">Reglas Específicas</span>
                    
                    {setupPaymentRules.length === 0 ? (
                      <p className="text-[11px] text-white/30 italic text-center py-2.5 bg-white/5 rounded-xl border border-white/5">
                        No hay reglas específicas configuradas.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                        {setupPaymentRules.map((rule) => (
                          <div
                            key={rule.id}
                            className="bg-black/20 border border-white/5 rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                          >
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-black text-theme-primary">{rule.depositPercentage}%</span>
                                <span className="text-xs text-white/60">de seña</span>
                              </div>
                              <div className="text-[10px] text-white/40 font-bold uppercase tracking-wider flex items-center gap-1.5 flex-wrap">
                                <span className="bg-white/5 px-1.5 py-0.5 rounded">{rule.startHour} a {rule.endHour}</span>
                                <span>-</span>
                                <span>
                                  {rule.daysOfWeek.map(d => ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'][d]).join(', ')}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => handleStartEditPaymentRule(rule)}
                                className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                                title="Editar regla"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => handleRemovePaymentRule(rule.id)}
                                className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                                title="Eliminar regla"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Formulario de Nueva Regla */}
                  <div className="bg-[#1b1e26]/40 border border-theme-primary/20 rounded-xl p-4 space-y-4">
                    <span className="text-xs font-black uppercase text-theme-primary tracking-widest block">
                      {editingPaymentRuleId ? 'Editar Regla' : 'Agregar Regla'}
                    </span>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Horario */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 space-y-1">
                          <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Desde</label>
                          <input
                            type="time"
                            value={newPaymentRuleStart}
                            onChange={(e) => setNewPaymentRuleStart(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Hasta</label>
                          <input
                            type="time"
                            value={newPaymentRuleEnd}
                            onChange={(e) => setNewPaymentRuleEnd(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                          />
                        </div>
                      </div>

                      {/* Porcentaje */}
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Seña Exigida</label>
                        <select
                          value={newPaymentRulePercentage}
                          onChange={(e) => setNewPaymentRulePercentage(Number(e.target.value) as 25 | 50 | 100)}
                          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-[30px]"
                        >
                          <option value={25}>25%</option>
                          <option value={50}>50%</option>
                          <option value={100}>100%</option>
                        </select>
                      </div>
                    </div>

                    {/* Días */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Días que aplica</label>
                      <div className="flex flex-wrap gap-1.5">
                        {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map((dayName, idx) => {
                          const isSelected = newPaymentRuleDays.includes(idx);
                          return (
                            <button
                              key={'rule_day_'+idx}
                              type="button"
                              onClick={() => {
                                if (isSelected) setNewPaymentRuleDays(prev => prev.filter(d => d !== idx));
                                else setNewPaymentRuleDays(prev => [...prev, idx].sort());
                              }}
                              className={`w-8 h-8 rounded-lg text-[10px] font-black uppercase transition-all ${isSelected ? 'bg-white text-black shadow-[0_0_10px_rgba(255,255,255,0.3)]' : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'}`}
                            >
                              {dayName}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <button
                      onClick={handleSavePaymentRule}
                      className="w-full bg-white/5 hover:bg-white/10 text-white text-xs font-black uppercase tracking-widest py-2 rounded-xl transition-colors border border-white/5"
                    >
                      {editingPaymentRuleId ? 'Guardar Cambios' : 'Agregar Regla'}
                    </button>
                    {editingPaymentRuleId && (
                      <button
                        onClick={() => {
                          setEditingPaymentRuleId(null);
                          setNewPaymentRuleStart('17:00');
                          setNewPaymentRuleEnd('22:00');
                          setNewPaymentRuleDays([1,2,3,4,5,6]);
                          setNewPaymentRulePercentage(50);
                        }}
                        className="w-full mt-2 bg-transparent text-white/40 hover:text-white text-[10px] font-bold uppercase tracking-widest py-1.5"
                      >
                        Cancelar Edición
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {setupActiveTab === 'whatsapp' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <AdminWhatsApp db={db} getCollectionPath={getCollectionPath} userId={userId} subscriptionPlan={subscriptionPlan} />
              </div>
            )}

            {/* Indicador Auto-Guardado (Mobile) */}
            <div className="md:hidden mt-6 space-y-3 pt-4 border-t border-white/5">
              {setupSuccess && (
                <div className="text-emerald-400 text-xs font-black uppercase tracking-wider bg-emerald-500/10 px-4 py-3 rounded-xl border border-emerald-500/20 text-center flex items-center justify-center gap-1.5 w-full">
                  <Check size={12} /> Guardado automáticamente
                </div>
              )}
              {schedule && !onlySetup && (
                <button
                  type="button"
                  onClick={() => setShowSetup(false)}
                  className="w-full bg-white/5 border border-white/10 text-white font-bold uppercase py-3 rounded-xl hover:bg-white/10 hover:text-white transition-all text-xs tracking-widest flex items-center justify-center gap-2"
                >
                  <X size={14} /> Volver a la grilla
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }


  // --- LOGICA CANTINA ---
  const handleAddToCart = (product: Product) => {
    setCantinaCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { id: product.id, name: product.name, price: product.price, quantity: 1, isPaid: false, imageUrl: product.imageUrl || '' }];
    });
  };

  const handleAddCantinaQuickItem = () => {
    if (!cantinaQuickItemName.trim() || cantinaQuickItemPrice === '' || cantinaQuickItemQuantity < 1) return;

    const newItem: BookingItem = {
      id: `quick_cantina_${Date.now()}`,
      name: cantinaQuickItemName.trim(),
      price: Number(cantinaQuickItemPrice),
      quantity: cantinaQuickItemQuantity,
      note: '',
      imageUrl: ''
    };

    setCantinaCart(prev => [...prev, newItem]);
    setCantinaQuickItemName('');
    setCantinaQuickItemPrice('');
    setCantinaQuickItemQuantity(1);
    setShowCantinaQuickItemModal(false);
  };

  const handleRemoveFromCart = (productId: string) => {
    setCantinaCart(prev => {
      const existing = prev.find(item => item.id === productId);
      if (existing && existing.quantity > 1) {
        return prev.map(item => item.id === productId ? { ...item, quantity: item.quantity - 1 } : item);
      }
      return prev.filter(item => item.id !== productId);
    });
  };

  const handleSaveCantinaSale = async () => {
    if (cantinaCart.length === 0) {
      alert('El carrito está vacío.');
      return;
    }
    if (!userId) return;

    try {
      const cartTotal = cantinaCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const newSaleId = 'pos_cantina_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const saleDocRef = doc(db, getCollectionPath('bookings'), newSaleId);
      
      const timeSlot = new Date().toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false });
      
      const newSale = {
        id: newSaleId,
        clubId: userId,
        courtName: 'CANTINA',
        playerName: cantinaClientName || 'Consumidor Final',
        date: todayStr,
        timeSlot: timeSlot,
        duration: 0,
        status: 'paid',
        isFixed: false,
        price: cartTotal,
        paymentBreakdown: { 
          cash: Math.min(cartTotal, parseInt(cantinaPaymentCash || '0')), 
          transfer: cartTotal - Math.min(cartTotal, parseInt(cantinaPaymentCash || '0')) 
        },
        paymentHistory: [{ 
          action: 'initial_charge', 
          timestamp: Date.now(), 
          by: activeUserName || 'Admin', 
          newTotal: cartTotal, 
          newCash: Math.min(cartTotal, parseInt(cantinaPaymentCash || '0')), 
          newTransfer: cartTotal - Math.min(cartTotal, parseInt(cantinaPaymentCash || '0')) 
        }],
        items: cantinaCart.map(item => ({ ...item, isPaid: true })),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        hiddenFromGrid: true,
        courtId: 'pos_cantina',
        receiptNumber: ''
      };

      const alerts: string[] = [];

      await runTransaction(db, async (transaction) => {
        // 1. Guardar la venta
        transaction.set(saleDocRef, newSale);

        // 2. Descontar stock
        for (const item of cantinaCart) {
          const prod = products.find(p => p.id === item.id);
          if (prod && prod.stock !== undefined && prod.stock !== null) {
            const productRef = doc(db, getCollectionPath('products'), item.id);
            const newStock = Math.max(0, prod.stock - item.quantity);
            
            transaction.update(productRef, {
              stock: newStock,
              updatedAt: Date.now()
            });

            if (newStock <= (prod.minStockAlert || 0) && prod.stock > (prod.minStockAlert || 0)) {
              alerts.push(`¡Alerta de stock! Quedan ${newStock} unidades de ${prod.name}.`);
            } else if (newStock <= (prod.minStockAlert || 0)) {
              alerts.push(`Stock bajo: Quedan ${newStock} unidades de ${prod.name}.`);
            }
          }
        }
      });
      
      setShowCantinaModal(false);
      showSuccessToast('Venta registrada con éxito');
      setCantinaCart([]);
      setCantinaSearch('');
      setCantinaCategory('all');
      setCantinaPaymentCash('');
      setCantinaClientName('Consumidor Final');

      if (alerts.length > 0) {
        setTimeout(() => {
          alert(alerts.join('\n'));
        }, 500);
      }
      
    } catch (error) {
      console.error("Error al registrar venta cantina:", error);
      alert('Hubo un error al registrar la venta.');
    }
  };

  const handleSaveExpense = async () => {
    const amountNum = parseFloat(expenseAmount);
    if (!amountNum || amountNum <= 0) {
      alert('Por favor ingrese un monto válido para el egreso.');
      return;
    }
    
    if (!userId) return;

    try {
      const newExpenseId = 'booking_expense_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const expenseDocRef = doc(db, getCollectionPath('bookings'), newExpenseId);
      
      const timeSlot = new Date().toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false });
      
      const newExpense = {
        id: newExpenseId,
        clubId: userId,
        courtName: expenseCategory,
        playerName: expenseDescription || 'Egreso de caja',
        date: todayStr,
        timeSlot: timeSlot,
        duration: 0,
        status: 'paid',
        isFixed: false,
        price: -amountNum,
        paymentBreakdown: { 
          cash: expenseMethod === 'cash' ? -amountNum : 0, 
          transfer: expenseMethod === 'transfer' ? -amountNum : 0 
        },
        paymentHistory: [{ 
          action: 'initial_charge', 
          timestamp: Date.now(), 
          by: activeUserName || 'Admin', 
          newTotal: -amountNum, 
          newCash: expenseMethod === 'cash' ? -amountNum : 0, 
          newTransfer: expenseMethod === 'transfer' ? -amountNum : 0 
        }],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        hiddenFromGrid: true,
        courtId: 'expense',
        receiptNumber: ''
      };

      await setDoc(expenseDocRef, newExpense);
      
      setShowExpenseModal(false);
      showSuccessToast('Egreso registrado con éxito');
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseCategory('Gastos / Pagos');
      setExpenseMethod('cash');
      setExpenseTab('general');
      
    } catch (error) {
      console.error("Error al registrar egreso:", error);
      alert('Hubo un error al registrar el egreso.');
    }
  };

  const handleSaveStockPurchase = async () => {
    const amountNum = parseFloat(expenseAmount);
    const qty = parseInt(stockQuantity);
    
    if (!amountNum || amountNum <= 0) {
      alert('Por favor ingrese un costo total válido.');
      return;
    }
    if (!stockProductId) {
      alert('Por favor seleccione un producto.');
      return;
    }
    const prod = products.find(p => p.id === stockProductId);
    if (!prod) return;
    
    if (prod.trackStock !== false) {
      if (!qty || qty <= 0) {
        alert('Por favor ingrese una cantidad válida.');
        return;
      }
    }
    if (!userId) return;

    try {
      // 1. Crear el egreso
      const newExpenseId = 'booking_expense_stock_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const expenseDocRef = doc(db, getCollectionPath('bookings'), newExpenseId);
      
      const timeSlot = new Date().toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false });
      
      const newExpense = {
        id: newExpenseId,
        clubId: userId,
        courtName: 'Compra de Stock',
        playerName: `Compra de ${prod.trackStock === false ? 'varios' : qty + 'x'} ${prod.name}`,
        date: todayStr,
        timeSlot: timeSlot,
        duration: 0,
        status: 'paid',
        isFixed: false,
        price: -amountNum,
        paymentBreakdown: { 
          cash: expenseMethod === 'cash' ? -amountNum : 0, 
          transfer: expenseMethod === 'transfer' ? -amountNum : 0 
        },
        paymentHistory: [{ 
          action: 'initial_charge', 
          timestamp: Date.now(), 
          by: activeUserName || 'Admin', 
          newTotal: -amountNum, 
          newCash: expenseMethod === 'cash' ? -amountNum : 0, 
          newTransfer: expenseMethod === 'transfer' ? -amountNum : 0 
        }],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        hiddenFromGrid: true,
        courtId: 'expense',
        receiptNumber: ''
      };

      // 2. Actualizar el stock del producto
      const productRef = doc(db, getCollectionPath('products'), stockProductId);
      const currentStock = prod.stock || 0;
      
      await runTransaction(db, async (transaction) => {
        transaction.set(expenseDocRef, newExpense);
        if (prod.trackStock !== false) {
          transaction.update(productRef, {
            stock: currentStock + qty,
            updatedAt: Date.now()
          });
        }
      });
      
      setShowExpenseModal(false);
      showSuccessToast('Compra de stock registrada con éxito');
      setExpenseAmount('');
      setStockQuantity('');
      setStockProductId('');
      setExpenseMethod('cash');
      setExpenseTab('general');
      
    } catch (error) {
      console.error("Error al registrar compra de stock:", error);
      alert('Hubo un error al registrar la compra de stock.');
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">

      {/* CABECERA DE LA PÁGINA CON BOTÓN DE AJUSTES */}
      <div className="flex items-center justify-between gap-3 md:gap-0 w-full relative">
        
        {/* MOBILE HEADER (Left Aligned Logo + "panel principal") */}
        <div className="shrink-0 flex items-center gap-2 md:hidden">
          <h2 className="text-xl font-black uppercase tracking-wider text-white flex items-center gap-2 italic">
            {sedesPublicas?.find(s => s.uid === userId)?.photoUrl || players?.find(p => p.id === userId)?.photoUrl ? (
              <img 
                src={sedesPublicas?.find(s => s.uid === userId)?.photoUrl || players?.find(p => p.id === userId)?.photoUrl} 
                alt="Logo Sede" 
                className="w-[37px] h-[37px] rounded-full object-cover shrink-0" 
              />
            ) : (
              <Sliders className="theme-primary-text shrink-0" size={24} />
            )}
            <span className="truncate">panel principal</span>
          </h2>
        </div>

        {/* DESKTOP HEADER LEFT: Navigation Buttons */}
        <div className="hidden md:flex items-center gap-4 shrink-0 w-[140px] lg:w-[180px] pl-2">
          <button
            onClick={() => onNavigateToSection?.('torneos')}
            className="flex flex-col items-center justify-center text-white/50 hover:text-white transition-all hover:scale-110 px-2 py-1"
            title="Ir a Torneos"
          >
            <Trophy size={28} className="mb-1.5" />
            <span className="text-[10px] lg:text-[11px] font-black uppercase tracking-widest">Torneos</span>
          </button>
          
          {(userRole === 'admin' || userRole === 'co_admin' || userRole === 'gestor_torneos' || userRole === 'superadmin') && (
            <button
              onClick={() => onNavigateToSection?.('analiticas')}
              className="flex flex-col items-center justify-center text-white/50 hover:text-white transition-all hover:scale-110 px-2 py-1"
              title="Ir a Métricas"
            >
              <BarChart3 size={28} className="mb-1.5" />
              <span className="text-[10px] lg:text-[11px] font-black uppercase tracking-widest">Métricas</span>
            </button>
          )}
        </div>

        {/* DESKTOP LINE 1 (Longer with gradient) */}
        <div className="hidden md:block flex-1 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-white/30 mx-2 lg:mx-4" />

        {/* DESKTOP CENTER: Logo + PANEL PRINCIPAL + DATE */}
        <div className="hidden md:flex flex-col items-center justify-center shrink-0 px-2 gap-1 relative">
          <div className="flex items-center gap-3">
            {sedesPublicas?.find(s => s.uid === userId)?.photoUrl || players?.find(p => p.id === userId)?.photoUrl ? (
              <img 
                src={sedesPublicas?.find(s => s.uid === userId)?.photoUrl || players?.find(p => p.id === userId)?.photoUrl} 
                alt="Logo Sede" 
                className="w-[48px] h-[48px] lg:w-[56px] lg:h-[56px] rounded-full object-cover shrink-0 border border-white/10" 
              />
            ) : (
              <Sliders className="theme-primary-text shrink-0" size={32} />
            )}
            <h2 className="text-2xl lg:text-3xl font-black uppercase tracking-widest text-white italic drop-shadow-md">
              PANEL PRINCIPAL
            </h2>
          </div>
          <span className="absolute top-[90%] text-[10px] lg:text-xs font-bold text-zinc-400 uppercase tracking-[0.35em] text-center whitespace-nowrap">
            {(() => {
              const days = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
              const d = new Date();
              const closeTimeStr = schedule?.cashRegisterCloseTime || '03:30';
              const [closeHour, closeMinute] = closeTimeStr.split(':').map(Number);
              const shiftedDate = new Date(d.getTime() - (closeHour * 60 + closeMinute) * 60000);
              
              return `${days[shiftedDate.getDay()]} ${String(shiftedDate.getDate()).padStart(2, '0')}/${String(shiftedDate.getMonth() + 1).padStart(2, '0')}`;
            })()}
          </span>
        </div>

        {/* DESKTOP LINE 2 (Longer with gradient) */}
        <div className="hidden md:block flex-1 h-[1px] bg-gradient-to-r from-white/30 via-white/20 to-transparent mx-2 lg:mx-4" />

        {/* MOBILE & DESKTOP RIGHT: Buttons */}
        <div className="flex items-center gap-2 shrink-0 w-[100px] lg:w-[140px] md:justify-end">
          {/* Botón de Egresos (Solo Móvil) */}
          <button
            onClick={() => setShowExpenseModal(true)}
            className="md:hidden bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 hover:border-red-500/40 text-red-500 p-1.5 rounded-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-md group cursor-pointer shrink-0"
            title="Registrar Egreso"
          >
            <TrendingDown size={16} className="group-hover:scale-110 transition-transform" />
          </button>

          {/* Botón de Configuración (Engranaje + Palabra Configuración) */}
          <button
            onClick={onNavigateToConfiguracion}
            className="flex items-center justify-center gap-2.5 text-white/50 hover:text-white transition-all hover:scale-110 group cursor-pointer shrink-0 px-2 py-1"
            title="Configuración de Turnos"
          >
            <span className="hidden md:inline text-[11px] lg:text-[12px] font-black uppercase tracking-widest transition-colors">Configuración</span>
            <Settings size={26} className="text-white/60 group-hover:text-yellow-400 group-hover:rotate-90 transition-all duration-500" />
          </button>
        </div>
      </div>

      {/* MOBILE TABS: Torneos y Métricas (Debajo de la cabecera) */}
      <div className="flex md:hidden justify-center items-center gap-4 w-full pt-1 pb-2">
        <button
          onClick={() => onNavigateToSection?.('torneos')}
          className="flex-1 flex items-center justify-center gap-2 text-white/70 hover:text-white transition-all bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl border border-white/10"
        >
          <Trophy size={16} className="text-theme-primary/70" />
          <span className="text-[10px] font-black uppercase tracking-widest">Torneos</span>
        </button>
        
        {(userRole === 'admin' || userRole === 'co_admin' || userRole === 'gestor_torneos' || userRole === 'superadmin') && (
          <button
            onClick={() => onNavigateToSection?.('analiticas')}
            className="flex-1 flex items-center justify-center gap-2 text-white/70 hover:text-white transition-all bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl border border-white/10"
          >
            <BarChart3 size={16} className="text-theme-primary/70" />
            <span className="text-[10px] font-black uppercase tracking-widest">Métricas</span>
          </button>
        )}
      </div>

      {/* 1. TARJETAS KPI (ESTADÍSTICAS) CON NUEVO DISEÑO RESPONSIVO */}
      <div className="grid grid-cols-12 gap-1.5 md:gap-4">
{/* CARD 3: Balance Hoy (Disposición mejorada con línea y botón debajo) */}
        <div className="col-span-4 md:col-span-3 bg-[#1a1c23] border border-[#2d313f] rounded-xl md:rounded-2xl px-2.5 md:px-4 flex flex-col justify-between py-2 md:py-2.5 h-[68px] md:h-[86px] relative overflow-hidden shadow-lg group hover:border-theme-primary/40 transition-all">


          {/* Header con título a la izquierda, botón en móvil, y línea en desktop */}
          <div className="flex items-center justify-between sm:justify-start gap-1.5 sm:gap-2 w-full select-none shrink-0 mt-0.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[6.5px] min-[360px]:text-[7.5px] sm:text-[9.5px] md:text-[11px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">
                <span className="inline sm:hidden">Balance</span>
                <span className="hidden sm:inline">Balance Hoy</span>
              </span>
              {/* Ojo en DESKTOP: al lado del texto */}
              <button
                onClick={() => setShowBalance(!showBalance)}
                className="hidden sm:flex text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer p-0.5 items-center justify-center"
                title={showBalance ? "Ocultar importe" : "Mostrar importe"}
              >
                {showBalance ? <EyeOff size={12} className="md:w-3.5 md:h-3.5" /> : <Eye size={12} className="md:w-3.5 md:h-3.5" />}
              </button>
            </div>

            {/* Línea divisoria: se oculta en móvil, se muestra en desktop */}
            <div className="hidden sm:block h-[1px] bg-zinc-800/80 flex-grow" />

            {/* Botón Ver Caja: visible en la cabecera SÓLO en móvil */}
            <button
              onClick={() => setIsCashModalOpen(true)}
              className="sm:hidden bg-[#FACC15] hover:bg-[#fde047] text-black text-[5.5px] min-[360px]:text-[6px] font-black uppercase tracking-wider px-1 py-0.5 rounded transition-all hover:scale-105 active:scale-95 shadow-md shadow-[#FACC15]/10 flex items-center gap-0.5 cursor-pointer whitespace-nowrap shrink-0"
            >
              <span>Ver caja</span>
            </button>
          </div>

          {/* Contenido (monto completo abajo; centrado en móvil y alineado a la izquierda en desktop con botón a la derecha) */}
          <div className="flex items-center justify-center sm:justify-between w-full flex-grow pb-0.5 md:pb-1 gap-1 md:gap-2 z-10">
            <span className="text-[10px] min-[360px]:text-[11px] sm:text-xs md:text-lg lg:text-xl font-black text-white leading-none truncate pr-1 text-center sm:text-left">
              {showBalance ? <><span className="mr-0.5">$</span><AnimatedNumber value={stats.totalIngresos} /></> : '••••••'}
            </span>

            {/* Botón Ver Caja: visible abajo SÓLO en desktop */}
            <button
              onClick={() => setIsCashModalOpen(true)}
              className="hidden sm:flex bg-[#FACC15] hover:bg-[#fde047] text-black text-[7px] min-[360px]:text-[7.5px] sm:text-[9px] md:text-[10px] font-black uppercase tracking-wider px-1.5 sm:px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg transition-all hover:scale-105 active:scale-95 shadow-md shadow-[#FACC15]/10 items-center gap-0.5 sm:gap-1 cursor-pointer whitespace-nowrap"
            >
              <span>Ver caja</span>
            </button>
          </div>

          {/* Ojo en MÓVIL: esquina inferior derecha, posición absoluta */}
          <button
            onClick={() => setShowBalance(!showBalance)}
            className="sm:hidden absolute bottom-1.5 right-2 text-zinc-500 hover:text-zinc-300 active:text-zinc-200 transition-colors cursor-pointer p-0.5 flex items-center justify-center z-20"
            title={showBalance ? "Ocultar importe" : "Mostrar importe"}
          >
            {showBalance ? <EyeOff size={11} /> : <Eye size={11} />}
          </button>
        </div>


                {/* CARD 3: Cuentas / Mesas Cantina */}
        <AdminCantinaTabsCard 
          tabs={cantinaTabs} 
          recentPayments={recentCantinaPayments}
          onTabClick={(id, defName) => { setSelectedTabId(id); setCantinaTabDefaultName(defName); setCantinaTabSlotNumber(undefined); setIsTabModalOpen(true); }}
          onNewTabClick={(defName, slotNum) => { setSelectedTabId(null); setCantinaTabDefaultName(defName); setCantinaTabSlotNumber(slotNum); setIsTabModalOpen(true); }} 
          onFastSaleClick={() => setShowCantinaModal(true)}
        />

        {/* CARD: Egresos (Solo Desktop) */}
        <button
          onClick={() => {
            setExpenseTab('general');
            setShowExpenseModal(true);
          }}
          className="hidden md:flex col-span-1 bg-[#1a1c23] border border-[#2d313f] rounded-xl md:rounded-2xl flex-col items-center justify-center h-[68px] md:h-[86px] relative shadow-lg hover:border-white/40 hover:bg-white/5 transition-all group cursor-pointer active:scale-95"
          title="Registrar Egreso"
        >
          <TrendingDown size={20} className="text-red-500 group-hover:scale-110 transition-transform mb-1" />
          <span className="text-[8px] md:text-[10px] font-black uppercase tracking-wider text-zinc-400 group-hover:text-white">Egresos</span>
          {bookings.filter(b => b.courtId === 'expense' && b.courtName !== 'Compra de Stock' && b.status !== 'cancelled' && b.date === todayStr).length > 0 && (
            <div className="absolute top-1 right-1 bg-red-500 text-white text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-[0_0_8px_rgba(239,68,68,0.4)] z-10">
              {bookings.filter(b => b.courtId === 'expense' && b.courtName !== 'Compra de Stock' && b.status !== 'cancelled' && b.date === todayStr).length}
            </div>
          )}
        </button>

        {/* CARD: Stock Compra (Solo Desktop) */}
        <button
          onClick={() => {
            setExpenseTab('stock');
            setShowExpenseModal(true);
          }}
          className="hidden md:flex col-span-1 bg-[#1a1c23] border border-[#2d313f] rounded-xl md:rounded-2xl flex-col items-center justify-center h-[68px] md:h-[86px] relative shadow-lg hover:border-white/40 hover:bg-white/5 transition-all group cursor-pointer active:scale-95"
          title="Comprar Stock"
        >
          <ShoppingCart size={20} className="text-theme-primary group-hover:scale-110 transition-transform mb-1" />
          <span className="text-[8px] md:text-[10px] font-black uppercase tracking-wider text-zinc-400 group-hover:text-white text-center leading-tight">Stock<br/>Compra</span>
          {bookings.filter(b => b.courtId === 'expense' && b.courtName === 'Compra de Stock' && b.status !== 'cancelled' && b.date === todayStr).length > 0 && (
            <div className="absolute top-1 right-1 bg-white text-black text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-[0_0_8px_rgba(255,255,255,0.4)] z-10">
              {bookings.filter(b => b.courtId === 'expense' && b.courtName === 'Compra de Stock' && b.status !== 'cancelled' && b.date === todayStr).length}
            </div>
          )}
        </button>



      </div>

      {/* --- PANEL MOVIL DE SOLICITUDES (Colapsable, solo visible en movil, posicionado arriba del selector de fechas) --- */}
      <div className="block lg:hidden w-full max-w-[360px] mx-auto px-4 mb-3">
        <div className={`bg-[#1a1c23] rounded-2xl overflow-hidden shadow-xl border ${requests.length > 0
          ? 'border-theme-primary/30 shadow-[0_4px_20px_rgba(var(--glow-rgb),0.05)]'
          : 'border-[#2d313f]'
          }`}>
          <button
            id="mobile-pending-requests-toggle"
            onClick={() => setMobileRequestsOpen(!mobileRequestsOpen)}
            className="w-full p-4 flex items-center justify-between bg-[#222530] hover:bg-[#222530]/80 transition-all text-left"
          >
            <div className="flex items-center gap-2.5">
              {requests.length > 0 ? (
                <span className="relative flex h-3 w-3 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C5FF4A] opacity-80"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#C5FF4A] shadow-[0_0_10px_#C5FF4A]"></span>
                </span>
              ) : (
                <span className="w-2.5 h-2.5 rounded-full bg-zinc-600 shrink-0" />
              )}
              <h3 className="text-xs font-black uppercase tracking-widest text-zinc-200 text-center flex-1">
                Solicitudes de Reservas ({requests.length})
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <svg
                className={`w-4 h-4 text-zinc-400 transition-transform duration-300 ${mobileRequestsOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {mobileRequestsOpen && (
            <div className="p-4 space-y-3.5 max-h-[420px] overflow-y-auto bg-[#15171d]/50">
              {requests.length === 0 ? (
                <div className="text-center py-8 text-white/30 space-y-2">
                  <CheckCircle2 size={28} className="mx-auto text-white/10" />
                  <p className="text-xs font-medium">No hay solicitudes pendientes</p>
                </div>
              ) : (
                requests.map((req) => {
                  const isHighlighted = localHighlightId === req.id;
                  return (
                    <div
                      key={req.id}
                      id={`request-card-mobile-${req.id}`}
                      className={`relative bg-[#222530] border rounded-xl p-3.5 space-y-3 shadow-md transition-all duration-300 ${isHighlighted
                        ? 'border-yellow-500/80 shadow-[0_0_20px_rgba(234,179,8,0.25)] ring-1 ring-yellow-500/50 scale-[1.02] animate-pulse'
                        : 'border-[#2d313f]'
                        }`}
                    >
                      {/* Botón de Chat en la esquina superior derecha */}
                      <button
                        onClick={() => {
                          if (req.playerId) {
                            onOpenChatWithPlayer?.(req.playerId, req.playerName || 'Jugador', req.playerPhoto || '');
                          } else {
                            alert('Esta solicitud no tiene un jugador registrado.');
                          }
                        }}
                        className="absolute top-3.5 right-3.5 bg-[#2d313f]/60 hover:bg-[#393d4f] text-white border border-[#3e4357] p-2 rounded-lg flex items-center justify-center transition-all shadow-sm shrink-0 z-10"
                        title="Chatear con jugador"
                      >
                        <MessageSquare size={13} />
                      </button>

                      {/* Jugador Info */}
                      <div className="flex items-center gap-2.5">
                        {req.playerPhoto ? (
                          <img 
                            src={req.playerPhoto} 
                            alt={req.playerName} 
                            className="w-8 h-8 rounded-full object-cover border border-[#2d313f] cursor-pointer hover:border-theme-primary transition-colors hover:scale-105" 
                            onClick={(e) => {
                              e.stopPropagation();
                              const p = players.find(x => x.id === req.playerId);
                              if (p) setSelectedPlayerForDetail(p);
                            }}
                          />
                        ) : (
                          <div 
                            className="w-8 h-8 rounded-full bg-[#1e202b] flex items-center justify-center border border-[#2d313f] cursor-pointer hover:border-theme-primary transition-colors hover:scale-105"
                            onClick={(e) => {
                              e.stopPropagation();
                              const p = players.find(x => x.id === req.playerId);
                              if (p) setSelectedPlayerForDetail(p);
                            }}
                          >
                            <User size={14} className="text-zinc-400" />
                          </div>
                        )}
                        <div className="min-w-0 pr-8">
                          <h4 className="text-xs font-black text-white truncate leading-tight">{req.playerName}</h4>
                          <p className="text-[9px] text-theme-primary font-black uppercase mt-0.5">{req.playerCategory || 'Cat -'}</p>
                        </div>
                      </div>

                      {/* Detalle del Turno Solicitado */}
                      <div className="bg-[#1a1c23] rounded-xl p-2.5 space-y-1.5 text-xs border border-[#2d313f]/60">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-black uppercase text-zinc-400 tracking-widest">Turno Solicitado</span>
                          <span className="text-[10px] text-theme-primary font-black px-1.5 py-0.5 rounded bg-theme-primary/10 border border-theme-primary/20">
                            {req.option1.timeSlot}h
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-white font-black text-xs block truncate">{formatRequestDate(req.date)}</span>
                          <span className="text-zinc-400 font-bold text-[10px] block truncate">{req.option1.courtName}</span>
                        </div>
                      </div>

                      {req.notes && (
                        <p className="text-[10px] text-zinc-300 italic leading-snug bg-[#15171d] p-2 rounded-lg border border-[#2d313f]">
                          "{req.notes}"
                        </p>
                      )}

                      {/* Agregar Nota o Mensaje del Club */}
                      <div className="space-y-1">
                        <label className="text-[9px] font-black uppercase text-zinc-400 tracking-wider">Responder con mensaje (opcional):</label>
                        <textarea
                          value={requestNotes[req.id] || ''}
                          onChange={(e) => setRequestNotes(prev => ({ ...prev, [req.id]: e.target.value }))}
                          placeholder="Escribe un mensaje que el jugador verá en sus turnos..."
                          rows={2}
                          className="w-full bg-[#15171d] border border-[#2d313f] rounded-lg p-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-primary transition-colors resize-none"
                        />
                      </div>

                      {/* Acciones */}
                      {req.status === 'deposit_required' ? (
                        <div className="flex flex-col gap-2 border-t border-[#2d313f] pt-2.5 w-full items-center">
                          <div className="bg-sky-500/10 text-sky-400 border border-sky-500/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider text-center w-full relative flex flex-col items-center justify-center">
                            <span>Esperando pago de seña ({req.requiredDepositPercentage}%)</span>
                            {req.depositExpiresAt && <CountdownText expiresAt={req.depositExpiresAt} />}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (await window.confirm('¿Cancelar el requerimiento de seña y volver a estado pendiente?')) {
                                  handleCancelDeposit(req);
                                }
                              }}
                              className="absolute right-2 p-1 hover:bg-sky-500/20 rounded-full transition-colors text-sky-400/70 hover:text-sky-300"
                              title="Cancelar requerimiento de seña"
                            >
                              <X size={12} strokeWidth={3} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 border-t border-[#2d313f] pt-2.5 w-full">
                          <div className="flex items-center gap-2 w-full">
                            <button
                              onClick={async () => {
                                if (await window.confirm(`¿Estás seguro de que deseas ACEPTAR la solicitud de ${req.playerName} para el ${formatRequestDate(req.date)} a las ${req.option1.timeSlot}hs?`)) {
                                  handleResolveRequest(req, 'option1', requestNotes[req.id]);
                                }
                              }}
                              className="flex-[2] bg-[#C5FF4A]/15 hover:bg-[#C5FF4A]/25 text-[#C5FF4A] border border-[#C5FF4A]/30 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-center cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                            >
                              Aceptar
                            </button>
                            <button
                              onClick={async () => {
                                if (await window.confirm(`¿Estás seguro de que deseas RECHAZAR la solicitud de ${req.playerName} para el ${formatRequestDate(req.date)}?`)) {
                                  handleResolveRequest(req, 'reject', requestNotes[req.id]);
                                }
                              }}
                              className="flex-[1] bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-center cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                            >
                              Rechazar
                            </button>
                          </div>
                          
                          <div className="relative w-full">
                            <button
                              onClick={() => setDepositDropdownOpen(depositDropdownOpen === req.id ? null : req.id)}
                              className="w-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-center cursor-pointer flex items-center justify-center gap-1 hover:scale-[1.01] active:scale-[0.99]"
                            >
                              <DollarSign size={12} /> Requerir Seña {depositDropdownOpen === req.id ? <ChevronDown size={12} className="rotate-180" /> : <ChevronDown size={12} />}
                            </button>
                            
                            {depositDropdownOpen === req.id && (
                              <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#1a1c23] border border-[#2d313f] rounded-lg shadow-xl overflow-hidden z-20 flex flex-col animate-in slide-in-from-bottom-2 duration-200">
                                {[25, 50, 100].map((perc) => (
                                  <button
                                    key={perc}
                                    onClick={async () => {
                                      if (await window.confirm(`¿Estás seguro de que deseas requerir una seña del ${perc}% al jugador para confirmar este turno?`)) {
                                        handleRequireDeposit(req, 'option1', perc as 25 | 50 | 100);
                                        setDepositDropdownOpen(null);
                                      }
                                    }}
                                    className="px-3 py-2 text-xs font-bold text-white hover:bg-[#2d313f] text-left flex items-center justify-between border-b border-[#2d313f] last:border-0"
                                  >
                                    <span>Requerir {perc}%</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2. SELECTOR DE FECHA MÓVIL */}
      <div className="flex md:hidden justify-center items-center gap-2 w-full max-w-[360px] mx-auto px-4">
        {/* BOTÓN "HOY" AL COSTADO IZQUIERDO */}
        <button
          onClick={() => setSelectedDate(todayStr)}
          className={`h-11 px-4 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 transform active:scale-95 border flex items-center justify-center gap-1.5 shrink-0 ${selectedDate === todayStr
            ? 'theme-primary-bg text-black border-transparent shadow-[0_4px_12px_rgba(var(--glow-rgb),0.35)]'
            : 'bg-[#2a2d3a] text-white/80 border-white/10 hover:bg-[#34384a]'
            }`}
        >
          Hoy
        </button>

        {/* SELECTOR DE FECHA SÚPER PREMIUM MÓVIL */}
        <div className="flex-1 flex items-center justify-between bg-[#2a2d3a] border border-white/15 rounded-xl p-0.5 backdrop-blur-md h-11">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 active:scale-95 transition-all shrink-0"
          >
            <ChevronLeft size={16} />
          </button>

          <div
            onClick={() => {
              try {
                dateInputRef.current?.showPicker();
              } catch (err) {
                dateInputRef.current?.focus();
                dateInputRef.current?.click();
              }
            }}
            className="flex-1 px-2 text-center flex flex-col justify-center hover:bg-white/5 py-0.5 rounded-lg transition-all relative overflow-hidden cursor-pointer h-full"
            title="Seleccionar fecha"
          >
            <span className="text-[8px] font-bold uppercase tracking-wider text-theme-primary font-black pointer-events-none leading-none">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })}
            </span>
            <span className="text-xs font-black text-white leading-none mt-1 pointer-events-none">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })}
            </span>
            {/* Input de tipo fecha ocultado de forma segura, pero accesible vía ref */}
            <input
              ref={dateInputRef}
              type="date"
              min={minDateLimitStr}
              max={maxDateLimitStr}
              value={selectedDate}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate >= minDateLimitStr && newDate <= maxDateLimitStr) {
                  setSelectedDate(newDate);
                } else {
                  alert(`Para mantener la app rápida, solo puedes navegar entre el ${minDateLimitStr.split('-').reverse().join('/')} y el ${maxDateLimitStr.split('-').reverse().join('/')}`);
                }
              }}
              className="absolute inset-0 opacity-0 pointer-events-none w-0 h-0"
            />
          </div>

          <button
            onClick={() => changeDate(1)}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 active:scale-95 transition-all shrink-0"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ESCRITORIO: Barra premium de botones de días de la semana, botón "Otro...", sin Título/Descripción */}
      <div className="hidden md:flex md:items-stretch justify-between gap-2 w-full relative">
        {/* CAROUSEL CONTROLS & WRAPPER */}
        <div className="relative flex-1 flex items-center min-w-0 overflow-hidden">
          {canDaysScrollLeft && (
            <div className="absolute left-1 top-0 bottom-0 flex items-center justify-center z-10 pointer-events-none">
              <button 
                onClick={() => {
                  if (daysCarouselRef.current) {
                    daysCarouselRef.current.scrollBy({ left: -200, behavior: 'smooth' });
                  }
                }}
                className="bg-black/80 hover:bg-black rounded-full p-1.5 text-white border border-white/20 pointer-events-auto transition-transform hover:scale-110 active:scale-95 shadow-md"
              >
                <ChevronLeft size={16} className="stroke-[3]" />
              </button>
            </div>
          )}

          <div 
            ref={daysCarouselRef}
            onMouseDown={(e) => {
              setIsDraggingDaysCarousel(true);
              setDaysCarouselStartX(e.pageX - daysCarouselRef.current!.offsetLeft);
              setDaysCarouselScrollLeft(daysCarouselRef.current!.scrollLeft);
              hasDraggedDaysCarousel.current = false;
            }}
            onMouseLeave={() => setIsDraggingDaysCarousel(false)}
            onMouseUp={() => setIsDraggingDaysCarousel(false)}
            onMouseMove={(e) => {
              if (!isDraggingDaysCarousel) return;
              e.preventDefault();
              const x = e.pageX - daysCarouselRef.current!.offsetLeft;
              const walk = (x - daysCarouselStartX) * 1.5; 
              if (Math.abs(walk) > 5) {
                hasDraggedDaysCarousel.current = true;
              }
              daysCarouselRef.current!.scrollLeft = daysCarouselScrollLeft - walk;
            }}
            onScroll={checkDaysCarouselScroll}
            className={`flex items-center gap-2 md:gap-3 w-full overflow-x-auto scrollbar-hide py-1 px-1 ${isDraggingDaysCarousel ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ 
              msOverflowStyle: 'none', 
              scrollbarWidth: 'none',
              maskImage: `linear-gradient(to right, ${canDaysScrollLeft ? 'transparent, black 40px' : 'black 0, black'}, black calc(100% - ${canDaysScrollRight ? '40px' : '0px'}), ${canDaysScrollRight ? 'transparent' : 'black'})`,
              WebkitMaskImage: `linear-gradient(to right, ${canDaysScrollLeft ? 'transparent, black 40px' : 'black 0, black'}, black calc(100% - ${canDaysScrollRight ? '40px' : '0px'}), ${canDaysScrollRight ? 'transparent' : 'black'})`
            }}
          >
            {desktopDays.map((day) => {
              const isSelected = selectedDate === day.dateStr;
              const targetDayOfWeek = new Date(day.dateStr + 'T00:00:00').getDay();
              const directDay = bookings.filter(b => b.date === day.dateStr && (b.status === 'confirmed' || b.status === 'paid') && (!b.excludedDates || !b.excludedDates.includes(day.dateStr)) && !b.hiddenFromGrid);
              const projectedFixedDay = bookings.filter(b =>
                b.isFixed &&
                (!b.id.includes('_materialized_') || b.isFixedRoot) &&
                !b.id.includes('_cancelled_') &&
                (b.status === 'confirmed' || b.status === 'paid') &&
                new Date(b.date + 'T00:00:00').getDay() === targetDayOfWeek &&
                day.dateStr >= b.date &&
                (!b.excludedDates || !b.excludedDates.includes(day.dateStr)) &&
                !b.hiddenFromGrid &&
                !directDay.some(d => d.courtName === b.courtName && d.timeSlot === b.timeSlot)
              );
              const activeBookingsCount = directDay.length;
              const courtsCount = schedule?.courts?.length || 3;
              const actualSlotsCount = timeSlots.length;
              const totalSlotsCount = (actualSlotsCount || 10) * courtsCount;
              const occupancyRatio = totalSlotsCount > 0 ? activeBookingsCount / totalSlotsCount : 0;

              const occupancyPercentage = Math.min(100, Math.round(Math.sqrt(occupancyRatio) * 100));

              return (
                <button
                  key={day.dateStr}
                  onClick={(e) => {
                    if (hasDraggedDaysCarousel.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    setSelectedDate(day.dateStr);
                  }}
                  className={`shrink-0 min-w-[110px] lg:min-w-[140px] px-2 lg:px-4 py-3 rounded-xl text-[11px] lg:text-xs font-bold uppercase tracking-wider transition-all duration-300 transform active:scale-95 flex flex-col items-center gap-2 border ${isSelected
                    ? 'theme-primary-bg text-black font-black scale-105 shadow-md border-transparent'
                    : `bg-white/5 text-white/70 hover:text-white hover:bg-white/10 shadow-sm ${day.label === 'Hoy' ? 'border-theme-primary border-opacity-70' : 'border-white/5'}`
                    }`}
                >
                  {/* Barra continua indicadora de disponibilidad */}
                  <div className={`w-full h-1.5 rounded-full overflow-hidden p-[1px] shadow-inner border border-white/5 ${isSelected ? 'bg-black/20' : 'bg-zinc-900/60'
                    }`}>
                    {occupancyPercentage > 0 && (
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${isSelected
                          ? 'bg-black/80 shadow-[0_0_4px_rgba(0,0,0,0.5)]'
                          : 'bg-white shadow-[0_0_4px_rgba(255,255,255,0.5)]'
                          }`}
                        style={{ width: `${occupancyPercentage}%` }}
                      />
                    )}
                  </div>

                  <span className="truncate w-full text-center pointer-events-none">{day.label}</span>
                </button>
              );
            })}
          </div>

          {canDaysScrollRight && (
            <div className="absolute right-1 top-0 bottom-0 flex items-center justify-center z-10 pointer-events-none">
              <button 
                onClick={() => {
                  if (daysCarouselRef.current) {
                    daysCarouselRef.current.scrollBy({ left: 200, behavior: 'smooth' });
                  }
                }}
                className="bg-black/80 hover:bg-black rounded-full p-1.5 text-white border border-white/20 pointer-events-auto transition-transform hover:scale-110 active:scale-95 shadow-md"
              >
                <ChevronRight size={16} className="stroke-[3]" />
              </button>
            </div>
          )}
        </div>

        {/* BOTÓN CALENDARIO COMPLETO DEL MES (Fijo a la derecha) */}
        <button
          onClick={() => {
            setCalendarMonthDate(new Date(selectedDate + 'T00:00:00'));
            setShowMonthCalendar(true);
          }}
          className={`flex-none shrink-0 px-4 lg:px-5 py-2.5 lg:py-3 rounded-xl text-xs lg:text-sm font-bold uppercase tracking-wider transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-2 ${!isDateInDesktopDays
            ? 'theme-primary-bg text-black font-black scale-105 shadow-md'
            : 'bg-white/5 text-white/80 hover:text-white hover:bg-white/15 border border-dashed border-white/20 shadow-sm'
            }`}
          title="Ver calendario completo del mes con reservas"
        >
          <Calendar size={16} className={!isDateInDesktopDays ? "text-black shrink-0" : "text-theme-primary shrink-0"} />
          <span className="truncate whitespace-nowrap">
            {!isDateInDesktopDays ? `Calendario (${formattedCustomDate})` : 'Calendario'}
          </span>
        </button>
      </div>


      {/* 3. GRID PRINCIPAL (TABLERO GRILLA + SIDEBAR NOTIFICACIONES) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">

        {/* --- GRILLA HORIZONTAL DE CANCHAS --- */}
        <div className="lg:col-span-7 xl:col-span-8 bg-[#1a1c23] border border-[#2d313f] rounded-2xl overflow-hidden shadow-xl">

          {/* Header de la Grilla */}
          <div className="p-3 md:p-4 bg-[#222530] border-b border-[#2d313f] flex items-center justify-between min-h-[52px]">
            {/* Fecha seleccionada en la esquina izquierda */}
            <div className="flex items-center gap-1 md:gap-2 shrink-0 pointer-events-none pr-2">
              <CalendarDays size={14} className="text-theme-primary/80 md:w-4 md:h-4" />
              <span className="text-[10px] sm:text-xs md:text-[15px] font-black text-white uppercase tracking-wider whitespace-nowrap flex items-center gap-2 md:gap-3">
                <span>
                  <span className="hidden md:inline">{new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' }).replace(/^\w/, c => c.toUpperCase())} {formattedCustomDate}</span>
                  <span className="md:hidden">{new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short' }).replace(/^\w/, c => c.toUpperCase())} {formattedCustomDate}</span>
                </span>
                <span className="text-[8px] md:text-[10px] text-zinc-500 font-bold tracking-wide normal-case mt-0.5">
                  (Cierre diario operativo: {schedule?.cashRegisterCloseTime || '03:30'} AM)
                </span>
              </span>
            </div>

            {/* Referencias en el borde derecho */}
            <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2.5 md:gap-4 text-[7px] sm:text-[8px] md:text-[9px]">
              <span className="flex items-center gap-1 text-zinc-400 font-medium tracking-wide whitespace-nowrap">
                <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded bg-yellow-500/30 border border-yellow-500/60 inline-block" /> Pendiente
              </span>
              <span className="flex items-center gap-1 text-zinc-400 font-medium tracking-wide whitespace-nowrap">
                <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded bg-sky-500/30 border border-sky-500/60 inline-block" /> Confirmado
              </span>
              <span className="flex items-center gap-1 text-zinc-400 font-medium tracking-wide whitespace-nowrap">
                <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded bg-green-500/30 border border-green-500/60 inline-block" /> Pagado
              </span>
              <span className="flex items-center gap-0.5 text-zinc-400 font-medium tracking-wide whitespace-nowrap">
                <Pin size={8} className="text-amber-400 rotate-45 drop-shadow-[0_0_4px_rgba(245,158,11,0.6)] md:w-2.5 md:h-2.5" />
                Fijo
              </span>
            </div>
          </div>

          {/* Grilla Desplazable Horizontalmente */}
          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-[340px] md:min-w-[700px] border-collapse table-fixed">
              <thead>
                <tr className="border-b border-[#2d313f] bg-[#1e202b]">
                  {/* Celda de esquina horaria */}
                  <th className="w-12 md:w-20 p-1 md:p-3 text-center text-[10px] md:text-xs font-black uppercase tracking-wider text-zinc-400 border-r border-[#2d313f]">
                    <span>Hs</span>
                  </th>
                  {/* Columnas de canchas lado a lado (emulando Desktop Panel de la imagen) */}
                  {schedule.courts.map((court) => (
                    <th key={court} className="p-1 md:p-3 text-center text-[10px] md:text-xs font-black uppercase tracking-widest text-white border-r border-[#2d313f] last:border-r-0 truncate">
                      {court}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timeSlots.map((time) => (
                  <tr key={time} className="group/table-row border-b border-[#2d313f] transition-all">
                    {/* Fila del horario */}
                    <td className="p-1 md:p-3 text-center text-[10px] md:text-xs font-black tracking-wider text-theme-primary border-r border-[#2d313f] flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-1 h-[54px] md:h-[76px] bg-[#1a1c23] group-hover/table-row:bg-[#1e2029] hover:bg-theme-primary/10 hover:text-white transition-all cursor-default select-none group/time-slot">
                      <span>{time}</span>
                    </td>

                    {/* Celdas de turnos por cancha */}
                    {schedule.courts.map((court) => {
                      let currentBooking = bookings.find(
                        (b) => b.courtName === court && b.timeSlot === time && b.date === selectedDate && (!b.excludedDates || !b.excludedDates.includes(selectedDate)) && b.status !== 'cancelled' && !b.hiddenFromGrid
                      );

                      if (!currentBooking) {
                        const targetDayOfWeek = new Date(selectedDate + 'T00:00:00').getDay();
                        const fixedBooking = bookings.find(b =>
                          b.isFixed &&
                          (!b.id.includes('_materialized_') || b.isFixedRoot) &&
                          !b.id.includes('_cancelled_') &&
                          b.courtName === court &&
                          b.timeSlot === time &&
                          new Date(b.date + 'T00:00:00').getDay() === targetDayOfWeek &&
                          selectedDate >= b.date &&
                          b.status !== 'cancelled' &&
                          !b.hiddenFromGrid &&
                          (!b.excludedDates || !b.excludedDates.includes(selectedDate))
                        );

                        if (fixedBooking) {
                          currentBooking = {
                            ...fixedBooking,
                            id: fixedBooking.id + '_projected_' + selectedDate,
                            date: selectedDate,
                            status: 'pending',
                            confirmationStatus: 'pending_confirmation',
                            playerConfirmed: false,
                            items: [],
                            paymentHistory: [],
                            paymentBreakdown: undefined,
                            paymentStatus: undefined,
                            courtPaidPlayersCount: 0,
                            courtPriceNote: undefined
                          };
                        }
                      }

                      // Buscar si hay una solicitud de turno pendiente del jugador para este casillero
                      const pendingRequest = requests.filter(r => !r.depositExpiresAt || r.depositExpiresAt > Date.now()).find(
                        (r) =>
                          r.date === selectedDate &&
                          (r.status === 'pending' || r.status === 'deposit_required') &&
                          ((r.option1.courtName === court && r.option1.timeSlot === time) ||
                            (r.option2 && r.option2.courtName === court && r.option2.timeSlot === time))
                      );

                      // 1. Si hay una reserva real (no promo), mostrarla
                      if (currentBooking && currentBooking.status !== 'promo' && currentBooking.playerName !== 'BLOQUEADO' && currentBooking.playerName !== 'DESBLOQUEADO') {
                        const isFixedMaterialized = currentBooking.isFixed && currentBooking.id.includes('_materialized_');
                        
                        let isPaid = currentBooking.status === 'paid';
                        let isPending = (currentBooking.status === 'pending' || currentBooking.confirmationStatus === 'pending_confirmation') && !currentBooking.playerConfirmed;
                        let isConfirmed = (currentBooking.status === 'confirmed' && currentBooking.confirmationStatus !== 'pending_confirmation') || currentBooking.playerConfirmed;

                        if (isFixedMaterialized) {
                          const baseId = currentBooking.id.split('_materialized_')[0];
                          const baseBooking = bookings.find(root => root.id === baseId);
                          
                          let hasRealPayment = false;
                          if (currentBooking.paymentHistory && currentBooking.paymentHistory.length > 0) {
                            if (!baseBooking || !baseBooking.paymentHistory) {
                              hasRealPayment = true;
                            } else {
                              const baseTimestamps = new Set(baseBooking.paymentHistory.map(h => h.timestamp));
                              const hasNewPayment = currentBooking.paymentHistory.some(h => !baseTimestamps.has(h.timestamp));
                              if (hasNewPayment) {
                                hasRealPayment = true;
                              }
                            }
                          } else if (currentBooking.paymentStatus === 'full_paid' || currentBooking.paymentStatus === 'partial_paid') {
                            if (!baseBooking || currentBooking.paymentStatus !== baseBooking.paymentStatus) {
                              hasRealPayment = true;
                            }
                          }

                          const hasRealConfirmation = currentBooking.playerConfirmed || currentBooking.confirmationStatus === 'confirmed';
                          
                          if (hasRealPayment) {
                            isPaid = true;
                            isPending = false;
                            isConfirmed = false;
                          } else if (hasRealConfirmation) {
                            isPaid = false;
                            isConfirmed = true;
                            isPending = false;
                          } else {
                            isPending = true;
                            isConfirmed = false;
                            isPaid = false;
                          }
                        }

                        const hasItems = !!(currentBooking.items && currentBooking.items.length > 0);

                        let statusBg = 'bg-sky-500/15 border-sky-500/40 text-sky-300 hover:bg-sky-500/25';
                        let statusHoverGlow = 'group-hover/slot-cell:bg-sky-500/35 group-hover/slot-cell:border-sky-400 group-hover/slot-cell:shadow-[0_0_18px_rgba(14,165,233,0.4)] group-hover/slot-cell:text-white';
                        let statusText = (isConfirmed && (hasItems || currentBooking.paymentStatus === 'partial_paid')) ? 'Ticket Abierto' : 'Confirmado';

                        if (isPaid) {
                          statusBg = 'bg-green-500/15 border-green-500/40 text-green-300 hover:bg-green-500/25';
                          statusHoverGlow = 'group-hover/slot-cell:bg-green-500/35 group-hover/slot-cell:border-green-400 group-hover/slot-cell:shadow-[0_0_18px_rgba(34,197,94,0.4)] group-hover/slot-cell:text-white';
                          statusText = 'COBRADO';
                        } else if (isPending) {
                          statusBg = 'bg-yellow-500/15 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/25';
                          statusHoverGlow = 'group-hover/slot-cell:bg-yellow-500/35 group-hover/slot-cell:border-yellow-400 group-hover/slot-cell:shadow-[0_0_18px_rgba(234,179,8,0.4)] group-hover/slot-cell:text-white';
                          statusText = 'PENDIENTE';
                        }

                        return (
                          <td
                            key={court}
                            onClick={() => setSelectedSlot({ court, time, booking: currentBooking })}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({ x: e.clientX, y: e.clientY, cellInfo: { court, time, booking: currentBooking } });
                            }}
                            className="p-0.5 md:p-1.5 border-r border-[#2d313f] last:border-r-0 cursor-pointer transition-all group/slot-cell group-hover/table-row:bg-[#1e2029] relative"
                          >
                            <div className={`h-full min-h-[44px] md:min-h-[64px] border rounded md:rounded-md px-1 md:px-2.5 pt-1.5 md:pt-3 pb-0.5 md:pb-1.5 flex flex-col justify-between items-center md:items-stretch transition-all duration-200 group-hover/slot-cell:scale-[1.02] ${statusBg} ${statusHoverGlow} relative overflow-hidden shadow-sm`}>

                              {/* Detalle superior: Nombre del jugador */}
                              <div className="flex items-start justify-center w-full h-auto mt-1 md:mt-2">
                                <span className="text-[10px] md:text-[12px] font-black tracking-wider break-words whitespace-normal text-wrap w-full block text-white uppercase text-center leading-tight pr-4 md:pr-6 overflow-visible flex flex-col md:flex-row md:items-center justify-center gap-0.5 md:gap-1">
                                  {(currentBooking.paymentStatus === 'partial_paid' || currentBooking.paymentStatus === 'full_paid') && (
                                    <DollarSign size={10} className="text-emerald-400 shrink-0 hidden md:block" />
                                  )}
                                  <span className="flex items-center gap-0.5 w-full justify-center uppercase">
                                    {(currentBooking.paymentStatus === 'partial_paid' || currentBooking.paymentStatus === 'full_paid') && (
                                      <DollarSign size={8} className="text-emerald-400 shrink-0 md:hidden" />
                                    )}
                                    {currentBooking.playerName}
                                  </span>
                                </span>

                                {/* BOTÓN DE FIJO EN ESQUINA SUPERIOR DERECHA */}
                                <button
                                  type="button"
                                  disabled={isPending && currentBooking.isFixed}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmFixedPopup({
                                      booking: currentBooking!,
                                      targetIsFixed: !currentBooking!.isFixed
                                    });
                                  }}
                                  className={`absolute top-0 right-0 h-full flex flex-col justify-center items-center font-black uppercase transition-all z-10 w-5 md:w-6 lg:w-7 ${currentBooking.isFixed
                                      ? 'bg-amber-500/20 text-amber-400 border-l border-amber-500/50 shadow-[-2px_0_8px_rgba(245,158,11,0.2)]'
                                      : 'bg-black/30 text-zinc-400 border-l border-dashed border-zinc-500 hover:text-white hover:border-zinc-300'
                                    } ${isPending && currentBooking.isFixed
                                      ? 'cursor-not-allowed opacity-60'
                                      : 'cursor-pointer hover:bg-black/40'
                                    }`}
                                  title={
                                    isPending && currentBooking.isFixed
                                      ? "No se puede modificar un turno fijo si está Pendiente"
                                      : currentBooking.isFixed
                                        ? "Turno Fijo (Clic para quitar)"
                                        : "Fijar turno"
                                  }
                                >
                                  <Pin size={currentBooking.isFixed ? 12 : 10} className={currentBooking.isFixed ? 'mb-1' : 'rotate-45 mb-1'} />
                                  <span className={`hidden lg:block [writing-mode:vertical-rl] rotate-180 text-[8px] md:text-[9px] ${currentBooking.isFixed ? 'tracking-[0.2em]' : 'tracking-widest'}`}>{currentBooking.isFixed ? 'Fijo' : 'Fijar'}</span>
                                </button>
                              </div>

                              {/* Detalle inferior: Estado (Siempre visible en todas las resoluciones, sin categoría) */}
                              <div className="relative flex items-center justify-center w-full mt-1 md:mt-1.5 pt-1 md:pt-1.5 gap-1.5 pr-4 md:pr-6 before:absolute before:top-0 before:left-[15%] before:right-[20%] before:h-[1px] before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
                                {currentBooking.playerConfirmed && (
                                  <div className="flex items-center justify-center bg-emerald-500/20 rounded-full p-0.5 border border-emerald-500/40 animate-in zoom-in duration-300" title="Asistencia confirmada por el jugador">
                                    <CheckCircle2 size={10} className="text-emerald-400" />
                                  </div>
                                )}
                                <span className={`flex items-center gap-1 font-medium tracking-widest uppercase px-1.5 py-0.5 rounded bg-black/20 border border-white/5 leading-none text-white/50 ${isConfirmed && hasItems ? 'text-[6px] md:text-[7px]' : 'text-[6px] md:text-[7px]'
                                  }`}>
                                  {isConfirmed && hasItems && (
                                    <ShoppingCart size={10} className="text-sky-400 animate-pulse shrink-0" style={{ animationDuration: '1s' }} />
                                  )}
                                  <span>{statusText}</span>
                                </span>
                              </div>

                            </div>
                          </td>
                        );
                      }

                      // 2. Si hay un bloqueo temporal activo (Optimistic Lock), mostrarlo
                      const activeLock = activeLocks.find(
                        (l) => l.courtName === court && l.timeSlot === time && l.date === selectedDate && l.expiresAt > Date.now()
                      );

                      if (activeLock) {
                        return (
                          <td key={court} className="p-0.5 md:p-1.5 border-r border-[#2d313f] last:border-r-0">
                            <div className="h-full min-h-[44px] md:min-h-[64px] border border-orange-500/30 rounded-lg md:rounded-xl p-1 md:p-2.5 flex flex-col justify-between items-center md:items-stretch transition-all duration-200 bg-orange-500/10 text-orange-400 relative shadow-inner text-center animate-pulse">
                              {/* Detalle superior: Nombre de quien reserva */}
                              <div className="flex items-start justify-between w-full h-auto">
                                <span className="text-[8px] md:text-[11px] font-black tracking-tight break-words whitespace-normal text-wrap w-full pr-5 block text-orange-100 text-center md:text-left leading-tight overflow-visible">
                                  {activeLock.lockedByName || 'Jugador'}
                                </span>
                                <Clock size={8} className="text-orange-400 shrink-0 absolute top-1 md:top-2.5 right-1 md:right-2 animate-pulse" />
                              </div>

                              {/* Detalle inferior: Leyenda de Reservando */}
                              <div className="flex items-center justify-center md:justify-end w-full mt-0.5 md:mt-1 pt-0.5 md:pt-1.5 border-t border-orange-500/20">
                                <span className="text-[7px] md:text-[8px] font-black tracking-widest uppercase px-1 md:px-1.5 py-0.5 rounded bg-black/40 border border-orange-500/30 leading-none text-orange-300">
                                  RESERVANDO...
                                </span>
                              </div>
                            </div>
                          </td>
                        );
                      }

                      // 3. Si hay una oferta/plantilla de promoción activa, mostrarla
                      if (currentBooking && currentBooking.status === 'promo') {
                        const hasDrink = currentBooking.promoPerks?.drink;
                        const hasBalls = currentBooking.promoPerks?.balls;
                        const hasWinnerPrize = currentBooking.promoPerks?.winnerPrize;
                        const discount = currentBooking.promoDiscountPercentage || 0;

                        return (
                          <td
                            key={court}
                            onClick={() => setSelectedSlot({ court, time, booking: currentBooking })}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({ x: e.clientX, y: e.clientY, cellInfo: { court, time, booking: currentBooking } });
                            }}
                            className="p-0.5 md:p-1.5 border-r border-[#2d313f] last:border-r-0 cursor-pointer transition-all group/slot-cell group-hover/table-row:bg-[#1e2029] relative"
                          >
                            <div className="h-full min-h-[44px] md:min-h-[64px] border border-dashed border-purple-500/40 bg-purple-500/5 hover:bg-purple-500/15 hover:border-purple-400 rounded-lg md:rounded-xl p-1 flex flex-col justify-between items-center transition-all duration-200 group-hover/slot-cell:scale-[1.02] shadow-sm relative overflow-hidden opacity-60 group-hover/slot-cell:opacity-100">
                                <div className="flex-1 flex flex-col items-center justify-center w-full gap-1">
                                  <span className="text-[8px] md:text-[9px] font-black uppercase tracking-widest bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(168,85,247,0.6)] flex items-center gap-1">
                                    PROMO {discount > 0 ? `${discount}%` : ''}
                                  </span>
                                  {(hasDrink || hasBalls || hasWinnerPrize) && (
                                    <div className="flex items-center justify-center gap-1 flex-wrap">
                                      {hasDrink && (
                                  <span className="flex items-center justify-center w-3.5 h-3.5 md:w-4 md:h-4 bg-purple-950/40 border border-purple-500/20 rounded-full text-[8px]" title="Bebida de cortesía">
                                    🥤
                                  </span>
                                )}
                                {hasBalls && (
                                  <span className="flex items-center justify-center w-3.5 h-3.5 md:w-4 md:h-4 bg-purple-950/40 border border-purple-500/20 rounded-full text-[8px]" title="Pelotas nuevas">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" className="drop-shadow-md md:w-[10px] md:h-[10px]"><circle cx="12" cy="12" r="10" fill="#ccff00" stroke="#9acd00" strokeWidth="1" /><path d="M6 5.3a9 9 0 0 1 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" /><path d="M18 5.3a9 9 0 0 0 0 13.4" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" /></svg>
                                  </span>
                                )}
                                {hasWinnerPrize && (
                                  <span className="flex items-center justify-center w-3.5 h-3.5 md:w-4 md:h-4 bg-purple-950/40 border border-purple-500/20 rounded-full text-[8px]" title="Premio al ganador">
                                    🏆
                                  </span>
                                )}
                                    </div>
                                  )}
                                </div>
                                
                            </div>
                          </td>
                        );
                      }

                      // Si no hay reserva pero hay una solicitud pendiente, mostrarla en amarillo
                      if (pendingRequest) {
                        return (
                          <td
                            key={court}
                            onClick={() => {
                              setLocalHighlightId(pendingRequest.id);
                              setMobileRequestsOpen(true);

                              setTimeout(() => {
                                const el = document.getElementById(`request-card-${pendingRequest.id}`) || document.getElementById(`request-card-mobile-${pendingRequest.id}`);
                                if (el) {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                              }, 150);
                            }}
                            className="p-0.5 md:p-1.5 border-r border-[#2d313f] last:border-r-0 cursor-pointer transition-all group/slot-cell group-hover/table-row:bg-[#1e2029]"
                          >
                            <div className="h-full min-h-[44px] md:min-h-[64px] border rounded-lg md:rounded-xl p-1 md:p-2.5 flex flex-col justify-between items-center md:items-stretch transition-all duration-200 group-hover/slot-cell:scale-[1.02] bg-amber-500/15 border-amber-500/40 text-amber-300 group-hover/slot-cell:bg-amber-500/35 group-hover/slot-cell:border-amber-400 group-hover/slot-cell:shadow-[0_0_18px_rgba(245,158,11,0.4)] group-hover/slot-cell:text-white relative overflow-hidden shadow-sm">

                              {/* Detalle superior: Nombre del jugador */}
                              <div className="flex items-start justify-between w-full h-auto">
                                <span className="text-[8px] md:text-[11px] font-black tracking-tight break-words whitespace-normal text-wrap w-full pr-5 block text-amber-100 text-center md:text-left leading-tight group-hover/slot-cell:text-white overflow-visible">
                                  {pendingRequest.playerName}
                                </span>
                                <Clock size={8} className="text-amber-400 shrink-0 absolute top-1 md:top-2.5 right-1 md:right-2 animate-pulse" />
                              </div>

                              {/* Detalle inferior: Leyenda de Solicitud */}
                              <div className="flex items-center justify-center md:justify-end w-full mt-0.5 md:mt-1 pt-0.5 md:pt-1.5 border-t border-amber-500/20">
                                <span className="text-[7px] md:text-[8px] font-black tracking-widest uppercase px-1 md:px-1.5 py-0.5 rounded bg-black/40 border border-[#FFE600]/30 leading-none text-amber-300 group-hover/slot-cell:text-white">
                                  SOLICITUD
                                </span>
                              </div>

                            </div>
                          </td>
                        );
                      }

                      // Si no hay reserva ni solicitud, verificar si este slot está inhabilitado por un OffDay (por cancha o todo el club)
                      const blockedInfo = checkIfSlotBlockedByOffDay(
                        schedule?.offDays || setupOffDays,
                        selectedDate,
                        court,
                        time,
                        new Date(selectedDate + 'T00:00:00').getDay()
                      );

                      if (currentBooking && currentBooking.playerName === 'BLOQUEADO') {
                        blockedInfo.isBlocked = true;
                        blockedInfo.reason = currentBooking.notes || '';
                      }
                      if (currentBooking && currentBooking.playerName === 'DESBLOQUEADO') {
                        blockedInfo.isBlocked = false;
                      }

                      if (blockedInfo.isBlocked) {
                        const isManualBlock = currentBooking && currentBooking.playerName === 'BLOQUEADO';
                        return (
                          <td 
                            key={court} 
                            className={`p-0.5 md:p-1.5 border-r border-[#2d313f] last:border-r-0 cursor-not-allowed`}
                          >
                            <div className="group h-full min-h-[44px] md:min-h-[64px] border border-zinc-900/60 rounded-lg md:rounded-xl p-1 md:p-2 flex flex-col justify-center items-center bg-black/60 text-zinc-500 shadow-inner relative overflow-hidden">
                              <span className="text-[7px] md:text-[8px] font-bold tracking-wider uppercase text-zinc-600 text-center">INHABILITADA</span>
                              {blockedInfo.reason && (
                                <span className="text-[9px] md:text-[11px] font-black text-zinc-400 truncate max-w-full text-center mt-0.5" title={blockedInfo.reason}>
                                  {blockedInfo.reason}
                                </span>
                              )}
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isManualBlock) {
                                    setFastUnblockConfirm(currentBooking);
                                  } else {
                                    setFastUnblockConfirm({ id: 'config_block', courtName: court, timeSlot: time, isConfigBlock: true } as any);
                                  }
                                }}
                                className="absolute top-1 right-1 md:top-1.5 md:right-1.5 opacity-100 transition-opacity bg-black/80 rounded-full p-1 border border-zinc-700/50 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/50 cursor-pointer"
                                title="Desbloquear casillero"
                              >
                                <X size={10} className="stroke-[3]" />
                              </button>
                            </div>
                          </td>
                        );
                      }

                      // Celda Libre
                        const slotDate = selectedDate;
                        const slotCourt = court;
                        const slotTime = time;
                        const discounts = schedule?.timeDiscounts || setupTimeDiscounts || [];
                        let activeDiscount = undefined;

                        if (discounts.length && slotDate && slotCourt && slotTime) {
                          const d = new Date(slotDate + 'T00:00:00');
                          const dayOfWeek = d.getDay();

                          activeDiscount = discounts.find(td => {
                            if (!td.daysOfWeek?.includes(dayOfWeek)) return false;
                            if (td.courts && td.courts.length > 0 && !td.courts.includes(slotCourt)) return false;
                            return slotTime >= td.startHour && slotTime < td.endHour;
                          });
                        }

                        return (
                          <td
                            key={court}
                            onClick={() => {
                              const basePrice = schedule?.pricePerSlot || 12000;
                              let finalPrice = basePrice;
                              if (activeDiscount) {
                                if (activeDiscount.discountType === 'percentage') {
                                  finalPrice = Math.max(0, basePrice * (1 - activeDiscount.value / 100));
                                } else {
                                  finalPrice = Math.max(0, basePrice - activeDiscount.value);
                                }
                              }
                              setSelectedSlot({ court, time });
                              setManualPrice(finalPrice);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({ x: e.clientX, y: e.clientY, cellInfo: { court, time } });
                            }}
                            className="p-0.5 md:p-1.5 border-r border-[#2d313f] last:border-r-0 cursor-pointer bg-[#1a1c23] group/slot-cell group-hover/table-row:bg-[#1e2029] transition-all relative"
                          >
                            <div className={`h-full min-h-[44px] md:min-h-[64px] border border-dashed border-[#2d313f] group-hover/slot-cell:border-white/35 group-hover/slot-cell:border-solid group-hover/slot-cell:bg-white/[0.07] group-hover/slot-cell:scale-[1.02] rounded-lg md:rounded-xl flex items-center justify-center text-zinc-600 group-hover/slot-cell:text-white transition-all duration-200 shadow-sm shadow-transparent group-hover/slot-cell:shadow-[0_4px_15px_rgba(255,255,255,0.06)] overflow-hidden ${activeDiscount ? 'opacity-60 group-hover/slot-cell:opacity-100' : ''}`}>
                              {activeDiscount ? (
                                <span className="text-[8px] md:text-[9px] font-black uppercase tracking-widest bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(168,85,247,0.6)] flex items-center gap-1">
                                  PROMO {activeDiscount.discountType === 'percentage' ? `${activeDiscount.value}%` : `-${activeDiscount.value}`}
                                </span>
                              ) : (
                                <Plus size={14} className="md:w-[18px] md:h-[18px] transition-transform duration-200 group-hover/slot-cell:scale-110" />
                              )}
                            </div>
                          </td>
                        );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* --- PANEL LATERAL DE SOLICITUDES (Oculto en movil) --- */}
        <div
          id="admin-pending-requests-section"
          className={`hidden lg:flex lg:flex-col lg:col-span-5 xl:col-span-4 bg-[#1a1c23] rounded-2xl overflow-hidden p-4 gap-4 shadow-xl border h-full ${requests.length > 0
            ? 'border-theme-primary/30 shadow-[0_4px_20px_rgba(var(--glow-rgb),0.05)]'
            : 'border-[#2d313f]'
            }`}>
          <div className="flex items-center justify-between border-b border-[#2d313f] pb-3 shrink-0">
            <h3 className="text-xs font-black uppercase tracking-widest text-zinc-200 flex items-center justify-center gap-2 flex-1">
              {requests.length > 0 ? (
                <span className="relative flex h-3 w-3 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C5FF4A] opacity-80"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#C5FF4A] shadow-[0_0_10px_#C5FF4A]"></span>
                </span>
              ) : (
                <span className="w-2.5 h-2.5 rounded-full bg-zinc-600 shrink-0" />
              )}
              Solicitudes de Reservas ({requests.length})
            </h3>
          </div>

          <div className="flex-1 min-h-0 space-y-3.5 overflow-y-auto px-2 py-2">
            {requests.length === 0 ? (
              <div className="text-center py-10 text-white/30 space-y-2">
                <CheckCircle2 size={32} className="mx-auto text-white/10" />
                <p className="text-xs font-medium">No hay solicitudes pendientes</p>
              </div>
            ) : (
              requests.map((req) => {
                const isHighlighted = localHighlightId === req.id;
                return (
                  <div
                    key={req.id}
                    id={`request-card-${req.id}`}
                    className={`relative bg-[#222530] border rounded-xl p-3.5 space-y-3 transition-all duration-300 shadow-md ${isHighlighted
                      ? 'border-yellow-500 ring-1 ring-yellow-500/50 scale-[1.02] shadow-[0_0_15px_rgba(234,179,8,0.2)]'
                      : 'border-[#2d313f] hover:border-theme-primary/30'
                      }`}
                  >
                    {/* Jugador Info */}
                    <div className="flex items-center gap-2.5">
                      {req.playerPhoto ? (
                        <img 
                          src={req.playerPhoto} 
                          alt={req.playerName} 
                          className="w-8 h-8 rounded-full object-cover border border-[#2d313f] cursor-pointer hover:border-theme-primary transition-colors hover:scale-105 shrink-0" 
                          onClick={(e) => {
                            e.stopPropagation();
                            const p = players.find(x => x.id === req.playerId);
                            if (p) setSelectedPlayerForDetail(p);
                          }}
                        />
                      ) : (
                        <div 
                          className="w-8 h-8 rounded-full bg-[#1e202b] flex items-center justify-center border border-[#2d313f] cursor-pointer hover:border-theme-primary transition-colors hover:scale-105 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            const p = players.find(x => x.id === req.playerId);
                            if (p) setSelectedPlayerForDetail(p);
                          }}
                        >
                          <User size={14} className="text-zinc-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className="text-sm font-black text-white truncate leading-tight">{req.playerName}</h4>
                          {req.playerId && (
                            <button
                              onClick={() => {
                                onOpenChatWithPlayer?.(req.playerId, req.playerName || 'Jugador', req.playerPhoto || '');
                              }}
                              className="bg-[#2d313f]/60 hover:bg-[#393d4f] text-white border border-[#3e4357] p-1.5 rounded-lg flex items-center justify-center transition-all shadow-sm shrink-0"
                              title="Chatear con jugador"
                            >
                              <MessageSquare size={13} />
                            </button>
                          )}
                        </div>
                        {(() => {
                           const reqPlayer = players.find(p => p.id === req.playerId);
                           const phone = reqPlayer?.phone || (req as any).contactInfo;
                           if (!phone) return null;
                           return (
                             <a 
                                href={`https://wa.me/${phone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-white hover:text-green-400 truncate flex items-center gap-1 transition-all hover:scale-105 font-bold shrink-0 ml-2"
                              >
                                <div className="text-green-500">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                                </div>
                                {phone}
                              </a>
                           )
                        })()}
                      </div>
                    </div>

                    {/* Detalle del Turno Solicitado */}
                    {(() => {
                      const fullDateStr = formatRequestDate(req.date);
                      let tag = '';
                      let displayDate = fullDateStr;
                      if (fullDateStr.startsWith('Hoy (')) {
                        tag = 'HOY';
                        displayDate = fullDateStr.replace('Hoy (', '').replace(')', '');
                      } else if (fullDateStr.startsWith('Mañana (')) {
                        tag = 'MAÑANA';
                        displayDate = fullDateStr.replace('Mañana (', '').replace(')', '');
                      }

                      const [dayName, datePart] = displayDate.split(', ');

                      return (
                        <div className="flex flex-col border-t border-[#2d313f]/60 pt-3 pb-1">
                          <div className="flex items-center justify-between w-full px-1 gap-2">
                            {/* Left: Tag + Date */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {tag && (
                                <span className="text-[9px] font-black uppercase text-white px-1.5 py-0.5 rounded border border-white/60 shrink-0">{tag}</span>
                              )}
                              <div className="flex flex-col min-w-0">
                                <span className="text-[13px] font-black text-white leading-tight capitalize truncate">{dayName}</span>
                                {datePart && <span className="text-[10px] font-black text-white/75 leading-tight truncate">{datePart}</span>}
                              </div>
                            </div>
                            
                            {/* Center: Court */}
                            <div className="flex items-center justify-center shrink-0 px-2">
                              <span className="text-[11px] text-zinc-400 font-bold truncate">{req.option1.courtName}</span>
                            </div>
                            
                            {/* Right: Time */}
                            <div className="flex-1 flex justify-end min-w-0">
                              <span className="text-[13px] font-black text-white bg-[#1a1c23] border border-[#2d313f] px-3 py-1.5 rounded-lg shrink-0">{req.option1.timeSlot}h</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {req.notes && (
                      <p className="text-[10px] text-zinc-300 italic leading-snug bg-[#15171d] p-2 rounded-lg border border-[#2d313f]">
                        "{req.notes}"
                      </p>
                    )}
                                        {/* Acciones */}
                    {req.status === 'deposit_required' ? (
                      <div className="flex flex-col gap-2 border-t border-[#2d313f] pt-2.5 w-full items-center">
                        <div className="bg-sky-500/10 text-sky-400 border border-sky-500/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider text-center w-full relative flex flex-col items-center justify-center">
                          <span>Esperando pago de seña ({req.requiredDepositPercentage}%)</span>
                          {req.depositExpiresAt && <CountdownText expiresAt={req.depositExpiresAt} />}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (await window.confirm('¿Cancelar el requerimiento de seña y volver a estado pendiente?')) {
                                handleCancelDeposit(req);
                              }
                            }}
                            className="absolute right-2 p-1 hover:bg-sky-500/20 rounded-full transition-colors text-sky-400/70 hover:text-sky-300"
                            title="Cancelar requerimiento de seña"
                          >
                            <X size={12} strokeWidth={3} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 border-t border-[#2d313f] pt-2.5 w-full">
                        <button
                          onClick={async () => {
                            if (await window.confirm(`¿Estás seguro de que deseas ACEPTAR la solicitud de ${req.playerName} para el ${formatRequestDate(req.date)} a las ${req.option1.timeSlot}hs?`)) {
                              handleResolveRequest(req, 'option1', requestNotes[req.id]);
                            }
                          }}
                          className="w-full bg-[#C5FF4A]/15 hover:bg-[#C5FF4A]/25 text-[#C5FF4A] border border-[#C5FF4A]/30 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-center cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                        >
                          Aceptar
                        </button>

                        <div className="flex items-center gap-2 w-full">
                          <div className="relative flex-1">
                            <button
                              onClick={() => setDepositDropdownOpen(depositDropdownOpen === req.id ? null : req.id)}
                              className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-center cursor-pointer flex items-center justify-center gap-1 hover:scale-[1.01] active:scale-[0.99]"
                            >
                              <DollarSign size={12} /> Requerir Seña {depositDropdownOpen === req.id ? <ChevronDown size={12} className="rotate-180" /> : <ChevronDown size={12} />}
                            </button>
                          
                            {depositDropdownOpen === req.id && (
                              <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#1a1c23] border border-[#2d313f] rounded-lg shadow-xl overflow-hidden z-20 flex flex-col animate-in slide-in-from-bottom-2 duration-200">
                                {[25, 50, 100].map((perc) => (
                                  <button
                                    key={perc}
                                    onClick={async () => {
                                        if (await window.confirm(`¿Estás seguro de que deseas requerir una seña del ${perc}% al jugador para confirmar este turno?`)) {
                                          handleRequireDeposit(req, 'option1', perc as 25 | 50 | 100);
                                          setDepositDropdownOpen(null);
                                        }
                                      }}
                                    className="px-3 py-2 text-xs font-bold text-white hover:bg-[#2d313f] text-left flex items-center justify-between border-b border-[#2d313f] last:border-0"
                                  >
                                    <span>Requerir {perc}%</span>
                                    <span className="text-[10px] text-zinc-500 font-medium">del total</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <button
                            onClick={async () => {
                              if (await window.confirm(`¿Estás seguro de que deseas RECHAZAR la solicitud de ${req.playerName} para el ${formatRequestDate(req.date)}?`)) {
                                handleResolveRequest(req, 'reject', requestNotes[req.id]);
                              }
                            }}
                            className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all text-center cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
                          >
                            Rechazar
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })
            )}
          </div>

        </div>

      </div>

      {/* MODAL POPUP: CONFIRMACIÓN DE TURNO FIJO */}
      {confirmFixedPopup && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-300"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setConfirmFixedPopup(null);
            }
          }}
        >
          <div className="bg-[#1a1c23] border border-[#FFD700]/40 rounded-xl p-4 max-w-[280px] w-full shadow-[0_10px_40px_rgba(255,215,0,0.15)] flex flex-col items-center text-center animate-in zoom-in-95 duration-300">
            <div className="w-10 h-10 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 flex items-center justify-center mb-2.5">
              <Pin className={`text-[#FFD700] ${confirmFixedPopup.targetIsFixed ? 'rotate-45' : ''}`} size={18} />
            </div>

            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-1.5">
              {confirmFixedPopup.targetIsFixed ? '¿Fijar Turno?' : '¿Quitar Turno Fijo?'}
            </h3>

            <p className="text-[10px] text-zinc-400 mb-5 px-1 font-medium leading-relaxed">
              {confirmFixedPopup.targetIsFixed
                ? `Estás por fijar el turno de ${confirmFixedPopup.booking.playerName} todos los ${new Date(confirmFixedPopup.booking.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' })} a las ${confirmFixedPopup.booking.timeSlot}hs.`
                : '¡Atención! Al quitar este turno fijo se cancelarán automáticamente todos los turnos futuros de esta serie.'}
            </p>

            <div className="flex gap-2.5 w-full">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmFixedPopup(null);
                }}
                className="flex-1 bg-[#2a2d3a] hover:bg-[#34384a] text-white font-black text-[10px] py-2.5 rounded-lg transition-all cursor-pointer uppercase tracking-wider border border-white/10 active:scale-95"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  const popup = confirmFixedPopup;

                  // Validación de colisiones para turnos fijos
                  if (popup.targetIsFixed) {
                    try {
                        const projectedDates: string[] = [popup.booking.date];
                        const dateObj = new Date(popup.booking.date + 'T12:00:00');
                        const selectedDayOfWeek = dateObj.getDay();

                        for (let i = 1; i <= 8; i++) {
                          dateObj.setDate(dateObj.getDate() + 7);
                          projectedDates.push(dateObj.toISOString().split('T')[0]);
                        }
                        
                        // 1. Conflictos con turnos fijos
                        const fixedConflicts = bookings.filter(b => {
                          if (b.id === popup.booking.id) return false;
                          if (b.courtName !== popup.booking.courtName || b.timeSlot !== popup.booking.timeSlot) return false;
                          if (b.status === 'cancelled' || b.hiddenFromGrid) return false;
                          if (!['confirmed', 'paid', 'pending'].includes(b.status)) return false;
                          
                          if (b.isFixed) {
                             const bDateObj = new Date(b.date + 'T12:00:00');
                             if (bDateObj.getDay() === selectedDayOfWeek) {
                                 return projectedDates.some(d => {
                                     if (d < b.date) return false;
                                     return !b.excludedDates || !b.excludedDates.includes(d);
                                 });
                             }
                          }
                          return false;
                        });

                        if (fixedConflicts.length > 0) {
                          const conflict = fixedConflicts[0];
                          const [y, m, d] = conflict.date.split('-');
                          setConfirmFixedPopup(null);
                          setErrorModalMessage({
                            title: "COLISIÓN DE TURNOS",
                            message: `No se puede fijar este turno. Este horario choca con la serie de turnos fijos a nombre de ${conflict.playerName}, iniciada el ${d}/${m}/${y}.\n\nIntenta buscar otro horario o cancela la reserva futura primero.`
                          });
                          return;
                        }

                        // 2. Conflictos con turnos eventuales
                        const futureDocsQuery = query(
                          collection(db, getCollectionPath('bookings')),
                          where('clubId', '==', userId),
                          where('date', 'in', projectedDates)
                        );
                        const futureSnap = await getDocs(futureDocsQuery);
                        
                        let eventualConflict: Booking | null = null;
                        futureSnap.forEach(docSnap => {
                          if (eventualConflict) return;
                          
                          const b = docSnap.data() as Booking;
                          if (b.id === popup.booking.id || docSnap.id === popup.booking.id) return;
                          if (b.courtName !== popup.booking.courtName || b.timeSlot !== popup.booking.timeSlot) return;
                          if (b.status === 'cancelled' || b.hiddenFromGrid) return;
                          if (!['confirmed', 'paid', 'pending'].includes(b.status)) return;
                          if (b.isFixed) return;
                          
                          if (!b.excludedDates || !b.excludedDates.includes(b.date)) {
                             eventualConflict = b;
                          }
                        });

                        if (eventualConflict) {
                          const conflict = eventualConflict as Booking;
                          const [y, m, d] = conflict.date.split('-');
                          setConfirmFixedPopup(null);
                          setErrorModalMessage({
                            title: "COLISIÓN DE TURNOS",
                            message: `No se puede fijar este turno. Este horario choca con una reserva existente el día ${d}/${m}/${y} a nombre de ${conflict.playerName}.\n\nIntenta buscar otro horario o cancela la reserva futura primero.`
                          });
                          return;
                        }
                    } catch (err: any) {
                        console.error("🔥 ERROR AL VALIDAR FIJACIÓN:", err);
                        setConfirmFixedPopup(null);
                        setErrorModalMessage({
                          title: "ERROR DE VALIDACIÓN",
                          message: "Ocurrió un error al validar la disponibilidad en las próximas semanas. Por favor, revisa la consola o intenta de nuevo."
                        });
                        return;
                    }
                  }

                  setConfirmFixedPopup(null);

                  const updateData: any = {
                    isFixed: popup.targetIsFixed,
                    confirmationStatus: popup.targetIsFixed ? 'confirmed' : popup.booking.confirmationStatus
                  };

                  // Si estamos volviendo a fijar, generamos un nuevo ID de serie 
                  // para romper el vínculo con cancelaciones previas y que el banner aparezca
                  if (popup.targetIsFixed) {
                    updateData.seriesId = `series_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
                    updateData.originalBookingId = popup.booking.id;
                      if (popup.booking.id.includes('_materialized_')) {
                        updateData.isFixedRoot = true;
                      }

                    // Limpieza preventiva: eliminamos proyecciones o cancelaciones futuras del mismo slot
                    // para que la nueva serie proyecte libremente.
                    for (const b of bookings) {
                      if (b.courtName === popup.booking.courtName && b.timeSlot === popup.booking.timeSlot && b.date > popup.booking.date) {
                        if (b.id.includes('_projected_') || b.status === 'cancelled') {
                          await deleteDoc(doc(db, getCollectionPath('bookings'), b.id));
                        }
                      }
                    }
                  }

                  await ensureMaterialized(popup.booking, updateData);

                  if (!popup.targetIsFixed) {
                      const targetDateObj = new Date(popup.booking.date + 'T12:00:00');
                      const targetDayOfWeek = targetDateObj.getDay();

                      for (const b of bookings) {
                        const bDateObj = new Date(b.date + 'T12:00:00');
                        const bDayOfWeek = bDateObj.getDay();

                        const isSameCourtAndTime = b.courtName === popup.booking.courtName && b.timeSlot === popup.booking.timeSlot;
                        const isSameDayOfWeek = bDayOfWeek === targetDayOfWeek;
                        const isSameSeriesOrPlayer = (b.seriesId && b.seriesId === popup.booking.seriesId) || (!b.seriesId && b.playerName === popup.booking.playerName);

                        if (isSameCourtAndTime && isSameDayOfWeek && isSameSeriesOrPlayer) {
                          if (b.date > popup.booking.date) {
                            if (b.status === 'pending' || b.confirmationStatus === 'pending_confirmation') {
                              if (!b.id.includes('_projected_')) {
                                await deleteDoc(doc(db, getCollectionPath('bookings'), b.id));
                              }
                            } else {
                              if (b.isFixed && !b.id.includes('_projected_')) {
                                await updateDoc(doc(db, getCollectionPath('bookings'), b.id), { isFixed: false });
                              }
                            }
                          } else if (b.date < popup.booking.date) {
                            if (b.isFixed && !b.id.includes('_projected_')) {
                              await updateDoc(doc(db, getCollectionPath('bookings'), b.id), { isFixed: false });
                            }
                          }
                      }
                    }
                  }

                  await logActivity(
                    db,
                    getCollectionPath,
                    userId,
                    activeUserName,
                    userRole || 'admin',
                    popup.targetIsFixed
                      ? `Turno fijado semanalmente - Cancha: ${popup.booking.courtName}, Horario: ${popup.booking.timeSlot}hs, Titular: ${popup.booking.playerName}`
                      : `Turno fijo removido (ahora turno único) - Cancha: ${popup.booking.courtName}, Horario: ${popup.booking.timeSlot}hs, Titular: ${popup.booking.playerName}`
                  );

                  // Enviar notificación push al jugador si tiene playerId
                  if (popup.booking.playerId) {
                    const diaSemana = new Date(popup.booking.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' });
                    const notifTitle = popup.targetIsFixed ? '¡Turno Fijo Asignado!' : 'Baja de Turno Fijo';
                    const notifBody = popup.targetIsFixed
                      ? `Hola ${popup.booking.playerName}, el club ${clubName} te ha asignado un turno fijo todos los ${diaSemana} a las ${popup.booking.timeSlot} hs en ${popup.booking.courtName}.`
                      : `Hola ${popup.booking.playerName}, el club ${clubName} te ha dado de baja el turno fijo de los ${diaSemana} a las ${popup.booking.timeSlot} hs. Los turnos futuros fueron cancelados.`;

                    await addDoc(collection(db, getCollectionPath('notifications')), {
                      userId: popup.booking.playerId,
                      title: clubName,
                      body: notifBody,
                      type: popup.targetIsFixed ? 'fixed_booking_assigned' : 'fixed_booking_removed',
                      createdAt: Date.now(),
                      url: '/?tab=reservas&subtab=mis-turnos'
                    });
                  }
                }}
                className={`flex-1 ${confirmFixedPopup.targetIsFixed ? 'bg-[#FFD700] hover:bg-[#e5c100] shadow-[0_0_15px_rgba(255,215,0,0.4)]' : 'bg-red-500 hover:bg-red-400 shadow-[0_0_15px_rgba(239,68,68,0.4)]'} text-black font-black text-[10px] py-2.5 rounded-lg transition-all cursor-pointer uppercase tracking-wider active:scale-95`}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <AdminBookingDetailModal
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        setSelectedPlayer={setSelectedPlayer}
        setSearchPlayerQuery={setSearchPlayerQuery}
        manualPlayerName={manualPlayerName}
        setFormResetKey={setFormResetKey}
        setShowPlayerDropdown={setShowPlayerDropdown}
        setIsEditingPrice={setIsEditingPrice}
        setShowCancelConfirmPopup={setShowCancelConfirmPopup}
        activeBooking={activeBooking}
        getShortDayOfWeek={getShortDayOfWeek}
        isNextImmediatePendingTurn={isNextImmediatePendingTurn}
        schedule={schedule}
        handleSendConfirmationRequest={handleSendConfirmationRequest}
        setShowConfirmAttendancePopup={setShowConfirmAttendancePopup}
        showConfirmAttendancePopup={showConfirmAttendancePopup}
        handleConfirmFixedBooking={handleConfirmFixedBooking}
        onOpenChatWithPlayer={onOpenChatWithPlayer}
        players={players}
        handleUpdateBookingNotes={handleUpdateBookingNotes}
        showCobradoToast={showCobradoToast}
        showCancelConfirmPopup={showCancelConfirmPopup}
        executeDeleteBooking={executeDeleteBooking}
        setChargePaymentTotal={setChargePaymentTotal}
        currentSlotBookingTotal={currentSlotBookingTotal}
        setChargePaymentCash={setChargePaymentCash}
        setChargePaymentTransfer={setChargePaymentTransfer}
        setShowChargeConfirmModal={setShowChargeConfirmModal}
        handleUnmarkPaid={handleUnmarkPaid}
        showItemDropdown={showItemDropdown}
        setShowItemDropdown={setShowItemDropdown}
        availableProducts={availableProducts}
        handleDirectAddToCart={handleDirectAddToCart}
        handleRemoveFromTicket={handleRemoveFromTicket}
        setShowQuickItemModal={setShowQuickItemModal}
        showQuickItemModal={showQuickItemModal}
        quickItemName={quickItemName}
        setQuickItemName={setQuickItemName}
        quickItemPrice={quickItemPrice}
        setQuickItemPrice={setQuickItemPrice}
        setQuickItemQuantity={setQuickItemQuantity}
        quickItemQuantity={quickItemQuantity}
        handleAddQuickItem={handleAddQuickItem}
        isEditingDiscount={isEditingDiscount}
        tempDiscount={tempDiscount}
        handleSaveDiscountPercent={handleSaveDiscountPercent}
        setIsEditingDiscount={setIsEditingDiscount}
        setTempDiscount={setTempDiscount}
        currentDiscountPercent={currentDiscountPercent}
        handleUpdateCourtPaidPlayers={handleUpdateCourtPaidPlayers}
        tempCourtSelect={tempCourtSelect}
        isEditingPrice={isEditingPrice}
        tempPrice={tempPrice}
        handleUpdateBookingPrice={handleUpdateBookingPrice}
        setTempPrice={setTempPrice}
        bookingItems={bookingItems}
        ensureMaterialized={ensureMaterialized}
        handleUpdateItemQuantity={handleUpdateItemQuantity}
        handleUpdateItemPaidCount={handleUpdateItemPaidCount}
        tempItemSelect={tempItemSelect}
        handleRemoveProductFromSlot={handleRemoveProductFromSlot}
        currentSlotPaidTotal={currentSlotPaidTotal}
        currentSlotRemainingTotal={currentSlotRemainingTotal}
        setModalActiveTab={setModalActiveTab}
        modalActiveTab={modalActiveTab}
        selectedPlayer={selectedPlayer}
        searchPlayerQuery={searchPlayerQuery}
        filteredPlayers={filteredPlayers}
        showPlayerDropdown={showPlayerDropdown}
        formResetKey={formResetKey}
        manualPlayerPhone={manualPlayerPhone}
        setManualIsFixed={setManualIsFixed}
        manualIsFixed={manualIsFixed}
        manualNotes={manualNotes}
        handleAssignBooking={handleAssignBooking}
        promoDiscount={promoDiscount}
        setPromoDiscount={setPromoDiscount}
        promoDrink={promoDrink}
        setPromoDrink={setPromoDrink}
        promoBalls={promoBalls}
        setPromoBalls={setPromoBalls}
        promoWinnerPrize={promoWinnerPrize}
        setPromoWinnerPrize={setPromoWinnerPrize}
        handleDeletePromo={handleDeletePromo}
        handleSavePromo={handleSavePromo}
      />


      {/* ========================================================================= */}
      {/* MODAL HISTORIAL DE CAJA REGISTRADORA Y TRANSACCIONES DEL CLUB */}
      {/* ========================================================================= */}
      {isCashModalOpen && typeof document !== 'undefined' ? createPortal(
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-3 md:p-6 bg-black/50 backdrop-blur-sm animate-fade-in">
          {/* Fondo decorativo con luces difusas (Glows premium) */}
          <div className="absolute top-[-80px] left-[-80px] w-72 h-72 bg-[#C5FF4A]/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-[-80px] right-[-80px] w-72 h-72 bg-emerald-500/8 rounded-full blur-[120px] pointer-events-none" />

          {/* Contenedor principal de la ventana */}
          <div className="bg-[#16181f] border border-[#2d313f]/80 rounded-[24px] md:rounded-[32px] w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl relative overflow-hidden animate-scale-up">

            {/* Cabecera del Ledger / Modal */}
            <div className="p-5 md:p-6 border-b border-[#2d313f]/50 flex flex-col md:flex-row items-center justify-between bg-[#1a1c25]/80 backdrop-blur-sm z-10 relative gap-4">
              <h3 className="text-base md:text-xl font-black uppercase tracking-wider text-white flex items-center gap-2 italic">
                <DollarSign className="text-[#C5FF4A] animate-pulse" size={22} />
                <span>Movimientos de Caja</span>
              </h3>
              
              <div className="flex items-center gap-3">
                {activeCashSession ? (
                  <div className="flex items-center gap-4">
                    <div className="hidden md:flex flex-col text-right">
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">
                        Inicio: {new Date(activeCashSession.openedAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
                      </span>
                      <span className="text-xs text-white font-black">
                        Ef. Inicial: ${activeCashSession.initialCash.toLocaleString('es-AR')}
                      </span>
                    </div>
                    <button 
                      onClick={() => setShowCloseCashModal(true)}
                      className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer"
                    >
                      <LogOut size={14} /> Cerrar Turno
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setShowOpenCashModal(true)}
                    className="flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer"
                  >
                    <PlusCircle size={14} /> Abrir Caja
                  </button>
                )}
                
                <button
                  onClick={() => setIsCashModalOpen(false)}
                  className="p-1.5 md:p-2 bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-400 hover:text-white rounded-xl transition-all cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Pestañas de Navegación */}
            <div className="flex border-b border-[#2d313f]/40 bg-[#16181f]">
              <button
                onClick={() => setCashTab('movimientos')}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-wider transition-all ${cashTab === 'movimientos' ? 'text-[#C5FF4A] border-b-2 border-[#C5FF4A] bg-[#C5FF4A]/5' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                Transacciones Diarias
              </button>
              <button
                onClick={() => setCashTab('historial')}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-wider transition-all ${cashTab === 'historial' ? 'text-[#C5FF4A] border-b-2 border-[#C5FF4A] bg-[#C5FF4A]/5' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                Historial de Arqueos
              </button>
            </div>

            {cashTab === 'movimientos' && (
              <>
            {/* Panel de Filtros y Estadisticas Intermedias */}
            <div className="p-5 md:p-6 bg-[#13151b] border-b border-[#2d313f]/40 space-y-4">

              {/* Tarjetas de mini-KPIs internos de Caja */}
              {!['empleado', 'gestor_turnos'].includes(userRole || '') && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4">
                  <div className="bg-[#1a1c23] border border-[#2d313f]/50 rounded-xl md:rounded-2xl p-2.5 md:p-4 flex flex-col justify-center items-center text-center">
                    <span className="text-[7.5px] md:text-[9px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">Total Recaudado</span>
                    <span className="text-xs sm:text-base md:text-lg lg:text-xl font-black text-emerald-400 mt-1">${cashTotals.totalPaid.toLocaleString('es-AR')}</span>
                  </div>
                  <div className="bg-[#1a1c23] border border-[#2d313f]/50 rounded-xl md:rounded-2xl p-2.5 md:p-4 flex flex-col justify-center items-center text-center">
                    <span className="text-[7.5px] md:text-[9px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">Pendiente Cobro</span>
                    <span className="text-xs sm:text-base md:text-lg lg:text-xl font-black text-amber-400 mt-1">${cashTotals.totalPending.toLocaleString('es-AR')}</span>
                  </div>
                  <div className="bg-[#1a1c23] border border-[#2d313f]/50 rounded-xl md:rounded-2xl p-2.5 md:p-4 flex flex-col justify-center items-center text-center">
                    <span className="text-[7.5px] md:text-[9px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">Ventas Cantina</span>
                    <span className="text-xs sm:text-base md:text-lg lg:text-xl font-black text-blue-400 mt-1">${cashTotals.totalProducts.toLocaleString('es-AR')}</span>
                  </div>
                  <div className="bg-[#1a1c23] border border-[#2d313f]/50 rounded-xl md:rounded-2xl flex col-span-2 md:col-span-2 overflow-hidden">
                    <div className="flex-1 p-2.5 md:p-4 flex flex-col justify-center items-center text-center border-r border-[#2d313f]/50">
                      <span className="text-[7.5px] md:text-[9px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">Efectivo</span>
                      <span className="text-xs sm:text-base md:text-lg lg:text-xl font-black text-emerald-400 mt-1">${cashTotals.totalCash.toLocaleString('es-AR')}</span>
                    </div>
                    <div className="flex-1 p-2.5 md:p-4 flex flex-col justify-center items-center text-center">
                      <span className="text-[7.5px] md:text-[9px] font-bold uppercase tracking-wider text-zinc-400 block whitespace-nowrap">Transferencia</span>
                      <span className="text-xs sm:text-base md:text-lg lg:text-xl font-black text-white mt-1">${cashTotals.totalTransfer.toLocaleString('es-AR')}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Controles de Búsqueda y Filtrado */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-2">
                {/* Input buscador */}
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type="text"
                    value={cashSearch}
                    onChange={(e) => setCashSearch(e.target.value)}
                    placeholder="Buscar por cliente o cancha..."
                    className="w-full bg-[#1c1e26] border border-[#2d313f]/60 rounded-xl pl-9 pr-4 py-2 text-xs text-white focus:outline-none focus:border-theme-primary/50 transition-all placeholder-zinc-500"
                  />
                </div>

                {/* Filtros de estado y fecha */}
                <div className="flex flex-wrap items-center gap-2">

                  {/* Selector Estado */}
                  <div className="bg-[#1c1e26] border border-[#2d313f]/50 rounded-xl p-0.5 flex">
                    <button
                      onClick={() => setCashStatusFilter('all')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashStatusFilter === 'all' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Todos
                    </button>
                    <button
                      onClick={() => setCashStatusFilter('paid')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashStatusFilter === 'paid' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Pagados
                    </button>
                    <button
                      onClick={() => setCashStatusFilter('confirmed')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashStatusFilter === 'confirmed' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Pendientes
                    </button>
                    <button
                      onClick={() => setCashStatusFilter('cancelled')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashStatusFilter === 'cancelled' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Cancelados
                    </button>
                  </div>

                  {/* Selector Fecha */}
                  <div className="bg-[#1c1e26] border border-[#2d313f]/50 rounded-xl p-0.5 flex">
                    {!['empleado', 'gestor_turnos'].includes(userRole || '') && (
                      <button
                        onClick={() => setCashDateFilter('all')}
                        className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashDateFilter === 'all' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                      >
                        Histórico
                      </button>
                    )}
                    <button
                      onClick={() => setCashDateFilter('today')}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashDateFilter === 'today' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                    >
                      Hoy
                    </button>
                    {!['empleado', 'gestor_turnos'].includes(userRole || '') && (
                      <button
                        onClick={() => setCashDateFilter('month')}
                        className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer ${cashDateFilter === 'month' ? 'bg-[#FACC15] text-black shadow-md shadow-[#FACC15]/10' : 'text-zinc-400 hover:text-white'}`}
                      >
                        Mes
                      </button>
                    )}
                  </div>

                  {/* Selector de Día Específico (Calendario) */}
                  {!['empleado', 'gestor_turnos'].includes(userRole || '') && (
                    <div className="relative">
                      <button
                        onClick={() => {
                          const dateInput = document.getElementById('cash-custom-date-picker') as HTMLInputElement;
                          if (dateInput) dateInput.showPicker();
                        }}
                        className={`flex items-center gap-1.5 px-2.5 py-[7px] rounded-xl border transition-all cursor-pointer ${cashDateFilter === 'custom'
                          ? 'bg-[#FACC15] border-[#FACC15]/60 text-black shadow-md shadow-[#FACC15]/10'
                          : 'bg-[#1c1e26] border-[#2d313f]/50 text-zinc-400 hover:text-white hover:border-zinc-500'
                          }`}
                        title="Elegir día específico"
                      >
                        <Calendar size={13} />
                        {cashDateFilter === 'custom' && cashCustomDate && (
                          <span className="text-[10px] font-bold uppercase tracking-wider">
                            {(() => {
                              const [y, m, d] = cashCustomDate.split('-');
                              return `${d}/${m}`;
                            })()}
                          </span>
                        )}
                      </button>

                      <input
                        id="cash-custom-date-picker"
                        type="date"
                        value={cashCustomDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val) {
                            setCashCustomDate(val);
                            setCashDateFilter('custom');
                          }
                        }}
                        className="absolute inset-0 opacity-0 pointer-events-none w-0 h-0"
                        tabIndex={-1}
                      />
                    </div>
                  )}

                </div>
              </div>

            </div>

            {/* Listado de transacciones */}
            <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-3 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent"
              onScroll={(e) => {
                const target = e.target as HTMLElement;
                if (target.scrollHeight - target.scrollTop <= target.clientHeight + 100) {
                  setVisibleCashItemsCount(prev => Math.min(prev + 20, cashFilteredBookings.length));
                }
              }}>
              {cashFilteredBookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-zinc-500 space-y-3">
                  <DollarSign size={40} className="text-zinc-600 opacity-40 animate-pulse" />
                  <p className="text-xs font-medium text-zinc-400">No se encontraron movimientos registrados con los filtros seleccionados.</p>
                </div>
              ) : (
                cashFilteredBookings.slice(0, visibleCashItemsCount).map((b) => {
                  const basePrice = b.courtId === 'pos_cantina' ? 0 : (b.price ?? schedule?.pricePerSlot ?? 12000);
                  const productsSum = b.courtId === 'expense' ? 0 : (b.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0);
                  let totalAmount = basePrice + productsSum;
                  
                  if (b.status === 'paid' && b.paymentBreakdown && (b.paymentBreakdown.cash !== undefined || b.paymentBreakdown.transfer !== undefined)) {
                    totalAmount = (b.paymentBreakdown.cash || 0) + (b.paymentBreakdown.transfer || 0);
                  }

                  // Formato de fecha bonita
                  const [year, month, day] = b.date.split('-');
                  const shortDate = `${day}/${month}`;

                  return (
                    <div
                      key={b.id}
                      className="bg-[#1a1c23] border border-[#2d313f]/40 hover:border-theme-primary/20 rounded-xl md:rounded-2xl p-3 md:p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 transition-all duration-300"
                    >
                      {/* Información de la reserva */}
                      <div className="flex items-center gap-3">
                        {/* Fecha y hora */}
                        <div className="bg-[#222530] border border-[#2d313f]/50 rounded-xl px-2.5 py-1.5 flex flex-col items-center justify-center text-center shrink-0 min-w-[50px]">
                          <span className="text-[10px] font-black text-theme-primary">{b.timeSlot}</span>
                          <span className="text-[8px] font-semibold text-zinc-400 mt-0.5">{shortDate}</span>
                        </div>

                        {/* Detalle del cliente y cancha */}
                        <div className="min-w-0">
                          <span className="text-xs font-black text-white block truncate">{b.playerName}</span>
                          <span className="text-[10px] text-zinc-400 flex items-center gap-1.5 mt-0.5">
                            {b.courtId === 'pos_cantina' ? (
                              <span className="px-1.5 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-400 font-bold uppercase tracking-wider">{b.courtName}</span>
                            ) : b.courtId === 'expense' ? (
                              <span className="px-1.5 py-0.5 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 font-bold uppercase tracking-wider">{b.courtName}</span>
                            ) : (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-theme-primary/60 shrink-0" />
                                {b.courtName}
                              </>
                            )}
                          </span>
                          {(b.paymentHistory?.[0]?.by || b.operatorEmail) && (
                            <span className="text-[8px] sm:text-[9px] text-theme-primary/60 font-medium block mt-0.5">
                              {b.paymentHistory?.[0]?.by || b.operatorEmail?.split('@')[0]}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Detalles financieros / Cantina */}
                      <div className="flex items-center gap-4 md:gap-6 justify-between md:justify-end">
                        {/* desglose de precios */}
                        <div className="text-left md:text-right">
                          <span className="text-xs font-black text-white block">${totalAmount.toLocaleString('es-AR')}</span>
                          {b.status === 'paid' && b.paymentBreakdown ? (
                            !['empleado', 'gestor_turnos'].includes(userRole || '') && (
                              <span className="text-[8px] sm:text-[9px] text-zinc-500 font-medium block">
                                Ef: ${(b.paymentBreakdown.cash || 0).toLocaleString('es-AR')} | Tr: ${(b.paymentBreakdown.transfer || 0).toLocaleString('es-AR')}
                              </span>
                            )
                          ) : b.courtId === 'pos_cantina' ? (
                            <span className="text-[8px] sm:text-[9px] text-zinc-500 font-medium block">
                               Venta de Cantina
                            </span>
                          ) : productsSum > 0 ? (
                            <span className="text-[8px] sm:text-[9px] text-zinc-500 font-medium block">
                              Base: ${basePrice.toLocaleString('es-AR')} + Cantina: ${productsSum.toLocaleString('es-AR')}
                            </span>
                          ) : (
                            <span className="text-[8px] sm:text-[9px] text-zinc-500 font-medium block">{b.courtId === 'expense' ? 'Egreso' : 'Solo cancha'}</span>
                          )}
                          {(b.paymentHistory && b.paymentHistory.some(h => h.action === 'modification' || ((b.status === 'cancelled' || (b.hiddenFromGrid && b.courtId !== 'pos_cantina' && b.courtId !== 'expense')) && h.action === 'initial_charge') || (h.action === 'initial_charge' && h.newTotal !== undefined && h.newTotal !== totalAmount))) && (
                            <span className="text-[9px] font-bold text-amber-400 flex items-center justify-end gap-1 mt-0.5">
                              Valor modificado
                            </span>
                          )}
                        </div>

                        {/* Botón de cambio de Estado y Badge interactivo */}
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-wider block ${(b.status === 'cancelled' || (b.hiddenFromGrid && b.courtId !== 'pos_cantina' && b.courtId !== 'expense'))
                            ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                            : b.status === 'paid'
                            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                            : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
                            }`}>
                            {(b.status === 'cancelled' || (b.hiddenFromGrid && b.courtId !== 'pos_cantina' && b.courtId !== 'expense')) ? 'Cancelado' : b.status === 'paid' ? (b.courtId === 'expense' ? 'Registrado' : 'Pagado') : 'Pendiente'}
                          </span>

                          <button
                            onClick={() => {
                              setSelectedSlot({ court: setupCourts.find(c => c.id === b.courtId) || {id: b.courtId, name: b.courtName} as any, time: b.timeSlot, booking: b });
                              setIsCashModalOpen(false);
                            }}
                            className="p-1.5 rounded-lg border bg-[#222530] border-[#2d313f] text-zinc-400 hover:text-white hover:bg-theme-primary/20 hover:border-theme-primary/30 transition-all cursor-pointer flex items-center gap-1.5"
                            title="Ver Detalle"
                          >
                            <span className="text-[9px] font-bold uppercase tracking-wider hidden sm:block">Detalle</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                        </div>
                      </div>

                    </div>
                  );
                })
              )}
            </div>

            </>
            )}

            {cashTab === 'historial' && (
              <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent">
                {cashSessionsHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-zinc-500 space-y-3">
                    <p className="text-xs font-medium text-zinc-400">No hay historial de cajas cerradas.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cashSessionsHistory.map(session => (
                      <div key={session.id} className="bg-[#1a1c23] border border-[#2d313f] rounded-xl p-4 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase ${session.status === 'open' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'}`}>
                              {session.status === 'open' ? 'En curso' : 'Cerrada'}
                            </span>
                            <span className="text-xs text-zinc-400 font-medium">{new Date(session.openedAt).toLocaleString('es-AR')}</span>
                          </div>
                          <p className="text-sm text-white font-medium">Apertura: <span className="text-zinc-400">{session.openedBy.name}</span></p>
                          {session.closedBy && <p className="text-sm text-white font-medium">Cierre: <span className="text-zinc-400">{session.closedBy.name}</span> - {new Date(session.closedAt!).toLocaleTimeString('es-AR')}</p>}
                        </div>
                        <div className="flex gap-4 items-center bg-[#13151b] p-3 rounded-lg border border-[#2d313f]/50">
                          <div className="text-center">
                            <p className="text-[10px] text-zinc-500 uppercase font-bold">Inicial</p>
                            <p className="text-sm font-black text-white">${session.initialCash}</p>
                          </div>
                          {session.status === 'closed' && (
                            <>
                              <div className="text-center">
                                <p className="text-[10px] text-zinc-500 uppercase font-bold">Declarado</p>
                                <p className="text-sm font-black text-white">${session.declaredCash}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-zinc-500 uppercase font-bold">Diferencia</p>
                                <p className={`text-sm font-black ${(session.difference || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {(session.difference || 0) >= 0 ? '+' : ''}${session.difference}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pie de modal */}
            <div className="p-4 md:p-5 border-t border-[#2d313f]/50 bg-[#1a1c25]/50 flex items-center justify-between text-[9px] md:text-[10px] text-zinc-500 z-10">
              <span>Sistemas de Caja - RPT2</span>
              <span>Filtrado: {cashFilteredBookings.length} resultados</span>
            </div>

          </div>
        </div>,
        document.getElementById('root') || document.body
      ) : null}

      
      <AdminCantinaTabModal
        isOpen={isTabModalOpen}
        onClose={() => setIsTabModalOpen(false)}
        tabId={selectedTabId}
        clubId={userId!}
        sessionId={null}
        availableProducts={availableProducts}
        activeUserName={activeUserName}
        onSaved={(details) => {
          fetchCantinaTabs();
          if (details && details.amount > 0) {
            setRecentCantinaPayments(prev => [...prev, { ...details, timestamp: Date.now() }]);
          }
        }}
        operatingDate={todayStr}
        defaultName={cantinaTabDefaultName}
        slotNumber={cantinaTabSlotNumber}
      />

      {/* MODAL VENTA CANTINA */}
      {showCantinaModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-[#16181f] border border-[#2d313f] rounded-2xl w-full max-w-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col my-auto max-h-[90vh] animate-in zoom-in-95 duration-200">
            
            {/* Header */}
            <div className="p-4 sm:p-5 bg-[#1a1c23] border-b border-[#2d313f] flex flex-col items-center justify-center relative sticky top-0 z-10 gap-3">
              <div className="flex items-center gap-2 text-white">
                <Store size={22} className="mb-0.5" />
                <h3 className="text-base sm:text-lg font-black uppercase tracking-wide mt-0.5">Venta Cantina</h3>
              </div>
              <div className="flex items-center gap-2 text-[11px] sm:text-xs font-bold text-zinc-400 bg-[#16181f] px-3 py-1.5 rounded-lg border border-[#2d313f] w-max">
                <span>Cliente:</span>
                <input
                  type="text"
                  value={cantinaClientName}
                  onChange={(e) => setCantinaClientName(e.target.value)}
                  style={{ width: `${Math.max(cantinaClientName.length, 16)}ch` }}
                  className="bg-transparent text-white font-black focus:outline-none placeholder-zinc-600 text-center transition-all duration-200"
                  placeholder="Consumidor Final"
                />
              </div>
              <button
                onClick={() => setShowCantinaModal(false)}
                className="absolute right-4 top-4 sm:top-5 p-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white rounded-lg transition-all"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 sm:p-6 flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-6">
              
              {/* Selector de Productos */}
              <div className="bg-[#15171d] border border-[#2d313f] p-4 rounded-xl space-y-4">
                

                <div className="flex flex-wrap gap-2 justify-center max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                  {(!availableProducts || availableProducts.length === 0) ? (
                    <div className="w-full text-center py-8 text-zinc-500 text-xs font-bold uppercase tracking-wider border border-dashed border-[#2d313f] rounded-xl">
                      No tienes productos cargados en tu inventario.
                    </div>
                  ) : (
                    availableProducts.filter(p => (cantinaCategory === 'all' || p.category === cantinaCategory) && (p.name.toLowerCase().includes(cantinaSearch.toLowerCase()))).length === 0 ? (
                      <div className="w-full text-center py-8 text-zinc-500 text-xs font-bold uppercase tracking-wider border border-dashed border-[#2d313f] rounded-xl">
                        No se encontraron productos.
                      </div>
                    ) : (
                      availableProducts.filter(p => (cantinaCategory === 'all' || p.category === cantinaCategory) && (p.name.toLowerCase().includes(cantinaSearch.toLowerCase()))).map(prod => {
                        const nameLength = prod.name.length;
                        const fontSizeClass = nameLength > 22
                          ? 'text-[8px] sm:text-[9px] leading-[1.1]'
                          : nameLength > 12
                            ? 'text-[9px] sm:text-[10px] leading-tight'
                            : 'text-[10px] sm:text-xs leading-tight';

                        const cartItem = cantinaCart.find(i => i.id === prod.id);
                        const qty = cartItem ? cartItem.quantity : 0;

                        return (
                          <div
                            key={prod.id}
                            onClick={() => handleAddToCart(prod)}
                            className="relative flex flex-col items-center justify-between p-2 rounded-lg border cursor-pointer transition-all duration-200 w-[95px] sm:w-[110px] h-[95px] sm:h-[110px] shrink-0 bg-[#1e202b] border-[#2d313f] hover:border-theme-primary/50 hover:bg-[#252836] active:scale-95 group select-none"
                          >
                            {qty > 0 && (
                              <div className="absolute top-1 right-1 bg-blue-500 text-white font-black text-[11px] w-[22px] h-[22px] flex items-center justify-center rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)] z-10 pointer-events-none border border-blue-400">
                                {qty}
                              </div>
                            )}
                            <div className="w-full flex justify-center flex-1 items-center min-h-[36px] pointer-events-none">
                              {prod.imageUrl ? (
                                <img
                                  src={prod.imageUrl}
                                  alt={prod.name}
                                  className="w-[42px] h-[42px] sm:w-[54px] sm:h-[54px] rounded-md object-cover border border-[#2d313f] shrink-0 transition-all duration-300 group-hover:scale-105 select-none"
                                />
                              ) : (
                                <div className="w-[42px] h-[42px] sm:w-[54px] sm:h-[54px] rounded-md bg-[#1a1c23] flex items-center justify-center border border-[#2d313f] shrink-0 transition-all duration-300 select-none">
                                  <Store size={14} className="text-zinc-500 sm:scale-110" />
                                </div>
                              )}
                            </div>

                            <div className="mt-1.5 w-full text-center shrink-0 pointer-events-none select-none">
                              <div className={`${fontSizeClass} font-bold text-white line-clamp-2 break-words text-center w-full px-0.5 select-none`}>
                                {prod.name}
                              </div>
                              <div className="text-[9px] sm:text-[10px] text-zinc-400 font-bold mt-0.5 select-none">${prod.price.toLocaleString('es-AR')}</div>
                            </div>
                          </div>
                        );
                      })
                    )
                  )}
                </div>
              </div>


              {/* Ticket / Carrito */}
              <div className="bg-[#15171d] border border-[#2d313f] p-4 rounded-xl space-y-3">
                <div className="flex items-center justify-between border-b border-[#2d313f] pb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Detalle del Ticket</span>
                    <button
                      onClick={() => setShowCantinaQuickItemModal(true)}
                      className="text-blue-400 flex items-center gap-1 text-[9px] font-black uppercase bg-blue-400/10 border border-blue-400/20 hover:bg-blue-400/20 px-2 py-1 rounded transition-colors"
                    >
                      <Zap size={12} className="fill-blue-400" /> Item Rápido
                    </button>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">{cantinaCart.length} items</span>
                </div>
                
                <div className="max-h-[150px] overflow-y-auto custom-scrollbar space-y-2 pr-1">
                  {cantinaCart.length === 0 ? (
                    <div className="text-center py-6 border border-dashed border-[#2d313f] rounded-lg text-zinc-600 text-xs font-bold uppercase tracking-wider">
                      El carrito está vacío
                    </div>
                  ) : (
                    cantinaCart.map(item => (
                      <div key={item.id} className="flex items-center justify-between bg-[#1e202b] border border-[#2d313f] rounded-lg p-2 gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-white truncate">{item.name}</p>
                          <p className="text-[10px] text-zinc-400 font-bold">${item.price.toLocaleString('es-AR')} c/u</p>
                        </div>
                        <div className="flex items-center gap-1.5 bg-[#15171d] rounded-lg p-1 border border-[#2d313f]">
                          <button onClick={() => handleRemoveFromCart(item.id)} className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-[#2d313f] rounded flex-shrink-0 transition-colors">
                            <span className="font-bold text-sm leading-none -mt-0.5">-</span>
                          </button>
                          <span className="w-5 text-center text-[10px] font-black text-white">{item.quantity}</span>
                          <button onClick={() => handleAddToCart({ id: item.id, name: item.name, price: item.price } as Product)} className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-[#2d313f] rounded flex-shrink-0 transition-colors">
                            <span className="font-bold text-sm leading-none -mt-0.5">+</span>
                          </button>
                        </div>
                        <div className="w-[85px] flex items-center justify-end gap-2 text-right text-xs font-black text-theme-primary">
                          <span>${(item.price * item.quantity).toLocaleString('es-AR')}</span>
                          <button onClick={() => setCantinaCart(cantinaCart.filter(i => i.id !== item.id))} className="text-zinc-500 hover:text-red-400 transition-colors p-1.5 -mr-1.5 rounded-lg hover:bg-red-500/10 shrink-0">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {cantinaCart.length > 0 && (
                  <div className="flex flex-col md:flex-row items-end justify-center gap-6 pt-5 border-t border-[#2d313f]">
                     {(() => {
                        const cartTotal = cantinaCart.reduce((s, i) => s + (i.price * i.quantity), 0);
                        const cashAmount = Math.min(cartTotal, parseInt(cantinaPaymentCash || '0'));
                        const transferAmount = cartTotal - cashAmount;
                        
                        return (
                          <div className="flex items-center justify-center gap-4 w-full md:w-auto">
                            <div className="flex-1 sm:flex-none flex flex-col items-center gap-1.5">
                              <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 text-center">Efectivo</span>
                              <div className="relative flex items-center bg-[#1a1c23] rounded-xl px-3 py-2.5 w-full sm:w-[140px] border border-[#2d313f] focus-within:border-blue-500/50 transition-colors">
                                <span className="text-zinc-400 font-bold text-[13px] absolute left-3">$</span>
                                <input
                                  type="tel"
                                  value={cantinaPaymentCash ? parseInt(cantinaPaymentCash).toLocaleString('es-AR') : ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setCantinaPaymentCash(val);
                                  }}
                                  onBlur={() => {
                                    let numVal = parseInt(cantinaPaymentCash || '0');
                                    if (numVal > cartTotal) numVal = cartTotal;
                                    setCantinaPaymentCash(numVal === 0 ? '' : numVal.toString());
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                  }}
                                  className="w-full bg-transparent text-white font-black text-[14px] focus:outline-none text-center pl-4"
                                  placeholder="0"
                                />
                              </div>
                            </div>

                            <div className="flex-1 sm:flex-none flex flex-col items-center gap-1.5">
                              <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 text-center">Transferencia</span>
                              <div className="relative flex items-center bg-[#1a1c23] rounded-xl px-3 py-2.5 w-full sm:w-[140px] border border-[#2d313f] focus-within:border-blue-500/50 transition-colors">
                                <span className="text-zinc-400 font-bold text-[13px] absolute left-3">$</span>
                                <input
                                  type="tel"
                                  value={transferAmount > 0 ? transferAmount.toLocaleString('es-AR') : ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    let t = parseInt(val || '0');
                                    if (t > cartTotal) t = cartTotal;
                                    setCantinaPaymentCash((cartTotal - t).toString());
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                  }}
                                  className="w-full bg-transparent text-white font-black text-[14px] focus:outline-none text-center pl-4"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    
                    <div className="flex items-center w-full md:w-auto justify-center shrink-0 mt-2 md:mt-0">
                      <button
                        onClick={handleSaveCantinaSale}
                        className="bg-blue-500 hover:bg-blue-400 text-white rounded-xl transition-all active:scale-95 shadow-[0_0_20px_rgba(59,130,246,0.3)] flex items-center justify-center gap-3 w-full sm:w-[170px] py-2.5 px-3"
                      >
                        <Store size={24} className="text-white shrink-0" />
                        <div className="flex flex-col items-start justify-center">
                          <span className="text-[11px] font-black text-blue-100 uppercase tracking-widest leading-tight whitespace-nowrap">Confirmar</span>
                          <span className="text-[17px] font-black text-white leading-tight">${cantinaCart.reduce((s, i) => s + (i.price * i.quantity), 0).toLocaleString('es-AR')}</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>,
        document.getElementById('root') || document.body
      )}

      
{/* MODAL ITEM RAPIDO CANTINA */}
      {showCantinaQuickItemModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[110] bg-black/30 backdrop-blur-[2px] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-[#1a1c23] border border-[#2d313f] rounded-2xl w-full max-w-sm shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-[#2d313f] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-[#FFE600]">
                  <Zap size={16} className="fill-[#FFE600]" />
                </div>
                <h3 className="font-black text-white uppercase tracking-wider text-sm">Item Rápido</h3>
              </div>
              <button onClick={() => setShowCantinaQuickItemModal(false)} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-white/5 transition-colors">
                <X size={18} />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1 block">Nombre del Producto</label>
                <input
                  type="text"
                  value={cantinaQuickItemName}
                  onChange={(e) => setCantinaQuickItemName(e.target.value)}
                  placeholder="Ej: Alquiler de paleta"
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-theme-primary/50"
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1 block">Precio Unitario</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-bold">$</span>
                    <input
                      type="number"
                      value={cantinaQuickItemPrice}
                      onChange={(e) => setCantinaQuickItemPrice(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-8 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-theme-primary/50"
                    />
                  </div>
                </div>
                <div className="w-32">
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1 block">Cantidad</label>
                  <div className="flex items-center bg-[#15171d] rounded-xl border border-[#2d313f] overflow-hidden h-[42px]">
                    <button
                      type="button"
                      onClick={() => setCantinaQuickItemQuantity(q => Math.max(1, q - 1))}
                      className="w-9 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 transition-all font-black text-base cursor-pointer select-none border-r border-[#2d313f]"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={cantinaQuickItemQuantity}
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) setCantinaQuickItemQuantity(val);
                        else if (e.target.value === '') setCantinaQuickItemQuantity(1);
                      }}
                      className="flex-1 w-full bg-transparent h-full text-sm text-white font-semibold text-center outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setCantinaQuickItemQuantity(q => q + 1)}
                      className="w-9 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 transition-all font-black text-base cursor-pointer select-none border-l border-[#2d313f]"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-[#15171d] border-t border-[#2d313f] flex justify-end gap-2">
              <button onClick={() => setShowCantinaQuickItemModal(false)} className="px-4 py-2 text-zinc-400 font-bold text-xs uppercase tracking-wider hover:text-white transition-colors">
                Cancelar
              </button>
              <button 
                onClick={handleAddCantinaQuickItem}
                disabled={!cantinaQuickItemName.trim() || !cantinaQuickItemPrice || Number(cantinaQuickItemPrice) <= 0}
                className="bg-[#FFE600] hover:bg-[#e6d000] disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-black text-xs uppercase tracking-widest px-5 py-2 rounded-xl transition-all shadow-[0_0_15px_rgba(245,158,11,0.3)] disabled:shadow-none"
              >
                Agregar
              </button>
            </div>
          </div>
        </div>,
        document.getElementById('root') || document.body
      )}
      {/* MODAL EGRESOS DE CAJA Y STOCK */}
      {showExpenseModal && createPortal(
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
          <div className="bg-[#15171d] border border-[#2d313f] rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col my-auto relative">
            <div className="p-6 border-b border-[#2d313f] flex flex-col items-center justify-center relative bg-[#1a1c25]">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 border ${expenseTab === 'stock' ? 'bg-[#FFD145]/10 text-[#FFD145] border-[#FFD145]/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                {expenseTab === 'stock' ? <ShoppingCart size={24} /> : <TrendingDown size={24} />}
              </div>
              <h3 className="text-lg font-black text-white capitalize tracking-wide text-center">
                {expenseTab === 'stock' ? 'Comprar Stock' : 'Registrar Egreso'}
              </h3>
              <p className="text-xs text-zinc-400 font-medium text-center mt-1">
                {expenseTab === 'stock' ? 'Ingreso de mercadería y egreso de caja' : 'Salida de dinero de la caja'}
              </p>
              <button
                onClick={() => setShowExpenseModal(false)}
                className="absolute top-4 right-4 p-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white rounded-xl transition-all active:scale-95 cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>
            
            {/* TABS (Solo visible en movil o para cambiar rápido) */}
            <div className="flex border-b border-[#2d313f]">
              <button
                onClick={() => setExpenseTab('general')}
                className={`flex-1 py-3 text-xs font-black uppercase tracking-wider transition-colors ${expenseTab === 'general' ? 'text-white border-b-2 border-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                Gasto General
              </button>
              <button
                onClick={() => setExpenseTab('stock')}
                className={`flex-1 py-3 text-xs font-black uppercase tracking-wider transition-colors ${expenseTab === 'stock' ? 'text-[#FFD145] border-b-2 border-[#FFD145]' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                Compra Stock
              </button>
            </div>

            <div className="p-6 space-y-6">
              {expenseTab === 'general' ? (
                <>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Categoría</label>
                    <div className="grid grid-cols-2 gap-2 p-1 bg-[#1a1c23] rounded-xl border border-[#2d313f]">
                      <button
                        onClick={() => setExpenseCategory('Gastos / Pagos')}
                        className={`py-3 rounded-lg text-sm font-bold transition-all cursor-pointer ${expenseCategory === 'Gastos / Pagos' ? 'bg-transparent border border-white text-white shadow-sm' : 'border border-transparent text-zinc-400 hover:text-white'}`}
                      >
                        Gastos / Pagos
                      </button>
                      <button
                        onClick={() => setExpenseCategory('Retiro de Dinero')}
                        className={`py-3 rounded-lg text-sm font-bold transition-all cursor-pointer ${expenseCategory === 'Retiro de Dinero' ? 'bg-transparent border border-white text-white shadow-sm' : 'border border-transparent text-zinc-400 hover:text-white'}`}
                      >
                        Retiro de Dinero
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Descripción (Opcional)</label>
                    <input
                      type="text"
                      value={expenseDescription}
                      onChange={(e) => setExpenseDescription(e.target.value)}
                      placeholder={expenseCategory === 'Gastos / Pagos' ? "Ej: Pago de luz, proveedor..." : "Ej: Retiro dueño..."}
                      className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 text-white font-medium focus:outline-none focus:border-zinc-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Monto</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-bold">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        value={expenseAmount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          setExpenseAmount(val ? Number(val).toString() : '');
                        }}
                        className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl pl-8 pr-4 py-3 text-white font-black text-lg focus:outline-none focus:border-zinc-500 transition-colors"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Producto a comprar</label>
                    <select
                      value={stockProductId}
                      onChange={(e) => setStockProductId(e.target.value)}
                      className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 text-white font-medium focus:outline-none focus:border-theme-primary/50 transition-colors appearance-none"
                    >
                      <option value="">-- Seleccionar Producto --</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.trackStock === false ? 'Stock: ∞' : `Stock actual: ${p.stock || 0}`})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Cantidad</label>
                      {(() => {
                        const selectedProd = products.find(p => p.id === stockProductId);
                        const isInfinite = selectedProd && selectedProd.trackStock === false;
                        return isInfinite ? (
                          <div className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 text-zinc-500 flex items-center justify-center h-[52px]">
                            <span className="text-3xl leading-none pb-1 font-bold">∞</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={stockQuantity}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              setStockQuantity(val ? Number(val).toString() : '');
                            }}
                            className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 text-white font-black text-lg focus:outline-none focus:border-[#FFD145]/50 transition-colors h-[52px]"
                          />
                        );
                      })()}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Costo Total</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-bold">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={expenseAmount}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setExpenseAmount(val ? Number(val).toString() : '');
                          }}
                          className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl pl-8 pr-4 py-3 text-white font-black text-lg focus:outline-none focus:border-theme-primary/50 transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block">Medio de Pago</label>
                <div className="grid grid-cols-2 gap-2 p-1 bg-[#1a1c23] rounded-xl border border-[#2d313f]">
                  <button
                    onClick={() => setExpenseMethod('cash')}
                    className={`py-3 rounded-lg text-sm font-bold transition-all cursor-pointer ${expenseMethod === 'cash' ? 'bg-transparent border border-white text-white shadow-sm' : 'border border-transparent text-zinc-400 hover:text-white'}`}
                  >
                    Efectivo
                  </button>
                  <button
                    onClick={() => setExpenseMethod('transfer')}
                    className={`py-3 rounded-lg text-sm font-bold transition-all cursor-pointer ${expenseMethod === 'transfer' ? 'bg-transparent border border-white text-white shadow-sm' : 'border border-transparent text-zinc-400 hover:text-white'}`}
                  >
                    Transferencia
                  </button>
                </div>
              </div>

              {expenseTab === 'general' ? (
                <button
                  onClick={handleSaveExpense}
                  disabled={!expenseAmount || parseFloat(expenseAmount) <= 0}
                  className="w-full bg-[#FFD700] hover:bg-[#FFE44D] disabled:bg-[#222530] disabled:text-zinc-600 text-black font-black uppercase tracking-widest py-4 rounded-xl transition-all active:scale-95 disabled:active:scale-100 cursor-pointer shadow-[0_0_15px_rgba(255,215,0,0.3)] disabled:shadow-none"
                >
                  Registrar Egreso
                </button>
              ) : (
                <button
                  onClick={handleSaveStockPurchase}
                  disabled={!expenseAmount || parseFloat(expenseAmount) <= 0 || !stockProductId || (products.find(p => p.id === stockProductId)?.trackStock !== false && (!stockQuantity || parseInt(stockQuantity) <= 0))}
                  className="w-full bg-[#FFD145] hover:bg-[#e6b933] disabled:bg-[#222530] disabled:text-zinc-600 text-black font-black uppercase tracking-widest py-4 rounded-xl transition-all active:scale-95 disabled:active:scale-100 cursor-pointer shadow-[0_0_15px_rgba(255,209,69,0.3)] disabled:shadow-none"
                >
                  Confirmar Compra
                </button>
              )}
            </div>
          </div>
        </div>,
        document.getElementById('root') || document.body
      )}

      {/* MODAL CALENDARIO COMPLETO DEL MES */}
      {showMonthCalendar && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto animate-fade-in">
          <div className="bg-[#16181f] border border-[#2d313f] rounded-3xl w-full max-w-4xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col my-auto max-h-[90vh]">

            {/* Header del Calendario */}
            <div className="p-4 sm:p-6 bg-gradient-to-r from-[#1e212b] to-[#16181f] border-b border-[#2d313f] flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-theme-primary/10 border border-theme-primary/20 flex items-center justify-center text-theme-primary shadow-inner">
                  <Calendar size={22} />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-white capitalize tracking-wide">
                    {calendarMonthDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                  </h3>
                  <p className="text-xs text-zinc-400 font-medium">Vista general de ocupación y reservas</p>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                <button
                  onClick={handlePrevMonth}
                  className="px-3.5 py-2 bg-[#222530] hover:bg-[#2a2e3b] active:scale-95 border border-[#2d313f] rounded-xl text-xs font-bold text-zinc-300 transition-all flex items-center gap-1 shadow-sm"
                >
                  <ChevronLeft size={16} />
                  <span>Anterior</span>
                </button>
                <button
                  onClick={handleNextMonth}
                  className="px-3.5 py-2 bg-[#222530] hover:bg-[#2a2e3b] active:scale-95 border border-[#2d313f] rounded-xl text-xs font-bold text-zinc-300 transition-all flex items-center gap-1 shadow-sm"
                >
                  <span>Siguiente</span>
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={() => setShowMonthCalendar(false)}
                  className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl border border-red-500/20 transition-all active:scale-95 ml-2"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Cuerpo / Grilla del calendario */}
            <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-extrabold uppercase tracking-wider text-zinc-400">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                  <div key={d} className="py-2 bg-white/5 rounded-xl border border-white/5">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {calendarDaysList.map((dayItem, index) => {
                  if (!dayItem) {
                    return <div key={`empty-${index}`} className="min-h-[70px] sm:min-h-[90px] rounded-2xl bg-white/[0.01] border border-dashed border-white/5" />;
                  }

                  const dStr = dayItem.dateStr;
                  const dObj = new Date(dStr + 'T00:00:00');
                  const isToday = dStr === todayStr;

                  const isSelected = dStr === selectedDate;

                  // Calcular reservas del día (exactamente como en desktopDays para consistencia)
                  const directDay = bookings.filter(b => b.date === dStr && (b.status === 'confirmed' || b.status === 'paid') && (!b.excludedDates || !b.excludedDates.includes(dStr)) && !b.hiddenFromGrid);
                  const projectedFixedDayRaw = bookings.filter(b =>
                    b.isFixed &&
                    (!b.id.includes('_materialized_') || b.isFixedRoot) &&
                    !b.id.includes('_cancelled_') &&
                    (b.status === 'confirmed' || b.status === 'paid') &&
                    new Date(b.date + 'T00:00:00').getDay() === dObj.getDay() &&
                    dStr >= b.date &&
                    (!b.excludedDates || !b.excludedDates.includes(dStr)) &&
                    !b.hiddenFromGrid &&
                    !directDay.some(d => d.courtName === b.courtName && d.timeSlot === b.timeSlot)
                  );

                  // Deduplicar por (cancha, horario) para no sumar el mismo turno fijo recurrente creado o confirmado en semanas previas
                  const seenFixed = new Set<string>();
                  const projectedFixedDay = projectedFixedDayRaw.filter(b => {
                    const key = `${b.courtName}_${b.timeSlot}`;
                    if (seenFixed.has(key)) return false;
                    seenFixed.add(key);
                    return true;
                  });

                  // Clasificar los turnos entre Confirmados reales y Fijos pendientes
                  const isBookingTrulyConfirmed = (b: Booking) => {
                    if (b.status === 'paid') return true;
                    if (b.playerConfirmed === true) return true;
                    if (b.isFixed) {
                      if (b.playerConfirmed === false) return false;
                      if (b.confirmationRequested && !b.playerConfirmed) return false;
                      return b.confirmationStatus === 'confirmed' || b.status === 'confirmed';
                    }
                    return b.status === 'confirmed';
                  };

                  let confirmadosCount = 0;
                  let fijosCount = projectedFixedDay.length;

                  directDay.forEach(b => {
                    if (isBookingTrulyConfirmed(b)) {
                      confirmadosCount++;
                    } else if (b.isFixed) {
                      fijosCount++;
                    } else {
                      confirmadosCount++;
                    }
                  });

                  // Calcular ocupación usando el total de confirmados + fijos pendientes
                  const dayBookingsCount = confirmadosCount + fijosCount;
                  const totalSlotsPossible = (schedule?.courts?.length || 3) * (timeSlots?.length || 14);
                  const occupancyRatio = totalSlotsPossible > 0 ? (dayBookingsCount / totalSlotsPossible) : 0;
                  const occPercentage = Math.min(100, Math.round(Math.sqrt(occupancyRatio) * 100));

                  const isOutOfBounds = dStr < minDateLimitStr || dStr > maxDateLimitStr;

                  return (
                    <button
                      key={dStr}
                      onClick={() => {
                        if (isOutOfBounds) {
                          alert(`Para mantener la app rápida, solo puedes navegar entre el ${minDateLimitStr.split('-').reverse().join('/')} y el ${maxDateLimitStr.split('-').reverse().join('/')}`);
                          return;
                        }
                        setSelectedDate(dStr);
                        setShowMonthCalendar(false);
                      }}
                      className={`relative min-h-[72px] sm:min-h-[90px] p-1.5 sm:p-2 rounded-2xl border flex flex-col justify-between transition-all group text-left overflow-hidden ${isOutOfBounds ? 'opacity-30 cursor-not-allowed bg-black/40 grayscale' : isSelected
                        ? 'bg-theme-primary/10 border-theme-primary shadow-[0_4px_20px_rgba(var(--glow-rgb),0.3)] active:scale-95'
                        : isToday
                          ? 'bg-[#1e212b]/60 border-[#FACC15] shadow-[0_0_12px_rgba(250,204,21,0.25)] hover:border-[#FACC15]/80 active:scale-95'
                          : 'bg-[#1e212b]/60 border-[#2d313f] hover:border-white/20 hover:bg-[#222530] active:scale-95'
                        }`}
                    >
                      <div className="flex flex-col items-start w-full">
                        <span className={`text-xs sm:text-sm font-black rounded-lg px-1.5 py-0.5 ${isSelected ? 'theme-primary-bg text-black font-black shadow-md' : isToday ? 'text-[#FACC15] font-black border border-[#FACC15]/40 bg-[#FACC15]/10' : 'text-white font-bold'
                          }`}>
                          {dayItem.dayNumber}
                        </span>

                        <div className="flex flex-col gap-0.5 mt-1 sm:mt-1.5 pl-0.5 w-full">
                          {confirmadosCount > 0 && (
                            <span className="text-[7.5px] sm:text-[9px] font-extrabold text-blue-400 tracking-tight leading-tight whitespace-nowrap truncate block">
                              confirmados: {confirmadosCount}
                            </span>
                          )}
                          {fijosCount > 0 && (
                            <span className="text-[7.5px] sm:text-[9px] font-extrabold text-[#FFE600] tracking-tight leading-tight whitespace-nowrap truncate block">
                              fijos: {fijosCount}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Barra de progreso de ocupación */}
                      <div className="w-full mt-1 sm:mt-2">
                        {dayBookingsCount > 0 ? (
                          <div className="w-full bg-zinc-900/80 rounded-full h-1.5 sm:h-2 overflow-hidden p-[1px] shadow-inner border border-white/10">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${isSelected ? 'bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]' : 'bg-white shadow-[0_0_6px_rgba(255,255,255,0.6)]'
                                }`}
                              style={{ width: `${Math.max(12, occPercentage)}%` }}
                            />
                          </div>
                        ) : (
                          <div className="text-[8px] sm:text-[9px] text-zinc-400 font-medium italic pl-0.5 whitespace-nowrap">Libre</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}



      {/* MODAL DE CONFIRMACIÓN DE COBRO */}
      <AdminChargeConfirmModal
        isOpen={showChargeConfirmModal}
        activeBooking={activeBooking}
        initialTotal={chargePaymentTotal}
        initialCash={chargePaymentCash}
        initialTransfer={chargePaymentTransfer}
        onClose={() => setShowChargeConfirmModal(false)}
        onConfirm={(cash: number, transfer: number, targetTotal: number) => {
          handleMarkAsPaid(activeBooking!, { cash, transfer }, targetTotal);
          setShowChargeConfirmModal(false);
        }}
        onUnmarkPaid={() => {
          handleUnmarkPaid(activeBooking!);
          setShowChargeConfirmModal(false);
        }}
      />

      {/* Modal de Detalle de Jugador */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {selectedPlayerForDetail && (
            <PlayerDetailModal
              player={selectedPlayerForDetail}
              players={players}
              currentUserId={userId}
              config={config}
              onMatchClick={() => {}}
              onPlayerClick={(pid) => {
                const p = players.find(x => x.id === pid);
                if (p) setSelectedPlayerForDetail(p);
              }}
              onClose={() => setSelectedPlayerForDetail(null)}
              onContactClick={onOpenChatWithPlayer}
            />
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* MODAL APERTURA DE CAJA */}
      {showOpenCashModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 bg-black/40 backdrop-blur-[2px] animate-in fade-in">
          <div className="bg-[#1a1c23] border border-[#2d313f] rounded-2xl w-full max-w-sm overflow-hidden flex flex-col my-auto shadow-2xl">
            <div className="p-4 bg-[#222530] border-b border-[#2d313f] flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><PlusCircle size={18} className="text-emerald-400"/> Abrir Caja</h3>
              <button onClick={() => setShowOpenCashModal(false)} className="text-zinc-500 hover:text-white"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-zinc-400">¿Con cuánto efectivo físico abrirás la caja?</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">$</span>
                <input
                  type="number"
                  id="initialCashInput" defaultValue={initialCashInput}
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-8 pr-4 py-3 text-white focus:outline-none focus:border-emerald-500/50"
                  placeholder="0"
                  autoFocus
                />
              </div>
              <button 
                onClick={handleOpenCashSession}
                className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 py-3 rounded-xl font-black uppercase tracking-wider transition-all"
              >
                Confirmar Apertura
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* TOAST DE ÉXITO (VENTA/EGRESO) */}
      <AnimatePresence>
        {successToast.show && (
          <motion.div
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(6px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)', transition: { duration: 0.6 } }}
            className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center bg-black/10"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20, transition: { duration: 0.5 } }}
              transition={{ duration: 0.4, type: 'spring', bounce: 0.4 }}
              className="pointer-events-auto flex flex-col sm:flex-row items-center justify-center gap-3 bg-[#C5FF4A] text-black px-5 sm:px-6 py-3 sm:py-4 rounded-3xl shadow-[0_20px_50px_rgba(197,255,74,0.3)] border-[3px] border-black/10 whitespace-nowrap"
            >
              <div className="bg-black/10 rounded-full p-1.5 shrink-0">
                <Check size={22} className="text-black" strokeWidth={3} />
              </div>
              <span className="font-black text-xs sm:text-sm uppercase tracking-wider leading-tight">
                {successToast.message}
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL CIERRE DE CAJA */}
      {showCloseCashModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 bg-black/40 backdrop-blur-[2px] animate-in fade-in">
          <div className="bg-[#1a1c23] border border-[#2d313f] rounded-2xl w-full max-w-sm overflow-hidden flex flex-col my-auto shadow-2xl">
            <div className="p-4 bg-[#222530] border-b border-[#2d313f] flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><LogOut size={18} className="text-red-400"/> Cerrar Turno</h3>
              <button onClick={() => setShowCloseCashModal(false)} className="text-zinc-500 hover:text-white"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl flex gap-2 text-[#FFE600] text-xs">
                <Lightbulb size={16} className="shrink-0" />
                <p>Contá todos los billetes físicos de la caja y declará el monto exacto. El sistema calculará automáticamente si hay diferencias.</p>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">$</span>
                <input
                  type="number"
                  id="declaredCashInput" defaultValue={declaredCashInput}
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-8 pr-4 py-3 text-white focus:outline-none focus:border-red-500/50"
                  placeholder="Efectivo físico total..."
                  autoFocus
                />
              </div>
              <button 
                onClick={handleCloseCashSession}
                className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 py-3 rounded-xl font-black uppercase tracking-wider transition-all"
              >
                Realizar Arqueo Ciego
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MODAL CONFIRMACIÓN DE REEMBOLSO MP */}
      {refundConfirmState && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-[#1a1c23] border border-blue-500/30 rounded-2xl w-full max-w-md overflow-hidden shadow-[0_0_40px_rgba(59,130,246,0.2)] relative z-10">
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-2 border border-blue-500/20">
                <CreditCard className="text-blue-400" size={32} />
              </div>
              <h3 className="text-xl font-black text-white">Pago de Mercado Pago Detectado</h3>
              <p className="text-sm text-zinc-300">
                Esta reserva tiene un pago asociado por Mercado Pago. <br/>
                ¿Deseas realizar la devolución automática del dinero al jugador antes de cancelar el turno?
              </p>
              
              <div className="bg-black/30 border border-white/5 rounded-xl p-4 text-xs text-zinc-400 text-left space-y-2 mt-4">
                <p>🟢 <b>Devolver Dinero:</b> El jugador recibirá el reintegro y se cancelará el turno.</p>
                <p>🔴 <b>No Devolver:</b> Te quedarás con la seña/pago y se cancelará el turno.</p>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={() => {
                    refundConfirmState.resolve(true);
                    setRefundConfirmState(null);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl font-black text-sm transition-colors"
                >
                  Devolver Dinero y Cancelar
                </button>
                <button
                  onClick={() => {
                    refundConfirmState.resolve(false);
                    setRefundConfirmState(null);
                  }}
                  className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-500 py-3 rounded-xl font-bold text-sm transition-colors"
                >
                  No Devolver (Sólo Cancelar Turno)
                </button>
                <button
                  onClick={() => {
                    refundConfirmState.resolve('cancel_action');
                    setRefundConfirmState(null);
                  }}
                  className="w-full py-2 text-zinc-500 hover:text-white text-xs font-bold transition-colors uppercase tracking-widest mt-2"
                >
                  Abortar Acción
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ERROR MODAL */}
      {errorModalMessage && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-[#1a1c23] border border-amber-500/30 rounded-2xl w-full max-w-md overflow-hidden shadow-[0_0_40px_rgba(245,158,11,0.2)] relative z-10">
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-2 border border-amber-500/20">
                <AlertTriangle className="text-amber-400" size={32} />
              </div>
              <h3 className="text-xl font-black text-white">{errorModalMessage.title}</h3>
              <p className="text-sm text-zinc-300 whitespace-pre-line">
                {errorModalMessage.message}
              </p>
              
              <div className="pt-4">
                <button
                  onClick={() => setErrorModalMessage(null)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black py-3 rounded-xl font-black text-sm transition-colors"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {contextMenu && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed z-[9999] bg-[#1a1c23] border border-[#2d313f] rounded-xl shadow-2xl py-1 w-48 overflow-hidden transform transition-all animate-in fade-in zoom-in-95 duration-150"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.cellInfo.booking ? (
            <button 
              onClick={() => {
                setSelectedSlot(contextMenu.cellInfo);
                setContextMenu(null);
              }}
              className="w-full text-left px-4 py-3 text-sm font-bold text-white hover:bg-theme-primary/10 hover:text-theme-primary transition-colors flex items-center gap-2"
            >
              <Eye size={16} /> Abrir
            </button>
          ) : (
            <>
              <button 
                onClick={() => {
                  contextMenuActionRef.current = 'promote';
                  setSelectedSlot(contextMenu.cellInfo);
                  setModalActiveTab('promote');
                  setContextMenu(null);
                }}
                className="w-full text-left px-4 py-3 text-sm font-bold text-white hover:bg-purple-500/10 hover:text-purple-400 transition-colors flex items-center gap-2"
              >
                <Flame size={16} /> Promocionar
              </button>
              <button 
                onClick={() => {
                  setFastBlockPrompt({ court: contextMenu.cellInfo.court, time: contextMenu.cellInfo.time, isPromo: false });
                  setContextMenu(null);
                }}
                className="w-full text-left px-4 py-3 text-sm font-bold text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
              >
                <Lock size={16} /> Bloquear
              </button>
            </>
          )}
        </div>,
        document.body
      )}

      {fastBlockPrompt && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in">
          <div className="bg-[#1a1c23] border border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl relative z-10">
            <div className="p-5 text-center space-y-4">
              <h3 className="text-lg font-black text-white">Bloquear Casillero</h3>
              <p className="text-xs text-zinc-400">
                {fastBlockPrompt.court} - {fastBlockPrompt.time}hs
              </p>
              <input
                type="text"
                value={fastBlockReason}
                onChange={(e) => setFastBlockReason(e.target.value)}
                placeholder="Motivo (opcional, ej: Mantenimiento)"
                className="w-full bg-[#111217] border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-theme-primary/50"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFastBlock(fastBlockPrompt.court, fastBlockPrompt.time, fastBlockPrompt.isPromo, fastBlockReason);
                    setFastBlockPrompt(null);
                    setFastBlockReason('');
                  }
                }}
              />
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => {
                    setFastBlockPrompt(null);
                    setFastBlockReason('');
                  }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    handleFastBlock(fastBlockPrompt.court, fastBlockPrompt.time, fastBlockPrompt.isPromo, fastBlockReason);
                    setFastBlockPrompt(null);
                    setFastBlockReason('');
                  }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-amber-500 text-black hover:bg-amber-600 transition-colors"
                >
                  Bloquear
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {fastUnblockConfirm && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in">
          <div className="bg-[#1a1c23] border border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl relative z-10">
            <div className="p-5 text-center space-y-4">
              <h3 className="text-lg font-black text-white">Desbloquear Casillero</h3>
              <p className="text-xs text-zinc-400">
                ¿Estás seguro de que deseas eliminar este bloqueo y habilitar la cancha?
              </p>
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setFastUnblockConfirm(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if ((fastUnblockConfirm as any).isConfigBlock) {
                      const baseBookingId = `${userId}_unblock_${fastUnblockConfirm!.courtName.replace(/\s+/g, '')}_${selectedDate}_${fastUnblockConfirm!.timeSlot.replace(':', '')}`;
                      const bookingDocRef = doc(db, getCollectionPath('bookings'), baseBookingId);
                      await setDoc(bookingDocRef, {
                        id: baseBookingId,
                        clubId: userId,
                        courtName: fastUnblockConfirm!.courtName,
                        date: selectedDate,
                        timeSlot: fastUnblockConfirm!.timeSlot,
                        playerName: 'DESBLOQUEADO',
                        status: 'confirmed',
                        createdAt: Date.now()
                      });
                    } else {
                      await deleteDoc(doc(db, getCollectionPath('bookings'), fastUnblockConfirm!.id));
                    }
                    setFastUnblockConfirm(null);
                  }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Desbloquear
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

export default AdminBookings;
